import {
  withLegacyToleranceNames,
  answerSymmetry,
  angleModeFor, addCompassVersion, applyCompassVersion, removeCompassVersion, versionRollKey, writeCompassVersion, type FieldVersion,
  visibleTargeted, SET_ANSWER_TRIGGER, displayEvaluation, computeCompassAcceptedRanges, evaluateCompass, computeTrueHeading, cardinalPointFromDegrees, isCompassDefaultIncorrectUnreachable, isCompassFeedbackFullySubsumed, doesCompassFeedbackOverlapAccepted, findCompassOverlappingAnswerPairs, findCompassOverlappingFeedbackPairs, COMPASS_HEADING_ACCURACY_CUTOFF, COMPASS_POSITION_ACCURACY_CUTOFF, hasVariableChip, sanitizeTriggerValue, probeRoll, computeFormulaExtrema, triggerFault, analyseOverlapAcrossRolls, chipNamesIn, resolveTriggerValues, forEachRoll, OVERLAP_ENUMERATION_MAX_COMBOS, NO_SENSOR_DATA_TRIGGER, type GradingMode, type FeedbackState, type CompassBlockData, type CompassInterval, type CompassPoint, type TriggerFault, type ScenarioFieldBranch, type DisplayVersion, writeScenarioRow, applyScenarioRow, remapVersionsToScenarios, syncScenarioBranches, type ScenarioLeaderVersion, type ScenarioRowTemplate, scenarioInForce, markScenarioReviewed, unreviewedCount, COMPASS_VERSION_KEYS , type DrivenFollower } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertTriangle,
  Compass,
  GripVertical,
  Trash2,
} from 'lucide-react';
import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useRolledWithRecalls } from './answer-ref-context';
import { useArmedDelete } from './block-actions';
import { type BlockIssue, incompleteBetweenRowIds, type IssueCache, type IssueContext, IssuesPanel, memoizedByKey, triggerFaultMessage, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { EmptyQuestionNote, CollapsedInputBlock, CollapsedBlockBar, MobileOnlyChip, collapsedModeBar, collapsedIssueBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { answerBarHasContent, AnswerBarText, activeIntervalIds, ConditionChipRow, toTestBarChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipClass, chipToneClass, UnreachableChip } from './eval-chip';
import { FeedbackCard, ChipDivider, chipEvalIcon, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { evaluateFormulaRaw } from './formula-eval';
import { FormulaField, FormulaWithValues, resolvedTriggerChip } from './formula-field';
import { gradingModes, positiveSlotTitleFor } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { fmtDegrees, fmtLocale } from './number-format';
import { OptionsSection } from './options-section';
import { ScenarioLinkMenu } from './scenario-link-menu';
import { ScenarioRemapModal } from './scenario-remap-modal';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { ScenarioStimulus } from './scenario-stimulus';
import { SensorAdvisory } from './sensor-advisory';
import { SensorFeedbackCard } from './sensor-feedback-card';
import { SensorManualBypass } from './sensor-manual-bypass';
import { SensorAnnounce, SensorCaptureButton, sensorReadoutTone } from './sensor-readout';
import { SensorSimulateBar, type SensorSourceMode, sensorHasReading, sensorSimRandom } from './sensor-simulate-bar';
import { accuracyForSignal, useSensorSimulator } from './sensor-simulator-context';
import { TOLERANCE_HINT_CLASS, toleranceHint } from './tolerance-hint';
import { SymmetryToggle, ToleranceRow } from './tolerance-row';
import { TriggerKindSelect, triggerKindOf } from './trigger-kind';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useInputBlockFeedback, normalizeStandardFeedback, hasEmptyFeedbackContent, positiveTriggerForMode, retriggerPositiveSlot } from './use-input-block-feedback';
import { useRestoredData } from './use-restored-data';
import { useScenarioRowProjection } from './use-scenario-rows';
import { answerPositionsKey, useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { ANSWER_COLUMN_CLASS, BlockQuestion, BlockPreviewProvider, rollVariableValues } from './variable-chip-preview';
import { VariableTestController } from './variable-test-controller';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { useVersionInForce } from './version-in-force';
import { VersionSelector } from './version-selector';


import type { Block, TaskVariable } from '@/types/experience';
import type { Editor } from '@tiptap/core';

import { BetweenAnswerRow } from '@/components/task/between-answer-row';
import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { Button } from '@/components/ui/button';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { RadioGroup } from '@/components/ui/radio-group';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { SpokenPause } from '@/components/ui/spoken-pause';
import { SpokenPlaceholder } from '@/components/ui/spoken-placeholder';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { useExtraBlockSetting } from '@/hooks/use-capabilities';
import { catchAllTitle, catchAllChips, compassAcceptedRangeConfig, compassChips, formatCardinalRange } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { compassTargetedChipLabel, compassTriggerSummary, stateChipLabel, answerExprTexts} from '@/lib/block-state-labels';
import { effectiveSetting, settingAvailable } from '@/lib/mode-gated-settings';

/** The raw bound values a trigger carries, for `resolvedTriggerChip`. Reads the stored string
 * rather than a parsed number, because the whole point is to find the formula text as authored. */
function triggerValuesOf(trigger: string): string[] {
  const parts = trigger.replace(/^[ab]:/, '').split(':');
  return parts.slice(1);
}
// Whether this browser exposes DeviceOrientationEvent at all — a stable, one-time platform
// fact (not a media query), computed once at module load, same convention as GPS's own
// GEO_SUPPORTED.
const ORIENTATION_SUPPORTED = typeof window !== 'undefined' && 'DeviceOrientationEvent' in window;

/**
 * How long the live Device watch waits for a usable heading before declaring the device incapable.
 *
 * Shorter than the one-shot capture's 10s: that one is a deliberate "hold still while I read you"
 * action where waiting is expected, whereas this is a background stream the teacher is watching, and
 * a real magnetometer delivers its first event within a few hundred milliseconds of being armed.
 */
const DEVICE_HEADING_TIMEOUT_MS = 4000;

// ─── Types ────────────────────────────────────────────────────────────────────

interface CompassBlockProps {
  block: Block;
  isPreview: boolean;
  /** Bumped by Preview's page-level Submit — see the effect that watches it. */
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

// One-sided conditions (lt/lte/gt/gte) are deliberately not offered for Compass — see
// compass.ts's own module note: Compass's 0deg=North is an arbitrary convention, not a
// physically privileged reference the way Angle's 0deg=flat (gravity) is, so "less than East"
// has no principled meaning. Only exact (+/- absolute tolerance) and between (an explicit
// range) survive.
type CompassConditionType = 'exact' | 'between';
type CompassTriggerType = 'exact' | 'range';
type CompassNorthReference = 'magnetic' | 'true';
type CompassVisibilityMode = 'live' | 'capture' | 'passive';
type CompassLiveDisplayFormat = 'degrees' | 'cardinalDegree' | 'cardinalOnly';

interface ExactAnswer {
  id: string; value: string; toleranceValue: string; toleranceAbove: string; toleranceBelow: string;
  /** Per answer since the tolerance move. Absent on answers authored before it, which fall
   *  back to the block's own field — always read through `answerSymmetry`, never directly. */
  toleranceSymmetry?: 'symmetric' | 'asymmetric';
}
interface BetweenAnswer { id: string; low: string; high: string }

// Fixed input domain for every answer/range-bound field, exactly like Angle's own
// ROTATION_MIN/ROTATION_MAX — never enforced in the underlying interval math (compass.ts), only at
// this input-validation layer.
const COMPASS_MIN = 0;
const COMPASS_MAX = 360;
/**
 * The largest tolerance that means anything on a circle.
 *
 * A heading is never more than 180° from any other heading, so ±180 already accepts every possible
 * reading — and anything beyond it accepts exactly the same set while making the DISPLAY nonsense:
 * the chip and arc draw the raw pre-wrap pair, so ±500 asked CompassArcDiagram for a 1000° wedge
 * from -410° to 590°. Evaluation was never wrong (wrapCompassInterval clamps a >=360° span to the
 * whole domain); it is the authoring surface that needs the bound.
 */
const COMPASS_MAX_TOLERANCE = 180;

// Editor-local shape — every numeric field is a string during editing, parsed only at
// validation/push time. Distinct name from @agavi/blocks' CompassBlockData (already-parsed
// numbers) since this is what the teacher is actively typing.
interface CompassBlockEditorData extends DrivenFollower {
  /**
   * Per-field angle modes, keyed by `fieldId` — what the in-field toggle writes.
   *
   * This block has no block-wide `angleMode` and never did: the ANGLE it measures is degrees of
   * arc, which is a fact about the instrument. How a teacher's `sin()` reads its argument is a
   * separate question, and that is what this answers — per formula field, as everywhere else.
   */
  angleModes?: Record<string, 'degrees' | 'radians'>;
  /** This block's question bank — see `versions.ts`. A version carries the accepted ANSWERS and
   *  the error model; the north reference and display format stay shared (§3.2). */
  versions?: FieldVersion<{ states: FeedbackState[] }>[];
  /** Following a scenario: the leader, and one answer setup per variant. A value block's row
   *  carries `COMPASS_VERSION_KEYS` flat, exactly as a version does — see `ScenarioFieldBranch`.
   *  Mutually exclusive with `versions` (§2.2). */
  followsBlockId?: string;
  scenarioBranches?: ScenarioFieldBranch<{ states: FeedbackState[] }>[];
  question: string;
  northReference: CompassNorthReference;
  visibilityMode: CompassVisibilityMode;
  liveDisplayFormat: CompassLiveDisplayFormat;
  conditionType: CompassConditionType;
  answers: ExactAnswer[];
  toleranceSymmetry: 'symmetric' | 'asymmetric';
  /** Show the student how precisely to orient. Precision, never the bearing itself — see the same
   *  field on Numerical for the rule, which is why it is offered on `exact` only. */
  showTolerance: boolean;
  betweenAnswers: BetweenAnswer[];
  gradingMode: GradingMode;
  bypassEnabled: boolean;
  feedback: { states: FeedbackState[] };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────


function num(s: string): number {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

// Wraps a raw degree value into [0, 360) for display purposes only — same convention as
// rotation-block.tsx's normalizeDegreesForDisplay, e.g. an exact answer's tolerance band of
// 360° ± 2° reads as "358° to 2°" (lo wraps below 0, hi normalizes above 360).
function normalizeDegreesForDisplay(deg: number): number {
  const m = deg % 360;
  return m < 0 ? m + 360 : m;
}

// English abbreviations differ from Portuguese ones (Leste/Oeste/Sudoeste/Noroeste, not
// East/West/Southwest/Northwest) — never hardcode the letters, always go through i18n.
const CARDINAL_I18N_KEYS: Record<CompassPoint, string> = {
  N: 'cardinalN', NE: 'cardinalNE', E: 'cardinalE', SE: 'cardinalSE',
  S: 'cardinalS', SW: 'cardinalSW', W: 'cardinalW', NW: 'cardinalNW',
};

/**
 * The same point as a WORD, for the student readout only.
 *
 * "N 0°" is read "en, zero degrees" — a lone letter has no pronunciation but its own name, and the
 * one thing the readout exists to tell a student is which way they are facing. The letter stays on
 * screen, where it is instantly legible and where the whole design of the readout depends on it
 * being two characters wide; the word goes in beside it, `sr-only`.
 *
 * Literal `t()` calls in a switch rather than a second `Record` keyed by a template, which is the
 * convention `kindLabel` in the accessibility panel already sets: a template key is invisible to
 * the every-key-exists invariant AND counts against the dynamic-key ceiling, and the exhaustive
 * switch means a new point fails to compile rather than rendering an identifier.
 *
 * Student surface only. A teacher's chips and hints stay lettered — they are scanned against a
 * compass rose beside them, they appear dozens to a panel, and the letters are what the teacher
 * typed the block in terms of.
 */
function cardinalSpoken(point: CompassPoint, t: (k: string) => string): string {
  switch (point) {
    case 'N': return t('blocks.compass.cardinalSpokenN');
    case 'NE': return t('blocks.compass.cardinalSpokenNE');
    case 'E': return t('blocks.compass.cardinalSpokenE');
    case 'SE': return t('blocks.compass.cardinalSpokenSE');
    case 'S': return t('blocks.compass.cardinalSpokenS');
    case 'SW': return t('blocks.compass.cardinalSpokenSW');
    case 'W': return t('blocks.compass.cardinalSpokenW');
    case 'NW': return t('blocks.compass.cardinalSpokenNW');
  }
}

// A dedicated, fixed-size readout in front of every raw-degree input in this editor — a
// standing dash until a real number is typed, then the resolved cardinal point at full
// legible weight. This is a load-bearing feature for teachers who aren't used to thinking in
// degrees (this product's own target audience skews toward informal/field educators, not
// necessarily anyone with prior compass literacy), so it needs to read at a glance, not as a
// footnote — full-strength text color and a real font size, not a muted aside. Fixed width so
// the box never reflows the row as the label's length changes (N vs NE vs SW etc.).
function CardinalHint({ value, id, t, compact }: { value: string; id?: string; t: (k: string) => string; compact?: boolean }) {
  const n = parseFloat(value);
  const valid = value.trim() !== '' && Number.isFinite(n);
  const label = valid ? t(`blocks.compass.${CARDINAL_I18N_KEYS[cardinalPointFromDegrees(n)]}`) : '—';
  return (
    <span id={id} className={`flex shrink-0 items-center justify-center rounded border border-ws-border bg-ws-btn font-semibold text-ws-text ${compact ? 'h-8 w-9 text-sm' : 'h-11 w-11 text-base'}`}>
      {label}
    </span>
  );
}

// Converts a compass bearing (0deg = N/up, clockwise) into on-screen (x, y) coordinates on a
// circle of radius r centered at (cx, cy) — matching CardinalArrow's own rotate() convention
// (0deg = up, clockwise for increasing degrees), so the two stay visually consistent.
function pointOnCompassCircle(deg: number, cx: number, cy: number, r: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.sin(rad), y: cy - r * Math.cos(rad) };
}

// A small compass-rose diagram with the accepted range shaded as a pie wedge from `lo` sweeping
// clockwise to `hi` — this is the one direct answer to "does 340 to 30 mean the SAME thing as
// 30 to 340?" (it doesn't: one is a 50deg wedge through North, the other a 310deg wedge the long
// way around), which is otherwise easy to miss when the only feedback is two raw numbers. Uses
// the exact same wraparound math as compass.ts's own rangeTriggerIntervals (a clockwise sweep
// from lo, wrapping through 0/360 whenever lo > hi) so the shaded wedge always matches what the
// block will actually evaluate — never a fresh, possibly-inconsistent reimplementation of the
// wraparound rule. A screen-reader-only text alternative carries the same information verbally
// (degrees + total span), since the wedge itself is a decorative reinforcement, not a control.
function CompassArcDiagram({ lo, hi, t, locale, compact }: {
  lo: string; hi: string; t: (k: string, opts?: Record<string, unknown>) => string; locale: string; compact?: boolean;
}) {
  const loNum = parseFloat(lo), hiNum = parseFloat(hi);
  const valid = lo.trim() !== '' && hi.trim() !== '' && Number.isFinite(loNum) && Number.isFinite(hiNum);
  const size = compact ? 32 : 44;
  const cx = size / 2, cy = size / 2, r = size / 2 - 3;

  let arcPath: string | null = null;
  let pointTip: { x: number; y: number } | null = null;
  let spanDeg = 0;
  // A full turn survives the modulo. `(hi - lo) % 360` is ZERO for a 360° span, which used to fall
  // into the degenerate branch below and draw a single spoke — so a ±180 tolerance, which accepts
  // every heading, was drawn as a line pointing one way. Checked on the RAW difference, before the
  // wrap, since that is the only place the distinction still exists.
  let fullCircle = false;
  if (valid) {
    fullCircle = Math.abs(hiNum - loNum) >= 360 - 1e-9;
    spanDeg = fullCircle ? 360 : ((hiNum - loNum) % 360 + 360) % 360;
    if (fullCircle) {
      // No wedge path: an SVG arc cannot express a closed 360° sweep (start and end coincide), so
      // the whole disc is filled instead — which is also the truest picture of "any heading passes".
    } else if (spanDeg > 0) {
      const p1 = pointOnCompassCircle(loNum, cx, cy, r);
      const p2 = pointOnCompassCircle(hiNum, cx, cy, r);
      const largeArcFlag = spanDeg > 180 ? 1 : 0;
      arcPath = `M ${cx} ${cy} L ${p1.x} ${p1.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${p2.x} ${p2.y} Z`;
    } else {
      // Degenerate: a single exact direction with no tolerance (or an 'exact' targeted-feedback
      // trigger) — a wedge has zero area here, so an empty circle would look indistinguishable
      // from "nothing configured yet." Draw a spoke straight to the exact point instead, so a
      // real answer of e.g. 90 deg visibly points due east rather than reading as blank.
      pointTip = pointOnCompassCircle(loNum, cx, cy, r);
    }
  }

  const label = valid && fullCircle
    ? t('blocks.compass.arcDiagramFullLabel')
    : valid
    ? t('blocks.compass.arcDiagramLabel', { lo: fmtLocale(normalizeDegreesForDisplay(loNum), locale), hi: fmtLocale(normalizeDegreesForDisplay(hiNum), locale), span: fmtLocale(spanDeg, locale) })
    : t('blocks.compass.arcDiagramEmptyLabel');

  return (
    <svg role='img' aria-label={label} width={size} height={size} viewBox={`0 0 ${size} ${size}`} className='shrink-0'>
      <circle cx={cx} cy={cy} r={r} fill='none' stroke='var(--color-ws-border)' strokeWidth={1.5} />
      {fullCircle && <circle cx={cx} cy={cy} r={r} fill='var(--color-ws-primary)' fillOpacity={0.25} stroke='var(--color-ws-primary)' strokeWidth={1} />}
      {arcPath && <path d={arcPath} fill='var(--color-ws-primary)' fillOpacity={0.25} stroke='var(--color-ws-primary)' strokeWidth={1} strokeLinejoin='round' />}
      {pointTip && <line x1={cx} y1={cy} x2={pointTip.x} y2={pointTip.y} stroke='var(--color-ws-primary)' strokeWidth={2} strokeLinecap='round' />}
      <line x1={cx} y1={cy - r} x2={cx} y2={cy - r + 3} stroke='var(--color-ws-muted)' strokeWidth={1.5} />
    </svg>
  );
}

// Shared text format for any resolved degree interval — "N (0°) to E (90°)" for a real span, or
// just "N (0°)" for a degenerate zero-span point (an exact trigger, or an exact answer with no
// tolerance at all). Used everywhere a chip/summary shows a range, so cardinal letters and raw
// degrees are never presented without each other.

// The canonical "chip" for any resolved degree interval in the feedback panel — Correct,
// Default Incorrect, and each targeted-feedback trigger all render one of these per range, so a
// teacher never has to mentally convert "340 to 30" into "this wraps through North" themselves.
// Colored per evaluation, same convention as the plain-text chips this replaces.
function CompassRangeChip({ lo, hi, t, locale, evaluation }: {
  lo: number; hi: number; t: (k: string, opts?: Record<string, unknown>) => string; locale: string;
  evaluation: FeedbackState['evaluation'];
}) {
  const chipColor =
    chipToneClass(evaluation);
  return (
    // Icon, divider, then content — the same left-to-right shape every other chip uses. This was
    // the one chip style carrying colour with no symbol, which is exactly the distinction a
    // colour-blind teacher cannot make. The arc stays stacked under the text, inside the content
    // column, so adding the symbol does not change what the chip conveys.
    <span className={chipClass(chipColor, { width: 'auto' })}>
      {chipEvalIcon(evaluation)}
      <ChipDivider />
      <span className='flex flex-col items-center gap-1'>
        <span>{formatCardinalRange(lo, hi, t, locale)}</span>
        <CompassArcDiagram lo={String(lo)} hi={String(hi)} t={t} locale={locale} compact />
      </span>
    </span>
  );
}

const CONDITION_TYPES: { id: CompassConditionType; symbol: string }[] = [
  { id: 'exact', symbol: '=' },
  { id: 'between', symbol: '↔' },
];

const CONDITION_ARIA_KEYS: Record<CompassConditionType, string> = {
  exact: 'conditionExact', between: 'conditionBetween',
};

// Blank, not pre-filled — matches rotation-block.tsx's makeExactAnswer/makeBetweenAnswer
// convention exactly.
function makeExactAnswer(): ExactAnswer {
  return { id: crypto.randomUUID(), value: '', toleranceValue: '', toleranceAbove: '', toleranceBelow: '' };
}
function makeBetweenAnswer(): BetweenAnswer {
  return { id: crypto.randomUUID(), low: '', high: '' };
}

function parseAnswers(rawAnswers: Partial<ExactAnswer>[] | undefined): ExactAnswer[] {
  return rawAnswers?.length
    ? rawAnswers.map(a => ({
        id: a.id ?? crypto.randomUUID(), value: a.value ?? '',
        toleranceValue: a.toleranceValue ?? '', toleranceAbove: a.toleranceAbove ?? '', toleranceBelow: a.toleranceBelow ?? '',
        // Carried, never defaulted: `undefined` MEANS "fall back to the block", and a
        // literal here would strand every answer authored before the move.
        toleranceSymmetry: a.toleranceSymmetry,
      }))
    : [makeExactAnswer()];
}
function parseBetween(rawBetween: Partial<BetweenAnswer>[] | undefined): BetweenAnswer[] {
  return rawBetween?.length
    ? rawBetween.map(a => ({ id: a.id ?? crypto.randomUUID(), low: a.low ?? '', high: a.high ?? '' }))
    : [makeBetweenAnswer()];
}

function initData(rawInput: Record<string, unknown>): CompassBlockEditorData {
  /* Blocks saved before the rename say `errorValue`; read under the current names. A missed
     one would not crash — `?? ''` turns it into an empty tolerance, and an empty tolerance
     grades EXACT, so every authored band would quietly become a point. */
  const raw = withLegacyToleranceNames(rawInput);
  return {
    /*
     * Builds a NEW object field by field, so anything not named is dropped — which is how a bank
     * comes to be created, written to Mongo and then silently gone.
     *
     * Each version's ANSWERS go through the same parsers the block's own do: they are structured
     * rows, and a stored row can predate a field. See tilt-block.tsx, where skipping this threw
     * on `.trim()` of an undefined tolerance.
     */
    versions: (raw.versions as CompassBlockEditorData['versions'])?.map(v => ({
      ...v,
      answers: parseAnswers(v.answers as Partial<ExactAnswer>[] | undefined),
      betweenAnswers: parseBetween(v.betweenAnswers as Partial<BetweenAnswer>[] | undefined),
    })),
    /* Named here or silently dropped — see the note above. A row's answers go through the
       same parsers a version's do: skipping it threw on an undefined tolerance once already. */
    followsBlockId: raw.followsBlockId as string | undefined,
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as string | undefined,
    scenarioBranches: (raw.scenarioBranches as CompassBlockEditorData['scenarioBranches'])?.map(r => ({
      ...r,
      answers: parseAnswers(r.answers as Partial<ExactAnswer>[] | undefined),
      betweenAnswers: parseBetween(r.betweenAnswers as Partial<BetweenAnswer>[] | undefined),
    })),

    question: (raw.question as string) ?? '',
    northReference: raw.northReference === 'true' ? 'true' : 'magnetic',
    visibilityMode: (['live', 'capture', 'passive'].includes(raw.visibilityMode as string) ? raw.visibilityMode : 'live') as CompassVisibilityMode,
    liveDisplayFormat: (['degrees', 'cardinalDegree', 'cardinalOnly'].includes(raw.liveDisplayFormat as string) ? raw.liveDisplayFormat : 'degrees') as CompassLiveDisplayFormat,
    conditionType: (CONDITION_TYPES.some(c => c.id === raw.conditionType) ? raw.conditionType : 'exact') as CompassConditionType,
    angleModes: raw.angleModes as Record<string, 'degrees' | 'radians'> | undefined,
    answers: parseAnswers(raw.answers as Partial<ExactAnswer>[] | undefined),
    toleranceSymmetry: raw.toleranceSymmetry === 'asymmetric' ? 'asymmetric' : 'symmetric',
    showTolerance: (raw.showTolerance as boolean) ?? false,
    betweenAnswers: parseBetween(raw.betweenAnswers as Partial<BetweenAnswer>[] | undefined),
    gradingMode: (['graded', 'survey', 'optional'].includes(raw.gradingMode as string) ? raw.gradingMode : 'graded') as GradingMode,
    bypassEnabled: raw.bypassEnabled === true,
    feedback: (raw.feedback ?? { states: [] }) as CompassBlockEditorData['feedback'],
  };
}

// Same colon-delimited trigger format compass.ts's parseValueTrigger expects ("exact:10",
// "range:5:15"), applied directly to the submitted heading value.
function parseCompassTrigger(trigger: string): { type: CompassTriggerType; v1: string; v2: string } {
  const parts = trigger.split(':');
  const raw = parts[0] ?? 'exact';
  const type = (['exact', 'range'].includes(raw) ? raw : 'exact') as CompassTriggerType;
  return { type, v1: parts[1] ?? '', v2: parts[2] ?? '' };
}
// Values are sanitised, not trusted: a trigger is ONE delimited string, so a colon inside a value
// silently re-splits it into a different trigger. See sanitizeTriggerValue.
function buildCompassTrigger(type: CompassTriggerType, v1: string, v2: string): string {
  const a = sanitizeTriggerValue(v1), b = sanitizeTriggerValue(v2);
  return type === 'range' ? `range:${a}:${b}` : `${type}:${a}`;
}

const TRIGGER_TYPES: { id: CompassTriggerType; symbol: string }[] = [
  { id: 'exact', symbol: '=' }, { id: 'range', symbol: '↔' },
];

const TRIGGER_ARIA_KEYS: Record<CompassTriggerType, string> = {
  exact: 'triggerTypeExact', range: 'triggerTypeRange',
};

// Identical to the chip label by contract (see block-state-labels.ts).
function compassTargetedTitle(trigger: string, t: (k: string, opts?: Record<string, unknown>) => string): string {
  return compassTargetedChipLabel(trigger, undefined, t, 'en');
}

// A block counts as "not set up yet" if it has no plausible correct answer at all — mirrors
// isRotationSetupIncomplete. Accuracy cutoffs are fixed constants now (see compass.ts), not a
// teacher-entered field, so there's nothing left to validate about them here.
/**
 * Is there no usable answer here yet?
 *
 * `num()` returns NaN for an equation, and NaN fails every comparison, so a perfectly good answer of
 * `{test}` was read as no answer at all — which switched off the editor's answer bar, among eight
 * other things this gates. A chip formula IS a configured answer; whether the value it resolves to
 * lands inside the domain is a question about a ROLL, and the per-roll validation already asks it
 * (and warns, and wraps). Asking it here, against a value that does not exist yet, can only ever
 * give the wrong answer.
 */
function isCompassSetupIncomplete(d: CompassBlockEditorData): boolean {
  const set = (v: string) => v.trim() !== '';
  const inDomain = (v: string, lo: number, hi: number) =>
    hasVariableChip(v) || (num(v) >= lo && num(v) <= hi);
  return d.conditionType === 'exact'
    ? !d.answers.some(a => set(a.value) && inDomain(a.value, COMPASS_MIN, COMPASS_MAX))
    : !d.betweenAnswers.some(a => set(a.low) && set(a.high)
        && inDomain(a.high, COMPASS_MIN, Infinity) && inDomain(a.low, -Infinity, COMPASS_MAX));
}

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical aria-hidden='true' className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// ─── Trigger UI (targeted/targeted feedback) ─────────────────────────────────

function CompassTriggerEditor({ trigger, onChange, t, locale, variables, rolledValues, onPlainFieldFocus, gradingMode, answerConditionChips, answerTaken, fieldId }: {
  /** The angle-mode key for this trigger's fields. A range's two bounds share it, because two
   *  bounds of one measurement disagreeing about degrees is never what anyone meant. */
  fieldId: string;
  /** Releases the shared rich-text toolbar — see FormulaField's `onFocus`. */
  onPlainFieldFocus: () => void;
  trigger: string; onChange: (t: string) => void; t: (k: string, opts?: Record<string, unknown>) => string; locale: string;
  variables: TaskVariable[]; rolledValues: Record<string, string>;
  /** Another card already points at the answer — see `TriggerKindSelect`. */
  answerTaken: boolean;
  gradingMode: GradingMode;
    /** The authored condition, rendered by the parent — it owns the data and the roll. */
  answerConditionChips: React.ReactNode;
}) {
  const { type: trigType, v1, v2 } = parseCompassTrigger(trigger);
  // Unlike a plain numeric range, min > max is a perfectly valid wraparound band on a circular
  // domain (e.g. 310 to 50 sweeps 310->360/0->50) — see compass.ts's own rangeTriggerIntervals,
  // which every reachability/matching function already treats this way. So there's no
  // "inverted" error here anymore, only a genuine out-of-domain check.
  const v1Num = parseFloat(v1), v2Num = parseFloat(v2);
  const v1OutOfRange = v1.trim() !== '' && !(Number.isFinite(v1Num) && v1Num >= COMPASS_MIN && v1Num <= COMPASS_MAX);
  const v2OutOfRange = trigType === 'range' && v2.trim() !== '' && !(Number.isFinite(v2Num) && v2Num >= COMPASS_MIN && v2Num <= COMPASS_MAX);

  // Fixed ~3-character width (COMPASS_MAX is "360", the longest legal value) rather than a
  // flex-grown width — this editor renders inside a `flex flex-wrap` trigger-zone slot (see
  // feedback-card.tsx) designed for small inline chips, which shrinks any child lacking its own
  // explicit width down to its own intrinsic content size; a flex-grown input had nothing real
  // to grow into there and collapsed to just a few pixels. `w-full` below (on the whole editor)
  // fixes that same collapse for the segmented control row; the inputs use a fixed width
  // instead of flex-grow so they stay reliably legible regardless of how much room the card
  // ends up with.
  const kind = triggerKindOf(trigger);
  return (
    <div className='flex w-full min-w-0 flex-1 flex-col gap-2'>
      <TriggerKindSelect
        trigger={trigger}
        gradingMode={gradingMode}
        answerTaken={answerTaken}
        onChange={onChange}
        customDefault={buildCompassTrigger('exact', '', '')}
      />
      {/* The PRESET shows the authored condition in the same place a custom one appears, read
          only — the teacher sees what it will match without being able to edit it here, and
          editing the answer upstream moves it. */}
      {kind === 'answer' ? (
        <div className='pointer-events-none opacity-60'>{answerConditionChips}</div>
      ) : (
      <>
      <div role='group' aria-label={t('blocks.conditions.typeLabel')} className='flex gap-0.5 overflow-hidden rounded-lg border border-ws-border bg-ws-btn p-0.5'>
        {TRIGGER_TYPES.map(({ id, symbol }) => (
          <button
            key={id}
            type='button'
            aria-pressed={trigType === id}
            aria-label={t(`blocks.compass.${TRIGGER_ARIA_KEYS[id]}`)}
            onClick={() => onChange(buildCompassTrigger(id, v1, v2))}
            className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
              trigType === id ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted hover:text-ws-text'
            }`}
          >
            {symbol}
          </button>
        ))}
      </div>
      {trigType === 'range' ? (
        <div className='flex flex-col gap-1'>
          {/* STACKED, matching slider's own trigger pair. Side by side, each bound had roughly 50px
              of usable width once the variables picker and the degree suffix took their share — and
              a bound can now hold a chip-rendered formula, which does not fit that.
              Stacking also pays for the cardinal hints: with a full row each, a hint sits beside
              its OWN bound instead of competing with it for the same line. */}
          <div className='flex flex-col gap-1.5'>
            <div className='flex items-center gap-2'>
              <CardinalHint value={v1} t={t} compact />
              <FormulaField
                fieldId={`${fieldId}.low`} angleModeKey={fieldId}
                hideEval
                value={v1}
                onChange={v => onChange(buildCompassTrigger('range', v, v2))}
                variables={variables}
                rolledValues={rolledValues}
                ariaLabel={t('blocks.compass.triggerRangeMinAria')}
                placeholder={t('blocks.triggers.triggerRangeMin')}
                suffix='°'
                hasError={v1OutOfRange}
                className='min-w-0 flex-1' 
                onFocus={onPlainFieldFocus}/>
            </div>
            <span aria-hidden='true' className='self-center text-xs text-ws-muted'>{t('blocks.connectors.to')}</span>
            <div className='flex items-center gap-2'>
              <CardinalHint value={v2} t={t} compact />
              <FormulaField
                fieldId={`${fieldId}.high`} angleModeKey={fieldId}
                hideEval
                value={v2}
                onChange={v => onChange(buildCompassTrigger('range', v1, v))}
                variables={variables}
                rolledValues={rolledValues}
                ariaLabel={t('blocks.compass.triggerRangeMaxAria')}
                placeholder={t('blocks.triggers.triggerRangeMax')}
                suffix='°'
                hasError={v2OutOfRange}
                className='min-w-0 flex-1' 
                onFocus={onPlainFieldFocus}/>
            </div>
          </div>
          {/* Same "min > max is a wraparound, not an error" clarification as the between-answer
              rows above — a range trigger's direction is just as easy to misread. */}
          <div className='flex items-center gap-2 self-start'>
            <CompassArcDiagram lo={v1} hi={v2} t={t} locale={locale} compact />
            <span className='text-xs text-ws-muted'>{t('blocks.compass.arcDiagramCaption')}</span>
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <CardinalHint value={v1} t={t} compact />
            <FormulaField
              fieldId={fieldId}
              hideEval
              value={v1}
              onChange={v => onChange(buildCompassTrigger(trigType, v, ''))}
              variables={variables}
              rolledValues={rolledValues}
              ariaLabel={t('blocks.compass.triggerValuePlaceholder')}
              /* No placeholder. A greyed "Heading (°)" in an empty condition field reads as a value
                that is already there, which is the same confusion the invented `lt:10` seed caused
                one line up. The field's aria-label still names it for a screen reader; sighted
                users have the condition's own symbol immediately to its left. */
              suffix='°'
              hasError={v1OutOfRange}
              className='min-w-0 flex-1' 
              onFocus={onPlainFieldFocus}/>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function CompassTriggerChip({ trigger, evaluation, t, locale, scenario, angleMode }: {
  /** THIS trigger's own angle mode — it is a formula field like any other. The caller looks it
   *  up with `angleModeFor`, because only the block knows the map. */
  angleMode: 'degrees' | 'radians';

  trigger: string; evaluation: FeedbackState['evaluation']; t: (k: string, opts?: Record<string, unknown>) => string; locale: string; scenario: Record<string, string>;
}) {
  const { type, v1, v2 } = parseCompassTrigger(trigger);
  const n1 = parseFloat(v1);
  const n2 = type === 'range' ? parseFloat(v2) : n1;
  // Falls back to the plain colon-format summary for an incomplete/not-yet-typed trigger,
  // where there's no real interval yet to hand CompassRangeChip.
  if (!Number.isFinite(n1) || !Number.isFinite(n2)) {
    const chipColor =
      chipToneClass(evaluation);
    return (
      <span className={`${TRIGGER_CHIP_CLASS} ${chipColor}`}>
        {chipEvalIcon(evaluation)}
        <ChipDivider />
        <span className={TRIGGER_CHIP_TEXT_CLASS}>{resolvedTriggerChip({
        trigger, scenario,
        summaryOf: resolved => compassTriggerSummary(resolved, t),
        evaluate: (expr, values) => evaluateFormulaRaw(expr, values, angleMode),
        valuesOf: triggerValuesOf,
        hasChip: hasVariableChip,
        resolveTrigger: resolveTriggerValues,
      })}</span>
      </span>
    );
  }
  return <CompassRangeChip lo={n1} hi={n2} t={t} locale={locale} evaluation={evaluation} />;
}

// ─── Correct-answer condition UI (SegmentedControl + exact/between) ─────────────
// Ported from rotation-block.tsx's SegmentedControl + AnswerSetupFields, minus percentage mode
// and one-sided conditions — neither is offered for Compass, see compass.ts's own module note.

interface AnswerSetupFieldsProps {
  t: (k: string, opts?: Record<string, unknown>) => string;
  locale: string;
  conditionType: CompassConditionType;
  onConditionTypeChange: (v: CompassConditionType) => void;
  answers: ExactAnswer[];
  onUpdateAnswer: (id: string, patch: Partial<ExactAnswer>) => void;
  onAddAnswer: () => void;
  onRemoveAnswer: (id: string) => void;
  /** Flips ONE answer between a single tolerance and a pair — the setting is per answer. */
  onToggleAnswerSymmetry: (id: string) => void;
  betweenAnswers: BetweenAnswer[];
  onUpdateBetweenAnswer: (id: string, patch: Partial<BetweenAnswer>) => void;
  onAddBetweenAnswer: () => void;
  onRemoveBetweenAnswer: (id: string) => void;
  armedAnswerId: string | null;
  setArmedAnswerId: (id: string | null) => void;
  armedBetweenId: string | null;
  setArmedBetweenId: (id: string | null) => void;
  /** Clears the shared rich-text toolbar's active-editor reference. Every plain <input> in this
   * block calls it on focus: without it the toolbar stays pointed at whichever RichTextEditor was
   * last active (the Question), so hitting Bold while the cursor sits in a numeric field silently
   * formats the question instead. Same fix MCQ/Ordering's own alt-text and label fields carry. */
  onPlainFieldFocus: () => void;
  /** Task variables offered by the answer fields' chip picker. Answer VALUES accept formulas;
   * tolerance deliberately does not — see the block's own note on why a tolerance on a loop is
   * absolute degrees only. */
  variables: TaskVariable[];
  /** The roll the answer fields preview against — `{}` in plain display, where chips stay
   * unresolved. */
  rolledValues: Record<string, string>;
}

function AnswerSetupFields({
  t, locale, conditionType, onConditionTypeChange, answers, onUpdateAnswer, onAddAnswer, onRemoveAnswer,
  onToggleAnswerSymmetry,
  betweenAnswers, onUpdateBetweenAnswer, onAddBetweenAnswer, onRemoveBetweenAnswer,
  armedAnswerId, setArmedAnswerId, armedBetweenId, setArmedBetweenId, onPlainFieldFocus,
  variables, rolledValues,
}: AnswerSetupFieldsProps) {
  const addAnswerBtnRef = useRef<HTMLButtonElement>(null);
  const addBetweenBtnRef = useRef<HTMLButtonElement>(null);
  return (
    <>
      <SegmentedControl fieldId='conditionType'
        options={CONDITION_TYPES.map(c => ({ id: c.id, label: c.symbol, ariaLabel: t(`blocks.conditions.${CONDITION_ARIA_KEYS[c.id]}`) }))}
        value={conditionType}
        onChange={v => onConditionTypeChange(v as CompassConditionType)}
        aria-label={t('blocks.conditions.typeLabel')}
        stretch
      />

      {conditionType === 'exact' && (
        <div className='flex flex-col gap-3'>
          {answers.map((a, i) => {
            const isAsym = answerSymmetry(a) === 'asymmetric';
            const isArmed = armedAnswerId === a.id;
            const aOutOfRange = a.value.trim() !== '' && (num(a.value) < COMPASS_MIN || num(a.value) > COMPASS_MAX);
            // The tolerance fields carry the same red treatment the value field already had. The
            // over-cap issue was only ever reported in the issue bar, so the actual offending box
            // looked fine — a teacher reading "Tolerance cannot exceed 180°" had to guess WHICH of
            // up to five answers, and which of its two asymmetric halves, was at fault.
            const tolOver = (v: string) => v.trim() !== '' && num(v) > COMPASS_MAX_TOLERANCE;
            const belowOver = tolOver(a.toleranceBelow), aboveOver = tolOver(a.toleranceAbove), symOver = tolOver(a.toleranceValue);
            const toleranceWarningId = `compass-tol-warn-${a.id}`;
            const anyTolOver = belowOver || aboveOver || symOver;
            return (
              <div key={a.id} data-field-id={a.id} className={`flex flex-col gap-1 ${isArmed ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''}`}>
              <div className='flex items-center gap-2'>
                <CardinalHint value={a.value} id={`answer-cardinal-${a.id}`} t={t} />
                {/* No filterPositiveNumber here: it strips everything but digits, which is right
                    for a tolerance and wrong for a formula. FormulaField owns its own input
                    discipline and the chip picker. */}
                <FormulaField fieldId={`${a.id}.value`}
                  hideEval
                  value={a.value}
                  onChange={v => onUpdateAnswer(a.id, { value: v })}
                  variables={variables}
                  rolledValues={rolledValues}
                  ariaLabel={answers.length > 1 ? `${t('blocks.answers.fieldValue')} ${i + 1}` : t('blocks.answers.fieldValue')}
                  ariaDescribedBy={`answer-cardinal-${a.id}`}
                  hasError={aOutOfRange}
                  className='min-w-0 flex-1' 
                  onFocus={onPlainFieldFocus}/>
              </div>
                {/* The tolerance has its own row in both modes, and `decimal` not `scientific`
                    because a bearing runs 0..360 — see `ToleranceRow` for both calls. */}
                <ToleranceRow
                  idPrefix={a.id}
                  asymmetric={isAsym}
                  grammar='decimal'
                  toleranceValue={a.toleranceValue}
                  toleranceBelow={a.toleranceBelow}
                  toleranceAbove={a.toleranceAbove}
                  onChange={patch => onUpdateAnswer(a.id, patch)}
                  warn={{ value: symOver, below: belowOver, above: aboveOver }}
                  describedBy={toleranceWarningId}
                  onFocus={onPlainFieldFocus}
                  trailing={<>
                    <SymmetryToggle compact fieldId={`${a.id}.toleranceSymmetry`}
                      asymmetric={isAsym} onToggle={() => onToggleAnswerSymmetry(a.id)} />
                    {answers.length > 1 && (
                      <IconButton type='button' variant='destructive' size='md' isArmed={isArmed}
                        aria-label={isArmed ? t('blocks.answers.confirmRemoveAnswer') : t('blocks.answers.removeAnswer')}
                        className='shrink-0'
                        onClick={() => {
                          if (isArmed) { onRemoveAnswer(a.id); setArmedAnswerId(null); requestAnimationFrame(() => addAnswerBtnRef.current?.focus()); }
                          else { setArmedBetweenId(null); setArmedAnswerId(a.id); }
                        }}
                        onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedAnswerId(null); }}
                        onBlur={() => { if (isArmed) setArmedAnswerId(null); }}
                      >
                        <Trash2 className='h-4 w-4' />
                      </IconButton>
                    )}
                  </>} />
              {/* sr-only, not a visible row: this editor states a block's problems in the issue bar,
                  not inline under the field, and the amber border already carries it visually. The
                  text still exists in the DOM so `aria-describedby` gives a screen-reader user the
                  same information the border gives a sighted one. */}
              {anyTolOver && (
                <p id={toleranceWarningId} className='sr-only'>
                  {t('blocks.compass.validationToleranceMax', { max: COMPASS_MAX_TOLERANCE })}
                </p>
              )}
              {/* Same disambiguation the between-answer rows get below — a resolved tolerance
                  band near the 0/360 seam (e.g. 358 to 360, wrapping to 0 to 2) is exactly as
                  easy to misread as an explicit between-range is. */}
              <div className='flex items-center gap-2 self-start'>
                <CompassArcDiagram
                  lo={a.value.trim() === '' ? '' : String(num(a.value) - (isAsym ? num(a.toleranceBelow) : num(a.toleranceValue)))}
                  hi={a.value.trim() === '' ? '' : String(num(a.value) + (isAsym ? num(a.toleranceAbove) : num(a.toleranceValue)))}
                  t={t} locale={locale}
                />
                <span className='text-xs text-ws-muted'>{t('blocks.compass.arcDiagramCaption')}</span>
              </div>
              </div>
            );
          })}

          {answers.length < 5 && (
            <AddRowButton ref={addAnswerBtnRef} onClick={onAddAnswer} label={t('blocks.answers.addAnswer')} />
          )}
        </div>
      )}

      {conditionType === 'between' && (
        <div className='flex flex-col gap-3'>
          {betweenAnswers.map((a, i) => {
            const isArmed = armedBetweenId === a.id;
            const bothBoundsSet = a.low.trim() !== '' && a.high.trim() !== '';
            const rangeOutOfRange = bothBoundsSet && (num(a.high) < COMPASS_MIN || num(a.low) > COMPASS_MAX);
            return (
              // `data-field-id` so `between-incomplete` can reach this row. Without it the issue
              // renders as a BUTTON (any non-empty `fields` does) that silently does nothing — the
              // dead control `IssueRow` documents avoiding, and the fault numerical's unit options
              // shipped with.
              <BetweenAnswerRow
                key={a.id}
                fieldId={a.id}
                armed={isArmed}
                grouped={betweenAnswers.length > 1}
                to={t('blocks.connectors.to')}
                low={
                  <div className='flex items-center gap-2'>
                    <CardinalHint value={a.low} id={`between-low-cardinal-${a.id}`} t={t} />
                    <FormulaField fieldId={`${a.id}.low`} angleModeKey={a.id}
                      hideEval
                      value={a.low}
                      onChange={v => onUpdateBetweenAnswer(a.id, { low: v })}
                      variables={variables}
                      rolledValues={rolledValues}
                      ariaLabel={betweenAnswers.length > 1 ? `${t('blocks.triggers.triggerRangeMin')} ${i + 1}` : t('blocks.triggers.triggerRangeMin')}
                      ariaDescribedBy={`between-low-cardinal-${a.id}`}
                      placeholder={t('blocks.triggers.triggerRangeMin')}
                      hasError={rangeOutOfRange}
                      className='min-w-0 flex-1' 
                      onFocus={onPlainFieldFocus}/>
                  </div>
                }
                high={
                  <div className='flex items-center gap-2'>
                    <CardinalHint value={a.high} id={`between-high-cardinal-${a.id}`} t={t} />
                    <FormulaField fieldId={`${a.id}.high`} angleModeKey={a.id}
                      hideEval
                      value={a.high}
                      onChange={v => onUpdateBetweenAnswer(a.id, { high: v })}
                      variables={variables}
                      rolledValues={rolledValues}
                      ariaLabel={betweenAnswers.length > 1 ? `${t('blocks.triggers.triggerRangeMax')} ${i + 1}` : t('blocks.triggers.triggerRangeMax')}
                      ariaDescribedBy={`between-high-cardinal-${a.id}`}
                      placeholder={t('blocks.triggers.triggerRangeMax')}
                      hasError={rangeOutOfRange}
                      className='min-w-0 flex-1' 
                      onFocus={onPlainFieldFocus}/>
                  </div>
                }
                action={betweenAnswers.length > 1 && (
                  <IconButton type='button' variant='destructive' size='md' isArmed={isArmed}
                    aria-label={isArmed ? t('blocks.answers.confirmRemoveRange') : t('blocks.answers.removeRange')}
                    className='shrink-0'
                    onClick={() => {
                      if (isArmed) { onRemoveBetweenAnswer(a.id); setArmedBetweenId(null); requestAnimationFrame(() => addBetweenBtnRef.current?.focus()); }
                      else { setArmedAnswerId(null); setArmedBetweenId(a.id); }
                    }}
                    onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedBetweenId(null); }}
                    onBlur={() => { if (isArmed) setArmedBetweenId(null); }}
                  >
                    <Trash2 className='h-4 w-4' />
                  </IconButton>
                )}
              >
                {/* 340 to 30 and 30 to 340 are different answers (a 50deg wedge through North
                    vs. a 310deg wedge the other way around) — this diagram is the direct
                    disambiguation for that, not decoration. */}
                <div className='flex items-center gap-2 self-start'>
                  <CompassArcDiagram lo={a.low} hi={a.high} t={t} locale={locale} />
                  <span className='text-xs text-ws-muted'>{t('blocks.compass.arcDiagramCaption')}</span>
                </div>
              </BetweenAnswerRow>
            );
          })}
          {betweenAnswers.length < 5 && (
            <AddRowButton ref={addBetweenBtnRef} onClick={onAddBetweenAnswer} label={t('blocks.answers.addAnswer')} />
          )}
        </div>
      )}
    </>
  );
}

// ─── Collapsed-view display ────────────────────────────────────────────────────

// The editor-local string shape converted into the parsed-number config compass.ts' interval math
// expects. Extracted so the issue computation and the editor's own `acceptedRanges` can never
// drift into computing "accepted" two slightly different ways.
/** `resolve` turns the editor's raw formula text ("{n}*30") into the number the maths needs.
 * Defaults to `num`, so every existing caller — and any plain-number setup — behaves exactly as
 * before. Tolerances deliberately never resolve: a tolerance on a loop is absolute degrees, not an
 * equation. Same bridge shape slider and numerical already use. */

// ─── Standardized issue reporting ──────────────────────────────────────────────
// Same shape and rules as gpsIssues/orderingIssues — see gps-block.tsx's own header
// comment for the full reasoning about what does and doesn't belong on this bar.
//
// Compass-specific notes: there is deliberately NO range-inversion check here (unlike Angle),
// because a Compass range whose low exceeds its high is a legitimate wraparound wedge through
// North, not a mistake — that's exactly what CompassArcDiagram exists to disambiguate. And
// per-answer/per-trigger range errors are aggregated one-per-kind rather than one-per-field, so
// three bad values read as one problem to fix, not three.
/** Backfills the default feedback set for a block read straight from `block.data` — the editor
 * seeds these into its own state, but nothing reaches the saved block until the teacher changes
 * something, so without this a freshly added block's test legend has no states to show while the
 * feedback panel beside it lists several. Shared by both non-editing render paths (the collapsed
 * card's test widget and the student-facing Preview). */
function compassExtraDefaults(t: (key: string, opts?: Record<string, unknown>) => string) {
  return [
    {
      // First, as the evaluator gates it: a dead magnetometer outranks a noisy one.
      trigger: NO_SENSOR_DATA_TRIGGER,
      title: t('blocks.sensors.noSensorDataFeedback'),
      content: `<p>${t('blocks.compass.noSensorDataDefault')}</p>`,
      evaluation: 'neutral' as const,
    },
    {
      trigger: 'poor_heading_accuracy',
      title: t('blocks.compass.poorHeadingAccuracyFeedback'),
      content: `<p>${t('blocks.compass.poorHeadingAccuracyDefault')}</p>`,
      evaluation: 'neutral' as const,
    },
    {
      trigger: 'poor_position_accuracy',
      title: t('blocks.compass.poorPositionAccuracyFeedback'),
      content: `<p>${t('blocks.compass.poorPositionAccuracyDefault')}</p>`,
      evaluation: 'neutral' as const,
    },
  ];
}

function buildNormalizedCompassData(
  rawD: CompassBlockEditorData,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The version drawn for this student, or the one the teacher's pager is on. */
  versionId?: string,
): CompassBlockEditorData {
  const rawVersioned = applyCompassVersion(rawD, versionId);
  return {
    ...rawVersioned,
    feedback: normalizeStandardFeedback(rawVersioned.feedback, t, rawVersioned.gradingMode, {
    defaults: { unansweredContent: `<p>${t('blocks.feedbackDefaults.noMeasurementContent')}</p>` },
      extraDefaults: compassExtraDefaults(t),
    }),
  };
}

/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `compassIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function compassBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return compassIssues(buildNormalizedCompassData(initData(block.data), ctx.t), ctx);
}

export function compassIssues(d: CompassBlockEditorData, ctx: IssueContext): BlockIssue[] {
  const { t, variables } = ctx;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const categoryFeedback = t('blocks.areas.feedback');

  // An answer field is one of three things, and `num()` cannot tell them apart: it returns 0 for
  // anything unparseable, so before this every FORMULA answer validated as though it were a literal
  // 0 — in range, overlapping every other formula answer, and centred on due north. Same class of
  // bug `hasNum()` was written for, and the reason each check below asks what KIND of field it has
  // before asking anything else.
  /**
   * §4.4 — a row copied by the projection is "copied, not decided". The box shows a count
   * beside the pager for the row you are on; this is the half that reaches the publication
   * gate, so a teacher who never pages to scenario 5 still learns it was never reviewed.
   */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: categoryAnswerSetup,
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }
  /** Each answer field's text WITH its id, so a check can read that field's own angle mode. */
  const answerFieldRows: { value: string; fieldId: string }[] = d.conditionType === 'exact'
    ? d.answers.map(a => ({ value: a.value, fieldId: `${a.id}.value` }))
    : d.betweenAnswers.flatMap(a => [
      { value: a.low, fieldId: a.id }, { value: a.high, fieldId: a.id }]);
  const answerFields: string[] = answerFieldRows.map(r => r.value);
  const probeValues = probeRoll(variables);
  const isFormula = (v: string) => hasVariableChip(v);
  const anyFormulaAnswer = answerFields.some(isFormula);

  // A `between` row with exactly one bound filled IS broken, loop or not — you cannot wrap from 23
  // to nothing, and the evaluator drops the row. See `incompleteBetweenRowIds`.
  if (d.conditionType === 'between') {
    const halfRows = incompleteBetweenRowIds(d.betweenAnswers);
    if (halfRows.length > 0) {
      issues.push({ key: 'between-incomplete', fields: halfRows, severity: 'error', kind: 'incompletion',
        category: categoryAnswerSetup, detail: t('blocks.answers.issueBetweenIncomplete') });
    }
  }

  /*
   * NO inverted-range check on this block, deliberately — and it is the only one of the four.
   *
   * A compass domain is a LOOP. `wrapCompassInterval` reads a `between` row with low > high as the
   * arc through the 0/360 seam, which is its own documented behaviour: 350 to 10 is a twenty-degree
   * arc across north, and it is a perfectly ordinary answer. Forcing it to 10 to 350 does not fix
   * anything, it selects the COMPLEMENTARY arc — a different question with a different answer set.
   *
   * So there is nothing to warn about, for literals or for equations: every ordered pair of
   * headings names a valid arc. I added a check here in the inversion pass, reasoning that compass
   * "lacked" one; it lacked one for a reason. A half-filled row is still broken (you cannot wrap
   * from 23 to nothing) and `incompleteBetweenRowIds` above reports that.
   */

  // A LITERAL outside the rose is a plain mistake; a formula is judged over its whole domain below.
  const outOfDomain = (v: string) => v.trim() !== '' && !isFormula(v) && !(num(v) >= COMPASS_MIN && num(v) <= COMPASS_MAX);
  if (answerFields.some(outOfDomain)) {
    issues.push({ key: 'answer-range', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.compass.validationValueOutOfRange') });
  }

  // Judged against a PROBE roll built from the variables' own domains, never against a live roll:
  // an unselected card has no roll at all, so every formula would read NaN and be called broken
  // purely for being looked at from the wrong surface. Same reasoning slider records.
  if (answerFieldRows.some(r => r.value.trim() !== '' && isFormula(r.value)
    && !Number.isFinite(evaluateFormulaRaw(r.value, probeValues, angleModeFor(d, r.fieldId))))) {
    issues.push({ key: 'answer-formula-invalid', severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.answers.issueAnswerFormulaInvalid') });
  }

  // A resolved target outside [0, 360) WRAPS — 390 is 30, which is physically true — so this is a
  // warning, not an error. Overrunning the rose is usually an authoring slip rather than intent,
  // and quantifying over the extrema is what makes the claim stable: judged on one roll it would
  // appear and vanish as the dice turn.
  if (anyFormulaAnswer) {
    const extrema = computeFormulaExtrema({
      conditionType: d.conditionType, answers: d.answers, betweenAnswers: d.betweenAnswers,
      threshold: '', toleranceMode: 'absolute', toleranceSymmetry: d.toleranceSymmetry, angleMode: 'degrees',
    }, variables);
    // NOT "outside [0, 360)". A band dipping below zero is the ordinary wraparound case on a
    // circular domain — this block's own note for range triggers says so outright — and −10 is a
    // perfectly natural way to write "just under level". Warning about it flags the most readable
    // way to express a band straddling zero as a defect.
    //
    // Two things are genuinely worth saying. Beyond a FULL TURN is more than one rotation and is
    // nearly always a coefficient slip. A band spanning 360 or more accepts every possible reading,
    // so the block grades nothing while looking configured — slider reports the same shape as
    // `tolerance-covers-track`.
    if (extrema && (extrema.lo <= -360 || extrema.hi >= 720)) {
      issues.push({ key: 'answer-beyond-turn', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
        detail: t('blocks.compass.issueAnswerBeyondTurn') });
    }
    /**
     * A band that spans the whole circle for SOME student — measured as the band's own WIDTH at each
     * roll, not as the envelope of where it can sit across rolls.
     *
     * The envelope was the bug. `computeFormulaExtrema` answers "how far can this reach", so an
     * answer of `{n}` over 1..400 reported a 399° span and tripped this — while every individual
     * student got a single heading with no tolerance at all. The block was graded perfectly well;
     * only the check thought otherwise.
     *
     * Restricted to `between`, because that is the only shape whose width can vary with the roll.
     * An exact answer's width is twice its tolerance, which does not depend on any variable and is
     * already reported, more precisely, by `tolerance-max`.
     */
    const bandWidth = (vals: Record<string, string>) => d.betweenAnswers.some(a => {
      const lo = evaluateFormulaRaw(a.low, vals, angleModeFor(d, a.id));
      const hi = evaluateFormulaRaw(a.high, vals, angleModeFor(d, a.id));
      return Number.isFinite(lo) && Number.isFinite(hi) && Math.abs(hi - lo) >= 360 - 1e-9;
    });
    if (d.conditionType === 'between') {
      const bandNames = chipNamesIn(d.betweenAnswers.flatMap(a => [a.low, a.high]));
      // ANY roll, not every: a band that swallows the circle for one student in ten is still a
      // student whose answer cannot be wrong, and the teacher cannot see that from their own screen.
      let coversCircle = false;
      if (bandNames.length === 0) {
        coversCircle = bandWidth(probeValues);
      } else {
        forEachRoll(bandNames, variables, vals => {
          if (bandWidth(vals)) { coversCircle = true; return 'stop'; }
          return undefined;
        }, OVERLAP_ENUMERATION_MAX_COMBOS);
      }
      if (coversCircle) {
        issues.push({ key: 'answer-covers-circle', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
          detail: t('blocks.compass.issueAnswerCoversCircle') });
      }
    }
  }

  /** Same check and same reasoning as Angle's: on a closed rose "0 +/- 2" and "360 +/- 2" are one
   * answer written twice, and only comparing the WRAPPED intervals can see it. A warning, not an
   * error: acceptance is their union, so the grading is right and the authoring is what is wrong.
   * GPS needs no equivalent — one target plus one tolerance is a single range by construction. */
  // Skipped entirely while a formula answer is present: the comparison needs concrete numbers, and
  // with `num()` turning every formula into 0 it reported two unrelated equations as the same
  // answer. A claim about overlap across all rolls needs its own extrema pass — deferred rather
  // than guessed, since a wrong overlap warning is worse than none.
  const overlappingAnswers = anyFormulaAnswer ? [] : findCompassOverlappingAnswerPairs(compassAcceptedRangeConfig(d));
  if (overlappingAnswers.length > 0) {
    const bandCrossesSeam = d.conditionType === 'exact' && d.answers.some(a => {
      const asym = answerSymmetry(a, d) === 'asymmetric';
      const loErr = num(asym ? a.toleranceBelow : a.toleranceValue);
      const hiErr = num(asym ? a.toleranceAbove : a.toleranceValue);
      return num(a.value) - loErr < COMPASS_MIN || num(a.value) + hiErr > COMPASS_MAX;
    });
    issues.push({
      key: 'answer-overlap',
      severity: 'warning',
      kind: 'defect',
      category: categoryAnswerSetup,
      detail: t(bandCrossesSeam ? 'blocks.compass.answerOverlapSeamWarning' : 'blocks.compass.answerOverlapWarning'),
    });
  }

  // Only `exact` carries a tolerance; a `between` answer states its own two edges instead.
  const toleranceTooLarge = d.conditionType === 'exact' && d.answers.some(a => {
    const vals = answerSymmetry(a, d) === 'asymmetric' ? [a.toleranceAbove, a.toleranceBelow] : [a.toleranceValue];
    return vals.some(v => v.trim() !== '' && num(v) > COMPASS_MAX_TOLERANCE);
  });
  if (toleranceTooLarge) {
    // A WARNING, not an error. Unlike an out-of-domain heading, an over-large tolerance is not
    // invalid input — evaluation handles it perfectly well, it simply accepts every heading. The
    // teacher is told what it means rather than told they got it wrong, matching how GPS reports a
    // network-based fix: real consequence, amber, not a red stop.
    issues.push({ key: 'tolerance-max', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.compass.validationToleranceMax', { max: COMPASS_MAX_TOLERANCE }) });
  }


  if (!d.question.replace(/<\/?p>/g, '').trim()) {
    issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  }

  // A variable chip in the QUESTION with a fixed answer is silent mis-grading: every student reads a
  // different target and every student is graded against the same one. No sensor block could detect
  // this — `htmlHasVariableChips` was called zero times in all three. A defect, not an incompletion:
  // both halves are authored, and more typing in the question will not resolve it.
  //
  // The "answer can vary" side is written against the mechanism each block actually has, so this
  // stays correct as those mechanisms land: chips in an answer field for compass/angle, a configured
  // branch set for gps.
  // The `&& !answerOutOfRange` that used to guard this is gone: `supersededBy` is the one
  // mechanism for standing a catch-all down, and doing it twice meant the new keys below would have
  // had to be added in two places to work.
  // Graded only: optional and survey author no answer key, so a missing one is the mode
  // working as intended rather than a setup the teacher has not finished.
  if (d.gradingMode === 'graded' && isCompassSetupIncomplete(d)) {
    issues.push({ key: 'setup-incomplete', severity: 'error', kind: 'incompletion', supersededBy: ['answer-range', 'answer-formula-invalid', 'answer-overlap', 'tolerance-max'], category: categoryAnswerSetup, detail: t('blocks.compass.barSetupIncomplete') });
  }

  const targetedStates = d.feedback.states.filter(s => s.type === 'targeted');
  const triggers = targetedStates.map(s => s.trigger);
  /** A trigger's own field, so the overlap analysis reads it in the mode it was authored in.
   *  Without this every trigger resolved in the block default and a radian trigger looked
   *  identical to a degree answer — reported as "completely overlaps Correct". */
  const triggerFieldId = new Map(targetedStates.map(s => [s.trigger, `feedback.${s.id}.trigger`] as const));
  const triggerOutOfRange = triggers.some(trig => {
    const { v1, v2 } = parseCompassTrigger(trig);
    return outOfDomain(v1) || outOfDomain(v2);
  });
  if (triggerOutOfRange) {
    issues.push({ key: 'trigger-range', severity: 'error', kind: 'defect', category: categoryFeedback, detail: t('blocks.compass.validationValueOutOfRange') });
  }


  /**
   * Every fault the shared detector finds in a targeted condition.
   *
   * `allowWrap` is the one thing that differs between the blocks that use this, and it is not a
   * matter of taste. A compass has no absolute reference, so a range whose min exceeds its max is an
   * ordinary wraparound band through north — `evaluateCompass` reads `310 to 50` as a hundred-degree
   * sweep, and its own comment calls that the ordinary case rather than an edge case. Reporting it
   * as inverted would accuse the most common setup this block has.
   */
  const triggerFaults = d.feedback.states
    .filter(s => s.type === 'targeted')
    .map(s => triggerFault(s.trigger, {
      // The same resolver the answer fields are judged with, a few lines above.
      resolve: (v: string) => evaluateFormulaRaw(v, probeValues, angleModeFor(d, `feedback.${s.id}.trigger`)),
      declared: new Set(variables.map(v => v.name)),
      probeable: new Set(Object.keys(probeValues)),
      allowWrap: true,
    }))
    .filter((f): f is TriggerFault => f !== null);

  if (triggerFaults.includes('unresolvable')) {
    issues.push({ key: 'trigger-unresolvable', severity: 'error', kind: 'defect', category: categoryFeedback, detail: t('blocks.conditions.issueUnresolvable') });
  }
  if (triggerFaults.includes('no-value')) {
    issues.push({ key: 'trigger-incomplete', severity: 'error', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.conditions.issueNoValue') });
  }
  if (triggerFaults.includes('range-incomplete')) {
    issues.push({ key: 'trigger-range-incomplete', severity: 'error', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.conditions.issueRangeIncomplete') });
  }

  if (hasEmptyFeedbackContent(d.feedback.states)) {
    issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });
  }

  if (d.gradingMode === 'graded' && !isCompassSetupIncomplete(d)) {
    if (findCompassOverlappingFeedbackPairs(triggers).length > 0) {
      issues.push({ key: 'feedback-overlap', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.compass.feedbackOverlapWarning') });
    }
    /**
     * Quantified over the roll space, not judged at one point of it.
     *
     * Two things were wrong with the single check this replaces. It resolved the answer with `num`,
     * which returns 0 for anything unparseable — so a formula answer put the accepted band around
     * ZERO and every claim below was made against a band the block does not have. And even with a
     * resolver it would have been one roll's answer about a band that moves with the dice.
     */
    const overlap = analyseOverlapAcrossRolls({
      triggers, variables,
      names: chipNamesIn([...answerFields, ...triggers]),
      atRoll: vals => {
        const resolveAt = (v: string, fieldId = '') => evaluateFormulaRaw(v, vals, angleModeFor(d, fieldId));
        const ranges = computeCompassAcceptedRanges(compassAcceptedRangeConfig(d, resolveAt));
        if (ranges.length === 0) return null;
        const at = (trig: string) =>
          resolveTriggerValues(trig, v => resolveAt(v, triggerFieldId.get(trig) ?? ''));
        return {
          subsumed: trig => isCompassFeedbackFullySubsumed(at(trig), ranges),
          overlaps: trig => doesCompassFeedbackOverlapAccepted(at(trig), ranges),
        };
      },
    });
    // Graded only. Every verdict here is about the ACCEPTED BAND, and in a participation mode
    // there is no band in force — correct is never checked, so nothing is subsumed by it and
    // nothing overlaps it. Reporting these outside graded would warn about a condition the
    // evaluator will happily fire.
    if (d.gradingMode === 'graded' && overlap.any.alwaysSubsumed) {
      issues.push({ key: 'feedback-subsumed', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.compass.feedbackSubsumedWarning') });
    }
    if (d.gradingMode === 'graded' && overlap.any.alwaysOverlaps) {
      issues.push({ key: 'feedback-overlaps-correct', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.compass.feedbackOverlapsCorrectWarning') });
    }
    // The collision a teacher cannot see, because the roll in front of them does not contain it.
    if (d.gradingMode === 'graded' && overlap.any.mayCollide) {
      issues.push({ key: 'feedback-may-overlap-correct', severity: 'warning', kind: 'defect',
        supersededBy: ['feedback-overlaps-correct', 'feedback-subsumed'],
        category: categoryFeedback, detail: t('blocks.conditions.mayOverlapCorrect') });
    }
  }

  return issues;
}

const compassIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();

// ─── Live test: scenario-driven simulated readings ─────────────────────────────
// A teacher authoring on a laptop has no compass, and even on a phone they can't physically face
// six different directions to check six feedback states. So the test panel offers a fixed menu of
// named scenarios instead of a free-typed heading field: pick "just outside tolerance", press the
// real Take Reading button, and a synthetic reading is computed relative to whatever the current
// correct answer happens to be. Changing the answer changes what every scenario means, which is
// the point — the teacher tests intent ("a student who's 90° off"), not arithmetic.
//
// Critically these feed the SAME evaluateCompass path a real capture does, never a parallel
// mocked evaluation, so tolerance/wraparound/accuracy-gate logic is genuinely exercised.
//
// Deliberately block-specific, not a shared sensor-wide component: "wrong turn" only means
// something for a bearing, exactly as "wrong location entirely" only means something for GPS.
/**
 * Simulated SENSOR FAULTS, and nothing else.
 *
 * The value scenarios this list used to carry (Exact, Within Tolerance, Near Tolerance, Off by 90,
 * Off by 180) are gone, because a heading is a single number the teacher can type into the box
 * beside this dropdown. A preset whose only contribution is a typeable number is a menu entry
 * earning nothing. What survives is what the box CANNOT express: a confidence figure bad enough to
 * trip a gate, and the absence of a reading altogether. GPS keeps its full list for the opposite
 * reason — a point 10 m off target is a coordinate pair, not something anyone types.
 */
type CompassScenarioId = 'poorHeading' | 'poorPosition' | 'noReading';

/** What the simulated magnetometer is currently armed to report. A named scenario is relative to the
 * authored answer (so changing the answer changes what every scenario means, which is the point),
 * while a manual heading is absolute — the escape hatch for one specific bearing the teacher has in
 * mind that no named scenario expresses. Mirrors GpsSimSelection exactly. */
type CompassSimSelection =
  | { kind: 'scenario'; id: CompassScenarioId }
  | { kind: 'manual'; headingDegrees: number }
  /** "Normal Reading" chosen with nothing typed yet — the block's default. Its own variant because
   * `manual` carries a real heading and no number means "none"; without it the dropdown's empty
   * option built `{ kind: 'scenario', id: '' }` and simulated a fault that does not exist. */
  | { kind: 'none' };

interface CompassSimReading {
  headingDegrees: number;
  headingAccuracy: number;
  positionAccuracy: number;
}

/** Scenarios offered for this block's current configuration. poorPosition only exists in True
 * North mode — magnetic readings never consult position at all, so offering it there would be a
 * scenario that provably cannot change the outcome. */
/**
 * A centre + tolerance for EVERY authored answer, not just the first.
 *
 * This used to `.find()` the first non-blank answer and simulate only that one, which had two
 * consequences on a multi-answer block. The other answers were never exercised — no amount of
 * rerolling could produce them — and, worse, a scenario named for being wrong could land on one of
 * them: with answers at 0 and 180, "Opposite" is 180 +/- 10, i.e. exactly the second correct
 * answer, so the scenario reported Correct.
 */
/** Position accuracy is only consulted in True North mode — magnetic readings never look at position
 * at all, so offering that fault there would be one that provably cannot change the outcome. */
function compassScenarioIds(d: CompassBlockEditorData): CompassScenarioId[] {
  const ids: CompassScenarioId[] = ['poorHeading'];
  if (d.northReference === 'true') ids.push('poorPosition');
  // Always offered: it deliberately produces NO reading, which is the only way to reach No Answer now
  // that two of the three visibility modes have no capture button to withhold (Live Reading always
  // has a value; Capture on Submit takes one automatically). A real field case too — a phone with no
  // magnetometer, or one that never finished calibrating.
  ids.push('noReading');
  return ids;
}

/** Falls back to the first offered fault when the armed one stops being available (switching away
 * from True North retires poorPosition). */
function compassEffectiveScenario(d: CompassBlockEditorData, id: CompassScenarioId): CompassScenarioId {
  const ids = compassScenarioIds(d);
  return ids.includes(id) ? id : ids[0]!;
}

function compassSimBases(d: CompassBlockEditorData): { center: number; tolerance: number }[] {
  if (isCompassSetupIncomplete(d)) return [];
  if (d.conditionType === 'exact') {
    return d.answers.filter(a => a.value.trim() !== '').map(a => {
      const asym = answerSymmetry(a, d) === 'asymmetric';
      const above = asym ? num(a.toleranceAbove) : num(a.toleranceValue);
      const below = asym ? num(a.toleranceBelow) : num(a.toleranceValue);
      return { center: num(a.value), tolerance: Math.max(above, below, 0) };
    });
  }
  return d.betweenAnswers.filter(a => a.low.trim() !== '' && a.high.trim() !== '').map(a => {
    const lo = num(a.low), hi = num(a.high);
    // Wraparound-aware: 340→30 is a 50° wedge through North, not a -310° one.
    const span = ((hi - lo) % 360 + 360) % 360;
    return { center: normalizeDegreesForDisplay(lo + span / 2), tolerance: span / 2 };
  });
}

/** The first authored answer, for callers that only need "is anything set up at all". */
function compassSimBase(d: CompassBlockEditorData): { center: number; tolerance: number } | null {
  return compassSimBases(d)[0] ?? null;
}

const COMPASS_GOOD_HEADING_ACCURACY = 3;   // degrees — a healthy phone magnetometer
const COMPASS_GOOD_POSITION_ACCURACY = 10; // metres — a healthy GPS fix

/**
 * The simulated reading for an armed fault, or a typed heading.
 *
 * `manualHeading` is threaded in because the faults still need a heading to hang a bad confidence
 * figure on, and the teacher's typed value is now the natural source for it. Falling back to an
 * authored answer keeps the faults usable before anything has been typed.
 */
function compassSimReading(
  selection: CompassSimSelection,
  d: CompassBlockEditorData,
  nonce: number,
  manualHeading: number | null,
): CompassSimReading | null {
  // A manually entered heading is taken at face value — normalized into the closed [0, 360) display
  // domain the rest of this block uses, so a typed 370 reads as 10 rather than as an impossible value.
  //
  // Resolved BEFORE the answer setup is consulted. A fault can fall back to the authored answer for
  // its heading; a typed heading is a literal and needs nothing. Gating it on the answer too meant a
  // teacher who wanted to watch the readout while roughing out a block got a dead box.
  if (selection.kind === 'manual') {
    return {
      headingDegrees: normalizeDegreesForDisplay(selection.headingDegrees),
      headingAccuracy: COMPASS_GOOD_HEADING_ACCURACY,
      positionAccuracy: COMPASS_GOOD_POSITION_ACCURACY,
    };
  }

  if (selection.kind === 'none') return null;

  const id = compassEffectiveScenario(d, selection.id);
  // Checked before any heading is resolved: No Reading needs nothing to report nothing, so it must
  // stay available on a block with no answer authored yet.
  if (id === 'noReading') return null;

  const center = manualHeading != null
    ? normalizeDegreesForDisplay(manualHeading)
    : compassSimBases(d)[0]?.center ?? null;
  if (center == null) return null;

  const rnd = (salt: number) => sensorSimRandom(nonce, salt);
  // The heading itself is left alone in both faults: only the confidence figure trips the gate, which
  // isolates the poor-accuracy feedback state rather than confounding it with a wrong heading.
  if (id === 'poorHeading') {
    return {
      headingDegrees: center,
      headingAccuracy: COMPASS_HEADING_ACCURACY_CUTOFF + 2 + 30 * rnd(6),
      positionAccuracy: COMPASS_GOOD_POSITION_ACCURACY,
    };
  }
  return {
    headingDegrees: center,
    headingAccuracy: COMPASS_GOOD_HEADING_ACCURACY,
    positionAccuracy: COMPASS_POSITION_ACCURACY_CUTOFF + 200 + 2000 * rnd(7),
  };
}

// ─── Live test: chip legend + evaluation ───────────────────────────────────────

interface CompassTestData {
  testResult: FeedbackState[] | null;
  testChips: TestBarChip[];
  hasChips: boolean;
}

/** Whether the ANSWER holds an equation. Several surfaces have to ask this before rendering a
 * resolved band, because `num()` turns a formula into 0 and the band it produces is a real heading
 * that is confidently wrong. */
function compassAnswerHasFormula(d: CompassBlockEditorData): boolean {
  return d.conditionType === 'exact'
    ? d.answers.some(a => hasVariableChip(a.value))
    : d.betweenAnswers.some(a => hasVariableChip(a.low) || hasVariableChip(a.high));
}

function computeCompassTestData(
  d: CompassBlockEditorData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  reading: CompassSimReading | null,
  hasSubmitted: boolean,
  /** A typed manual-bypass heading, already normalized, or null. Takes precedence over `reading`:
   * a student only reaches for the bypass when the sensor has failed them, so if they have typed an
   * answer that is the answer, whatever the instrument is still claiming. */
  bypassAnswer: number | null = null,
  /** Resolves a formula answer against the roll being tested. Omitted (or `num`) for a caller with
   * no roll in hand, which is exactly right for a plain-number setup. */
  resolve: (s: string, fieldId?: string) => number = num,
  /** The roll the TEST is running against — legend chips resolve, unlike feedback chips. */
  rolledValues: Record<string, string> = {},
  /**
   * The device produced nothing. Passed rather than inferred from a null `reading`, because a null
   * reading is also what a student who has not captured yet has — and that is an ordinary No
   * Answer, not a hardware failure.
   */
  sensorDead = false,
): CompassTestData {
  const testResult = hasSubmitted && bypassAnswer != null
    // `method: 'manual'` is what tells evaluateCompass to skip the accuracy gates — the gates judge
    // an instrument, and there is no instrument behind a typed answer.
    ? evaluateCompass(
        { touched: true, headingDegrees: bypassAnswer, method: 'manual' },
        { headingAccuracy: -1 },
        compassBlockDataForEval(d, resolve),
      )
    : hasSubmitted && reading
    ? evaluateCompass(
        { touched: true, headingDegrees: reading.headingDegrees, method: 'sensor' },
        { headingAccuracy: reading.headingAccuracy, positionAccuracy: reading.positionAccuracy },
        compassBlockDataForEval(d, resolve),
      )
    : hasSubmitted
      ? evaluateCompass(
          { touched: false, headingDegrees: 0, method: 'sensor' },
          // A dead magnetometer is only the student's problem if they had no typed route out of it.
          sensorDead ? { fault: 'noData', bypassAvailable: d.bypassEnabled } : { headingAccuracy: -1 },
          compassBlockDataForEval(d, resolve),
        )
      : null;
  /** The heading actually submitted, keyed for `activeIntervalIds` — so the legend lights the ONE
   *  band or gap it fell in rather than every chip of the state. Scope '' because a compass has one
   *  number line (unlike slider's two handles). */
  const submittedByScope: Record<string, number | null> = {
    '': hasSubmitted ? (bypassAnswer ?? reading?.headingDegrees ?? null) : null,
  };
  const firedIds = new Set((testResult ?? []).map(s => s.id));
  const states = d.feedback.states;
  const find = (trigger: string) => states.find(s => s.type === 'default' && s.trigger === trigger);

  const chips: TestBarChip[] = [];
  const push = (s: FeedbackState | undefined, category: TestBarChip['category'], condition: React.ReactNode, reachable?: boolean) => {
    if (!s) return;
    chips.push({ id: s.id, condition, evaluation: s.evaluation, active: firedIds.has(s.id), category, reachable });
  };

  /**
   * The participation arms used to emit the catch-all and RETURN, which put the early exit above
   * the targeted loop below — fine while a value block could not fire a targeted state outside
   * graded, wrong the moment it could. The mode now decides what to SKIP, not where to stop.
   */
  const participation = d.gradingMode !== 'graded';

  const gateLabel = (trigger: string) => stateChipLabel({ id: '', type: 'compass', data: d } as never, { type: 'default', trigger }, t, locale);
  if (d.gradingMode !== 'optional') {
    push(find('unanswered'), 'unanswered', gateLabel('unanswered'));
  }
  // Accuracy gates sit between "answered" and "graded" in evaluateCompass, so they belong in the
  // legend as their own reachable states, not hidden implementation details — each labelled with
  // the cutoff it fires above, matching the feedback panel.
  push(find('poor_heading_accuracy'), 'incomplete', gateLabel('poor_heading_accuracy'));
  if (d.northReference === 'true') push(find('poor_position_accuracy'), 'incomplete', gateLabel('poor_position_accuracy'));

  // A block with no usable answer yet has NO accepted band — not the degenerate one a blank field
  // parses into. The feedback panel has always read it that way: its Correct card is gated on
  // `isCompassSetupIncomplete` and its Default Incorrect gaps come out as the whole domain. The
  // test bar built a band around zero instead, so a freshly added block advertised "0° to 0°" as
  // the correct answer and called the catch-all unreachable — precisely inverting the panel beside
  // it. Same helper on both sides now.
  const setupDead = isCompassSetupIncomplete(d);
  const correctRanges = setupDead ? [] : computeCompassAcceptedRanges(compassAcceptedRangeConfig(d, resolve));
  // Nothing is correct outside graded, so the bar has no Correct chip to draw.
  const correctState = participation ? undefined : find('correct');
  // Hoisted: both default states' chips are derived from one function that needs the carve-outs,
  // and this used to be declared between them.
  const targetedTriggers = states.filter(s => s.type === 'targeted').map(s => s.trigger);
  /** The legend is a live surface, so it resolves — but an EMPTY map means "not rolled yet", not
   *  "no variables", and passing it as a roll would resolve every formula to NaN. */
  const chipRoll = Object.keys(rolledValues).length > 0 ? rolledValues : null;
  // The ONE derivation, rendered into the legend's envelope. `compassRangeChips` was this block's
  // fourth way of answering the same question; the legend now draws the same list the panel and the
  // bar do, and the roll is the only thing that differs between them.
  // Derived ONCE and reused for both the list and the highlight — two calls with independently
  // supplied arguments is the shape this refactor exists to remove.
  const correctList = correctState ? compassChips(d, correctState, chipRoll, t, locale, d.gradingMode, targetedTriggers) : [];
  const correctFanOut = (setupDead || !correctState) ? [] : toTestBarChips(correctList, {
      id: correctState.id, category: 'correct', active: firedIds.has(correctState.id),
      activeSourceIds: activeIntervalIds(correctList, submittedByScope),
      unreachableLabel: t('blocks.feedbackPanel.unreachable'),
      connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
    });
  // No `compassCorrectChipLabel` fallback any more. It existed because the per-range fan-out could
  // not describe an unrolled equation (`num()` made every band one around zero), so a formula
  // answer took a separate joined-string path — which is exactly the mode fork `compassChips` now
  // owns internally. Setup-incomplete is still its own case: there is no answer to state at all.
  if (setupDead) push(correctState, 'correct', t('blocks.feedbackPanel.unreachable'), false);
  else chips.push(...correctFanOut);

  // The PRESET states the authored condition on the bar too, the same chips the panel shows and
  // the same ones graded's Correct row draws — never "= ?" from a trigger that holds no value.
  for (const s of visibleTargeted(states, d.gradingMode)) {
    if (s.trigger === SET_ANSWER_TRIGGER) {
      chips.push(...toTestBarChips(
        compassChips(d, { trigger: 'correct' } as never, chipRoll, t, locale, d.gradingMode, targetedTriggers), {
          id: s.id, category: 'targeted', active: firedIds.has(s.id),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
      continue;
    }
    chips.push({
      id: s.id,
      condition: resolvedTriggerChip({
        trigger: s.trigger, scenario: rolledValues,
        summaryOf: resolved => compassTargetedChipLabel(resolved, d, t, locale),
        evaluate: (expr, values) =>
          evaluateFormulaRaw(expr, values, angleModeFor(d, `feedback.${s.id}.trigger`)),
        valuesOf: triggerValuesOf,
        hasChip: hasVariableChip,
        resolveTrigger: resolveTriggerValues,
      }),
      evaluation: displayEvaluation(d.gradingMode, s.evaluation),
      active: firedIds.has(s.id),
      category: 'targeted',
    });
  }


  if (participation) {
    const catchAll = find(d.gradingMode === 'optional' ? 'any_response' : 'any_answer');
    if (catchAll) {
      /*
       * OPTIONAL is ONE ROW. Everything accumulated above — the gates, the targeted loop — belongs
       * to a mode that can fire them, and optional fires none of them: it is inert, so Any Response
       * is the only outcome it has. Cleared rather than never-pushed, because the rungs above are
       * shared with survey, which does fire them. `legendStatesFor` makes the same call for the
       * blocks that build their legend through it.
       */
      if (d.gradingMode === 'optional') chips.length = 0;

      // The SAME derivation the panel's catch-all uses, so the bar and the card cannot disagree
      // about what still falls through. It enumerates once Set Answer carves the accepted band
      // out, and says the plain label when nothing has. The tick goes the moment a target exists.
      const targetedList = visibleTargeted(states, d.gradingMode);
      chips.push(...toTestBarChips(
        catchAllChips(d.gradingMode, targetedList.length > 0, t,
          // Enumerates whenever ANYTHING is targeted; `acceptedClaimed` only decides whether the
          // band comes out of the leftovers, which it does once a Set Answer target owns it.
          () => compassChips(d, { trigger: 'incorrect' } as never, chipRoll, t, locale, d.gradingMode,
            targetedTriggers, targetedList.some(x => x.trigger === SET_ANSWER_TRIGGER))),
        {
          id: catchAll.id, category: d.gradingMode === 'optional' ? 'any_response' : 'any_answer',
          active: firedIds.has(catchAll.id),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
    }
    return { testResult, testChips: chips, hasChips: chips.length > 0 };
  }

  // Default Incorrect goes unreachable once every non-accepted heading already has its own
  // targeted state — same convention every audited block's catch-all follows.
  const incorrectState = find('incorrect');
  // An answer that is an equation nobody has rolled yet has no resolved band, so the coverage maths
  // has nothing real to work with — it was concluding "fully covered" and printing Unreachable for
  // the state that in fact catches everything. Fall back to the symbolic label ("Anything but
  // {n}*30"), which is what the feedback panel shows for the same block, and let the resolved bands
  // take over the moment a scenario is rolled.
  const unresolvedFormulaAnswer = compassAnswerHasFormula(d) && correctFanOut.length === 0 && !setupDead;
  const incorrectReachable = unresolvedFormulaAnswer || !isCompassDefaultIncorrectUnreachable(correctRanges, targetedTriggers);
  // The same gaps the feedback panel maps over, so the two panels describe Default Incorrect
  // identically instead of one saying "anything but X" and the other listing the real leftovers.
  const incorrectList = incorrectState ? compassChips(d, incorrectState, chipRoll, t, locale, d.gradingMode, targetedTriggers) : [];
  const incorrectFanOut = (incorrectReachable && incorrectState) ? toTestBarChips(incorrectList, {
      id: incorrectState.id, category: 'incorrect', active: firedIds.has(incorrectState.id),
      activeSourceIds: activeIntervalIds(incorrectList, submittedByScope),
      unreachableLabel: t('blocks.feedbackPanel.unreachable'),
      connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
    }) : [];
  if (incorrectFanOut.length > 0) chips.push(...incorrectFanOut);
  // No `compassIncorrectChipLabel` fallback: an unresolved equation is no longer a special case
  // needing a joined string, because `compassChips` states the equations as chips instead.
  else push(incorrectState, 'incorrect', null, incorrectReachable);

  return { testResult, testChips: chips, hasChips: chips.length > 0 };
}

/** The editor's string-shaped state as the parsed CompassBlockData evaluateCompass expects. */
// The raw (pre-wraparound-split) {lo, hi} pair per teacher-authored answer — one entry per OR'd
// exact answer or between-row. Deliberately NOT the post-split pieces computeCompassAcceptedRanges
// produces for the reachability math: a single wrapped exact answer (e.g. 350 +/- 20) should render
// as ONE chip with one wedge that itself wraps through 0/360 (CompassArcDiagram already draws that
// correctly in one arc), not two separate-looking chips for what the teacher authored as one answer.
//
// Module scope, not component scope: the test-bar legend builds its chips from the same pairs the
// feedback panel does, and the two must not drift.
/** `resolve` defaults to `num`, so a display surface with no roll behaves as before; the TEST bar
 * passes the roll it is testing, which is what makes a formula answer show real numbers there
 * instead of a band around zero. */


/**
 * Fans one feedback state out into one legend pill per range, with plain "or" separators between —
 * the same breakdown the feedback panel renders, just as TestBarChips instead of an inline chip row.
 * Follows MCQ's own convention for a multi-part condition (see its testChips note): show each part
 * as its own pill rather than one joined label, because a joined label hides half the condition.
 *
 * Text only, no CompassArcDiagram: the legend rail is ~135px, far too narrow for a wedge drawing,
 * and the arc is the one part of the feedback chip that does not survive the move.
 *
 * No "or" separators between the pills. A legend chip lights up when its state fires, and that
 * already tells the teacher which range was hit — spelling out the relation earns nothing and adds
 * a second thing to read per row in a ~135px rail.
 */
/**
 * COMPASS'S ONE DERIVATION of what a default state's condition looks like, in either mode.
 *
 * This block answered that question in six places, partitioned by mode and by surface:
 * `correctFanOut`/`incorrectFanOut` for the legend, `incorrectChips` for the panel when rolled,
 * `incorrectFormulaChip` for the panel when unrolled, `summarizeCorrect` for the answer bar, and
 * `compassCorrectChipLabel`/`compassIncorrectChipLabel` in lib as a fallback. Each was locally
 * sensible; together they guaranteed the surfaces disagreed, and every fix to one left the others
 * behind.
 *
 * The mode is now a PARAMETER, not a fork in the code — which is the whole point, because the two
 * modes are exactly what kept drifting:
 *
 *   roll === null   the answers cannot be resolved, so state them: one chip per authored answer
 *                   (`formulas`, so the renderer draws real chips), "or" for Correct and
 *                   "not <answer>" joined "and" for its complement — De Morgan.
 *   roll supplied   every formula resolves, so say exactly what a hard-coded answer would say:
 *                   the accepted bands, or the leftover gaps, one chip each.
 *
 * Literals are included in the unrolled reading. `answerExprTexts` maps the answer list rather than
 * filtering it to formula-bearing entries, which is what dropped a literal sitting beside a
 * variable from every surface at once.
 */

/** `resolve` turns the editor's raw formula text ("{n}*30") into the number the maths needs.
 * Defaults to `num`, so every existing caller — and any plain-number setup — behaves exactly as
 * before. Tolerances deliberately never resolve: a tolerance on a loop is absolute degrees, not an
 * equation. Same bridge shape slider and numerical already use. */
/** Exported for `angle-mode-grading`, which grades through the REAL chain rather than a
 *  stand-in: the point of that test is that the app's own wiring resolves per field. */
export function compassBlockDataForEval(d: CompassBlockEditorData, resolve: (s: string, fieldId?: string) => number = num): CompassBlockData {
  return {
    question: d.question,
    visibilityMode: d.visibilityMode,
    liveDisplayFormat: d.liveDisplayFormat,
    northReference: d.northReference,
    conditionType: d.conditionType,
    answers: d.answers.map(a => ({ id: a.id, value: resolve(a.value, `${a.id}.value`), toleranceValue: num(a.toleranceValue), toleranceAbove: num(a.toleranceAbove), toleranceBelow: num(a.toleranceBelow) })),
    betweenAnswers: d.betweenAnswers.map(a => ({ id: a.id, low: resolve(a.low, a.id), high: resolve(a.high, a.id) })),
    toleranceSymmetry: d.toleranceSymmetry,
    gradingMode: d.gradingMode,
    bypassEnabled: d.bypassEnabled,
    feedback: d.feedback,
  };
}

/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderCompassFiredFeedback(testResult: FeedbackState[] | null): React.ReactNode {
  return <FiredFeedback states={testResult} className='mt-3 flex flex-col gap-2' />;
}

/** The type scale only — weight and colour come from `sensorReadoutTone`, which is shared with GPS
 *  and Tilt so that committing a reading looks the same on all three. */
const COMPASS_READOUT_CLASS = 'select-none font-mono text-5xl tabular-nums';

/**
 * THE student surface: exactly what a student sees and does, and nothing else.
 *
 * Counterpart of `GpsStudentWidget`, and the same contract — the live-test card, Preview and the (not
 * yet built) student runtime all render THIS, unmodified, so anything added here reaches students and
 * anything a student must never see belongs in `CompassTestWidget` instead. Order is the student's
 * reading order: question, then the control/readout the mode calls for, then whatever feedback fired.
 */
function CompassAnswerDisplay({
  d, reading, liveHeading, hasReading, onTakeReading, onClearReading, formatLive, deviceNotice, t, firedFeedback, disabled, bypass, scenario,
}: {
  d: CompassBlockEditorData;
  /** The leader's variant, ghosted, above the question — see `AnswerDisplayFrame`'s own note. */
  scenario?: React.ReactNode;
  reading: CompassSimReading | null;
  /**
   * What the instrument is seeing RIGHT NOW, whether or not it has been captured.
   *
   * Separate from `reading`, which Capture withholds until its button is pressed — that gate is
   * right for the evaluation and wrong for the display, which has to be able to show a muted live
   * heading before the press. Without it the pre-capture state was a blank line, and a student
   * could not tell a working compass from a dead one until after committing to it.
   */
  liveHeading?: number | null;
  hasReading: boolean;
  onTakeReading?: () => void;
  /** Discards the committed reading and puts the live readout back — see SensorCaptureButton. */
  onClearReading?: () => void;
  formatLive: (v: number, f: CompassLiveDisplayFormat) => React.ReactNode;
  /** A device failure that stopped a reading existing at all. Shown in EVERY visibility mode, Capture
   * on Submit included: that mode has no sensor control of its own, so without this a student whose
   * capture failed sees an unchanged block and no reason for it. */
  deviceNotice?: string | null;
  t: (k: string, opts?: Record<string, unknown>) => string;
  firedFeedback?: React.ReactNode;
  /** The student's typed fallback answer. Absent only on the unselected card, which renders the
   * box as an inert span (a real input cannot live inside the card's activation button).
   * `answer` is the same value parsed and normalized, or null — it drives the READOUT, not just the
   * evaluation, so a student who types their answer sees it where the sensor's would have been. */
  bypass?: { value: string; onChange: (v: string) => void; invalid: boolean; answer: number | null };
  /** Non-interactive summary for the unselected canvas card: same markup, nothing clickable.
   * Named to match MCQAnswerDisplay/DropdownAnswerDisplay's own prop. Also aria-hides, which those
   * two do not need — this renders INSIDE the card's activation button. */
  disabled?: boolean;
}) {
  // `exact` only: a tolerance around a hidden bearing discloses precision, while a `between` range
  // IS the answer. Absolute always — Compass hardcodes `toleranceMode: 'absolute'` because a percentage
  // of a bearing is meaningless on a loop ("360 +/- 2%" is +/-7.2 degrees purely from the numeral).
  const { i18n } = useTranslation();
  const first = d.answers[0];
  const sameTolerance = d.answers.every(a =>
    a.toleranceValue === first?.toleranceValue && a.toleranceAbove === first?.toleranceAbove && a.toleranceBelow === first?.toleranceBelow);
  // See `effectiveSetting`: a mode that takes the setting away must also take its effect away.
  const toleranceHintText = effectiveSetting('showTolerance', d.gradingMode, d.showTolerance)
    && d.conditionType === 'exact' && first && sameTolerance
    ? toleranceHint(
        { symmetry: answerSymmetry(first, d), toleranceValue: first.toleranceValue, toleranceAbove: first.toleranceAbove, toleranceBelow: first.toleranceBelow },
        { fmt: (n: number) => fmtLocale(n, i18n.language), t, unit: '°' })
    : null;
  const livePlaceholder = t(d.liveDisplayFormat === 'cardinalDegree' ? 'blocks.compass.liveReadPlaceholderCardinalDegree'
    : d.liveDisplayFormat === 'cardinalOnly' ? 'blocks.compass.liveReadPlaceholderCardinalOnly'
    : 'blocks.compass.liveReadPlaceholderDegrees');
  const captureLabel = d.northReference === 'true' ? t('blocks.compass.captureButtonTrue') : t('blocks.compass.captureButtonMagnetic');
  const bypassAnswer = bypass?.answer ?? null;
  /**
   * Whether what is on screen is the student's ANSWER rather than the instrument still reading.
   *
   * Live Reading counts as committed even though nothing was pressed: it updates forever and it is
   * graded as it stands, so muting it would promise a commit step that mode does not have.
   */
  const committed = d.visibilityMode === 'live' || bypassAnswer != null || hasReading;
  /** The one heading on screen, whatever its provenance — read once so the readout and the
   *  announcement can never disagree about what was committed. */
  const shownHeading = bypassAnswer != null ? bypassAnswer
    : reading != null ? reading.headingDegrees
    : liveHeading ?? null;

  /**
   * What to DO about a reading the accuracy gates would reject, shown while it can still be fixed.
   *
   * The feedback state says what went wrong; it fires after submission, by which point the attempt is
   * spent. This says how to fix it, and it has to be here rather than in that feedback because the
   * whole value is in arriving BEFORE the capture is committed. Same reason and same treatment as
   * GPS's network-based warning on its own display.
   *
   * Not a feedback state, and so not teacher-editable: it is the only actionable instruction a
   * student gets, and a teacher rewording their feedback should not be able to delete it. Nothing
   * else will supply it either — a browser cannot summon the OS calibration prompt that a native app
   * can, so if we do not say this, nobody does.
   *
   * Ordered and exclusive, mirroring evaluateCompass's own gate ladder: position accuracy only feeds
   * the true-north correction, so if the heading itself is too noisy there is nothing worth
   * correcting, and two near-identical "not accurate enough" lines would leave a student guessing
   * which to act on. Suppressed entirely for a typed bypass, whose evaluation skips both gates.
   */
  const accuracyAdvice = bypassAnswer != null || !reading ? null
    // Negative means "no confidence figure reported" (iOS's own convention), not a bad one.
    : reading.headingAccuracy >= 0 && reading.headingAccuracy > COMPASS_HEADING_ACCURACY_CUTOFF
      ? { label: t('blocks.compass.headingAccuracyAdviceLabel'), advice: t('blocks.compass.headingAccuracyAdvice') }
    : d.northReference === 'true' && reading.positionAccuracy > COMPASS_POSITION_ACCURACY_CUTOFF
      ? { label: t('blocks.compass.positionAccuracyAdviceLabel'), advice: t('blocks.compass.positionAccuracyAdvice') }
    : null;

  return (
    <div
      className={`flex flex-col gap-3 ${disabled ? 'pointer-events-none' : ''}`}
      aria-hidden={disabled || undefined}
    >
      {scenario}
      <BlockQuestion html={d.question} />

      {/* GUIDANCE, so it sits with the instruction and above the instrument, never after a reading.
          Same placement and italic treatment as GPS: a live heading readout a few lines below says
          what the device managed, and this says what the teacher requires. */}
      {toleranceHintText && <p className={`text-center ${TOLERANCE_HINT_CLASS}`}>{toleranceHintText}</p>}

      {/* Everything below the question dims on the collapsed card: the reading shown there is
          representative, not a live fix. The question is NOT dimmed — it is the same authored
          text on every surface, and dimming it also faded its variable chips to 50% in these
          three blocks and no others. */}
      {/*
        * THE SAME COLUMN THE OTHER BLOCKS GET, which this one was never given.
        *
        * Seven blocks route their answer through `AnswerDisplayFrame`, which caps and centres
        * it; the three sensors and FITB hand-roll the frame's shape and so were full-width and
        * left-aligned. The instrument itself was centred by its own `mx-auto w-fit` and looked
        * fine, which is why only the FEEDBACK under it read as out of place — it was the one
        * part wide enough to show the difference.
        *
        * The class and not the frame, because the frame also opens a `BlockPreviewProvider`,
        * and Preview deliberately supplies one roll for the whole page — see this block's own
        * note on not nesting it. `ordering-block` applies the column the same way and for the
        * same reason.
        */}
      <div className={`${ANSWER_COLUMN_CLASS} flex flex-col gap-3 ${disabled ? 'opacity-50' : ''}`}>

        {/* Capture on Submit shows the student no SENSOR control at all — the device reads on submit.
            The manual bypass below is outside this branch and is the one exception; see its comment. */}
        {d.visibilityMode !== 'passive' && (
          <div className='mx-auto flex w-fit flex-col items-center gap-2'>
            {/* Live Reading has no button by definition — the heading is simply on screen, and it
                is the answer. Capture is the only mode with anything to press. */}
            {d.visibilityMode !== 'live' && (
              <SensorCaptureButton
                committed={committed}
                captureLabel={captureLabel}
                icon={<Compass />}
                onCapture={onTakeReading}
                onReset={onClearReading}
                disabled={disabled}
              />
            )}
            {/*
              ONE readout for both modes, and one in both states of Capture — not a muted `text-sm`
              echo after the button. A captured heading is the student's answer and the most
              important thing on the block once it exists, so it is presented identically however it
              was obtained.

              Capture used to render an INVISIBLE spacer before the press: height held, paint
              dropped, on the reasoning that a heading above an unpressed button claims an answer
              already exists. The tone does that job properly now — muted says "the instrument is
              reading and this is not yours yet", which is both more informative than a blank line
              and the thing a student needs in order to know the compass works before committing to
              it. See `sensorReadoutTone`.

              A typed bypass wins over the sensor exactly as it does in the evaluation: showing the
              instrument's heading while grading the typed one would tell a student their answer is
              something other than what they entered.
            */}
            <span className={`${COMPASS_READOUT_CLASS} ${sensorReadoutTone(committed)}`}>
              {shownHeading != null ? formatLive(shownHeading, d.liveDisplayFormat)
                : <SpokenPlaceholder>{livePlaceholder}</SpokenPlaceholder>}
            </span>
            {/* The announcement, and the only live region here — this column used to be one itself,
                which in Live Reading meant one polite announcement per sensor sample. */}
            <SensorAnnounce
              value={d.visibilityMode === 'capture' && hasReading && shownHeading != null
                ? formatLive(shownHeading, d.liveDisplayFormat) : null}
            />
          </div>
        )}

        {/* The manual-entry fallback a teacher can enable, shown on EVERY surface when enabled — not
            just the unselected card. Gating it on `disabled` meant a teacher who ticked the setting saw
            no trace of it in test or Preview, which is the divergence this component exists to prevent.

            A REAL input. It was a static span placeholder, deferred to "the student runner", but that
            left the one affordance for a student whose sensor doesn't work as the one thing a teacher
            could not try — and it is this component's whole job that test, Preview and student are the
            same widget. On the unselected card it degrades to a span, for the same reason the capture
            button does: that card mounts this inside its own activation button, and a nested
            interactive element is invalid HTML.

            OUTSIDE the visibility-mode branch, so it appears in Capture on Submit too. That mode
            otherwise shows the student nothing but the question, and it is the one mode where a
            student whose sensor is missing has no gesture to fall back on at all — the device is
            supposed to read for them, and when it can't there is nothing else on screen. The bypass
            is the deliberate exception to "Capture on Submit shows no control". */}
        {d.bypassEnabled && (
          <div className='mx-auto flex w-fit flex-col items-center gap-2'>
            <SensorManualBypass
              label={t('blocks.manualBypass.label')}
              quantityLabel={t('blocks.compass.manualBypassQuantity')}
              value={bypass?.value ?? ''}
              onChange={bypass?.onChange ?? (() => {})}
              invalid={bypass?.invalid ?? false}
              invalidMessage={t('blocks.manualBypass.outOfRange')}
              inert={!bypass}
            />
          </div>
        )}

        {/* A missing reading outranks a poor one: there is nothing to judge the quality of. */}
        {deviceNotice
          ? <SensorAdvisory advice={deviceNotice} />
          : accuracyAdvice && <SensorAdvisory label={accuracyAdvice.label} advice={accuracyAdvice.advice} />}

        {firedFeedback}
      </div>
    </div>
  );
}

/** The teacher's test harness wrapped around `CompassAnswerDisplay`: a Simulate strip, plus the note
 * explaining Capture on Submit's deliberately empty student surface.
 *
 * Everything in here is teacher-only and must stay out of the student widget. The strip shows in all
 * three visibility modes — it is the only way this block gets tested at all, so hiding it in Capture
 * on Submit would make that mode untestable. It also shows in Preview: without a real magnetometer
 * there is no other way to drive a reading there, and it is the single acknowledged place where
 * Preview is not yet identical to what a student will get. */
function CompassTestWidget({
  d, selection, onSelectionChange, mode, onModeChange, deviceStatusHint, deviceNotice, manual, reading, liveHeading, onTakeReading, onClearReading, hasReading, t, locale, formatLive, hintReading, bypass, children, sharedDevice, scenario
}: {
  /** The leader's variant, ghosted, above the question — forwarded to the display. */
  scenario?: React.ReactNode;
  /**
   * Preview drives this block from the task's ONE shared device, so the per-block strip
   * comes out: three strips in Preview model three phones, which is the thing the shared
   * simulator exists to stop. The strip stays in the EDITOR, where a teacher is testing one
   * block against its own answer in isolation and that is exactly what they want.
   */
  sharedDevice?: boolean;
  d: CompassBlockEditorData;
  selection: CompassSimSelection;
  onSelectionChange: (next: CompassSimSelection) => void;
  /** Separate from onSelectionChange because arming the device must request permission first. */
  mode: SensorSourceMode;
  onModeChange: (next: SensorSourceMode) => void;
  /** What the magnetometer is doing, or why it cannot be read here. Only consulted in device mode. */
  deviceStatusHint: string;
  /** Device failures only, for the student surface. */
  deviceNotice: string | null;
  manual: { value: string; onChange: (v: string) => void; invalid: boolean };
  reading: CompassSimReading | null;
  onTakeReading: () => void;
  onClearReading: () => void;
  /** The uncaptured instrument value — see CompassAnswerDisplay's own prop. */
  liveHeading?: number | null;
  hasReading: boolean;
  t: (k: string, opts?: Record<string, unknown>) => string;
  locale: string;
  formatLive: (v: number, f: CompassLiveDisplayFormat) => React.ReactNode;
  /** Ungated: what the armed selection would report, for the always-visible hint. */
  hintReading: CompassSimReading | null;
  /** Device armed but this machine produced no heading — a hardware limit, not a broken block. */
  bypass: { value: string; onChange: (v: string) => void; invalid: boolean; answer: number | null };
  children?: React.ReactNode;
}) {
  const setupIncomplete = compassSimBase(d) == null;
  const isNoReading = selection.kind === 'scenario' && selection.id === 'noReading';
  // An existing reading is described first, ahead of the incomplete-setup notice. A manually typed
  // heading produces one even with no answer authored, and telling a teacher to "set a correct
  // heading" while the readout is showing them their own value contradicts what is on screen.
  const hint = mode === 'device' ? deviceStatusHint
    : hintReading ? t('blocks.compass.simulateWillRead', { value: `${fmtLocale(hintReading.headingDegrees, locale)}°` })
    : setupIncomplete ? t('blocks.compass.simulateNeedsAnswer')
    : isNoReading ? t('blocks.compass.simulateWillReadNothing')
    : '';

  return (
    <div className='flex flex-col gap-3'>
      {!sharedDevice && (
      <SensorSimulateBar
        groupLabel={t('blocks.sensorSource.scenarioGroup')}
        scenarios={compassScenarioIds(d).map(id => ({
          id,
          label: t(`blocks.compass.simulateScenario.${id}`),
          suppressesValue: id === 'noReading',
        }))}
        activeScenarioId={selection.kind === 'scenario' ? compassEffectiveScenario(d, selection.id) : null}
        onPickScenario={id => onSelectionChange(id === '' ? { kind: 'none' } : { kind: 'scenario', id: id as CompassScenarioId })}
        manual={{
          label: t('blocks.compass.simulateManualLabel'),
          active: selection.kind === 'manual',
          fields: [{
            key: 'heading',
            // Names the field AND its unit: the visible `°` is decorative and aria-hidden, and
            // "Or a specific heading" is the group's label rather than this one box's name.
            ariaLabel: t('blocks.compass.simulateManualAriaLabel'),
            value: manual.value,
            onChange: manual.onChange,
            invalid: manual.invalid,
            disabled: isNoReading,
            suffix: '°',
          }],
        }}
        mode={mode}
        onModeChange={onModeChange}
        deviceTabLabel={t('blocks.sensorSource.device')}
        simulateTabLabel={t('blocks.sensorSource.simulate')}
        hint={hint}
      />
      )}

      <CompassAnswerDisplay
          scenario={scenario}
        d={d}
        reading={reading}
        liveHeading={liveHeading}
        hasReading={hasReading}
        onTakeReading={onTakeReading}
        onClearReading={onClearReading}
        formatLive={formatLive}
        deviceNotice={deviceNotice}
        bypass={bypass}
        t={t}
        firedFeedback={children}
      />
    </div>
  );
}

function compassCollapsedBar(
  d: CompassBlockEditorData,
  /** Nodes, not a string: a formula answer renders through `FormulaWithValues` so the variable
   * appears as a CHIP, the same as numerical's and slider's answer bands. A plain string would put
   * the literal characters "{angle}" on the card. */
  correctSummary: React.ReactNode,
  t: (k: string, opts?: Record<string, unknown>) => string,
  issues: BlockIssue[],
): React.ReactNode {
  const mobileChip = <MobileOnlyChip />;

  // The mobile chip rides along in every state, which used to be the stated reason this bar
  // couldn't call a shared renderer. It can now: `trailing` is part of the shared bar's own
  // contract, so all three sensor blocks and the six others render one implementation.
  const issueBar = collapsedIssueBar(issues, mobileChip);
  if (issueBar) return issueBar;

  if (d.gradingMode !== 'graded') return collapsedModeBar(d.gradingMode, t, 'compass', mobileChip);

  return (
    <CollapsedBlockBar tone='success' align='start' scrollX trailing={mobileChip}>
      <span className='min-w-0'>{correctSummary}</span>
    </CollapsedBlockBar>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CompassBlock({
  block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver,
  isMain = false, embedded, onClose, mobileIsEditing, initialIsEditingFeedback, onFeedbackModeChange,
  blocks, onRename,
}: CompassBlockProps) {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  /** Through the version: the block's own top-level answer is vestigial once a bank exists. */
  /**
   * FOLLOWING A SCENARIO — the value family's shape, as Numerical and Slider carry it. A row
   * holds `COMPASS_VERSION_KEYS` flat, so the editor resolves one exactly as it resolves a version and
   * `applyScenarioRow` is the only extra step.
   */
  const storedForLink = initData(block.data);
  const leaderVersions = ((blocks ?? []).find(b => b.id === storedForLink.followsBlockId)?.data as
    { versions?: DisplayVersion[] } | undefined)?.versions;
  const [leaderShownRaw, setLeaderShown] = useVersionInForce(storedForLink.followsBlockId ?? block.id, 'test');
  const shownScenarioId = leaderShownRaw ?? leaderVersions?.[0]?.id;

  /**
   * WHAT THE TEST BAR PAGES WHEN THIS BLOCK FOLLOWS — the leader's scenarios, not its own bank.
   *
   * Same shape `useScenarioFollower` returns for the blocks that use it, and the same one
   * Dropdown and MCQ already build. A follower has no bank of its own (§2.2 makes the two
   * exclusive), so its test bar had nothing to page and a teacher could not step the scenarios
   * from the surface they were testing on — the only pager was in Answer Setup. The pointer is
   * shared, so this and the leader's own card move together rather than disagreeing.
   */
  const testPager = storedForLink.followsBlockId
    ? { versions: (leaderVersions ?? []) as { id: string }[], activeId: shownScenarioId,
      onSelect: setLeaderShown, noun: 'scenario' as const }
    : null;
  /** A version FIRST, then the row on top — a follower has no bank, so only one ever applies. */
  const resolveForEditor = (d: CompassBlockEditorData, versionId: string | undefined) =>
    applyScenarioRow(
      applyCompassVersion(d, versionId) as unknown as Record<string, unknown>,
      shownScenarioId, COMPASS_VERSION_KEYS,
    ) as unknown as CompassBlockEditorData;
  const initial = useRef(resolveForEditor(initData(block.data), editorVersion));
  const shownVersions = (initData(block.data).versions ?? []) as { id: string }[];
  const questionHtmlRef = useRef(initial.current.question);
  /**
   * The question HTML the editor is HANDED, mirroring the ref beside it. The ref is the
   * synchronous read the push paths depend on; a ref cannot re-render, so restored HTML needs
   * state to reach the screen. See keyword, where this was first worked out.
   */
  const [editedQuestionHtml, setQuestionHtml] = useState(initial.current.question);

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));

  const [editedNorthReference, setNorthReference] = useState(initial.current.northReference);
  const [editedVisibilityMode, setVisibilityMode] = useState(initial.current.visibilityMode);
  const [editedLiveDisplayFormat, setLiveDisplayFormat] = useState(initial.current.liveDisplayFormat);
  const [editedConditionType, setConditionType] = useState(initial.current.conditionType);
  const [editedAnswers, setAnswers] = useState(initial.current.answers);
  const [editedToleranceSymmetry, setToleranceSymmetry] = useState(initial.current.toleranceSymmetry);
  const [editedShowTolerance, setShowTolerance] = useState(initial.current.showTolerance);
  const [editedBetweenAnswers, setBetweenAnswers] = useState(initial.current.betweenAnswers);
  // Answer equations wait for the teacher to stop typing before being called malformed — every
  // formula is invalid partway through. Declared here, above the `if (!isEditing)` branch, because
  // that branch returns: a hook after it would not run on the collapsed path. Keyed off the same
  // state `liveD` is assembled from further down, and off `block.data` for the card.
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);
  const [editedBypassEnabled, setBypassEnabled] = useState(initial.current.bypassEnabled);
  const [variableTestMode, setVariableTestMode] = useState<'min' | 'max' | 'random' | null>(null);
  // Numerical's model, not slider's: a scenario SEPARATE from the block-test roll. Null means no
  // scenario picked, and the feedback panel then shows the equation as chips rather than resolving
  // it — a display surface shows the variable, not one roll of it. The test bar keeps using the
  // block roll, because that IS the testing-condition line.
  const [feedbackTestValues, setFeedbackTestValues] = useState<Record<string, string> | null>(null);
  const [armedAnswerId, setArmedAnswerId] = useState<string | null>(null);
  const [armedBetweenId, setArmedBetweenId] = useState<string | null>(null);
  const sharedToolbar = useSharedEditorToolbar();
  const { setActiveEditor } = sharedToolbar;

  const [isCapturing, setIsCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  /** The armed Device produced no usable heading within the timeout. Separate from `captureError`,
   * which renders beside the answer-setup Capture button — a teacher arming Device in the SIMULATE
   * strip needs the answer there, not in a different part of the editor they may not be looking at. */
  const [deviceUnavailable, setDeviceUnavailable] = useState(false);
  // A just-captured reading staged for review before it's committed into the last answer row —
  // never persisted itself; the teacher inspects heading/accuracy and explicitly clicks "Use",
  // so a poor-accuracy reading never silently overwrites an answer. Mirrors GPS's own
  // pendingCapture pattern exactly.
  const [pendingCapture, setPendingCapture] = useState<{ headingDegrees: number; headingAccuracy: number } | null>(null);

  // ── Live test state ─────────────────────────────────────────────────────────
  // Mirrors every audited block's own test-panel state: continuous vs. submit-gated, whether an
  // evaluation has run, and the block-specific "answer" — here, which simulated scenario is armed
  // and whether Take Reading has been pressed for it yet.
  const [testSelection, setTestSelection] = useState<CompassSimSelection>({ kind: 'none' });
  /** The live heading from this device, streaming only while the Device option is armed. A continuous
   * listener rather than the one-shot `readOneHeadingEvent` the answer-setup capture uses: Live Reading
   * needs it updating, and Capture/Capture on Submit just take the latest value when they fire. */
  const [deviceReading, setDeviceReading] = useState<{ headingDegrees: number; headingAccuracy: number } | null>(null);

  // Bumped on every scenario pick so the randomised value inside a category is redrawn — see
  // sensorSimRandom for why this can't just be Math.random().
  const [simNonce, setSimNonce] = useState(0);
  /** What this block handed in last time it was on screen -- see `readingFor` for when it is used. */
  const prior = usePriorAnswer<CompassSimReading>();
  const [restoredReading, setRestoredReading] = useState<CompassSimReading | null>(prior ?? null);
  const [testTaken, setTestTaken] = useState(prior !== undefined);
  const [continuous, setContinuous] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');
  /** This block's own variable roll, for BLOCK TEST mode only.
   *
   * Preview deliberately does NOT nest this: the task-level BlockPreviewProvider supplies one roll
   * shared by every block on the page, which is the whole point of previewing a task. Testing a
   * single block is the opposite case — its own roll, re-rollable from its own Reroll button. */
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>(() => rollVariableValues(variables));
  // Preview shows ONE task-level roll shared by every block: a task whose blocks all read {n} has
  // to agree about n, and seeding Preview from this block's own roll made them disagree. Block
  // testing keeps its private roll plus reroll, which is the whole point of it.
  const taskPreviewRoll = useTaskPreviewRoll();
  /* Recalled answers ride the same map the variables do — see `useRolledWithRecalls`. The
     equation field, the answer bar and the issue list then all read one roll. */
  const rolledValues = useRolledWithRecalls(isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues);
  // The roll a test grades against. Angles are always degrees, so the formula scope is fixed —
  // unlike numerical, there is no angleMode setting to honour here.
  const resolveAnswer = (s: string, fieldId = ''): number =>
    (s.trim() === '' ? NaN : evaluateFormulaRaw(s, rolledValues, angleModeFor(fields, fieldId)));
  // Fresh values each time the block enters a test surface, so opening a block never shows the
  // stale roll from the last time it was open. Mirrors MCQ's own isSelected/phoneTestMode effects.
  useEffect(() => {
    if (isSelected || phoneTestMode === 'test') setRolledValues(rollVariableValues(variables));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, phoneTestMode]);
  /** Scratch text for the simulate strip's manual heading field — parsed on apply, never saved. */
  const [simManualInput, setSimManualInput] = useState('');
  const [bypassInput, setBypassInput] = useState('');

  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    question: editedQuestionHtml,
    northReference: editedNorthReference,
    visibilityMode: editedVisibilityMode,
    liveDisplayFormat: editedLiveDisplayFormat,
    conditionType: editedConditionType,
    answers: editedAnswers,
    toleranceSymmetry: editedToleranceSymmetry,
    showTolerance: editedShowTolerance,
    betweenAnswers: editedBetweenAnswers,
    gradingMode: editedGradingMode,
    bypassEnabled: editedBypassEnabled,
  };
  const restored = useRestoredData<CompassBlockEditorData>({
    raw: block.data,
    isEditing,
    derive: (raw) => resolveForEditor(initData(raw as Parameters<typeof initData>[0]), editorVersion),
    current: { ...resolveForEditor(initData(block.data), editorVersion), ...edited } as CompassBlockEditorData,
    apply: seedEditorFrom,
  });

  /** Push one version's content into the editor's local state — an undo, another device's save
   *  and switching version are the same act from here. */
  function seedEditorFrom(next: CompassBlockEditorData) {
      setNorthReference(next.northReference);
      setVisibilityMode(next.visibilityMode);
      setLiveDisplayFormat(next.liveDisplayFormat);
      setConditionType(next.conditionType);
      setAnswers(next.answers);
      setToleranceSymmetry(next.toleranceSymmetry);
      setShowTolerance(next.showTolerance);
      setBetweenAnswers(next.betweenAnswers);
      setGradingMode(next.gradingMode);
      setBypassEnabled(next.bypassEnabled);
      questionHtmlRef.current = next.question;
      setQuestionHtml(next.question);
      resyncFeedbackRef.current?.(next.feedback);
  }

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(resolveForEditor(initData(block.data), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data)) => {
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(resolveForEditor(from, id));
  };

  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = () => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump();
  };
  const questionHtml = isEditing ? editedQuestionHtml : restored.data.question;
  const northReference = isEditing ? editedNorthReference : restored.data.northReference;
  /**
   * True north is the Extra Block Setting here, and `magnetic` is the default a novice keeps.
   * The two readings differ by the local declination and only one of them needs a GPS fix — a
   * distinction a teacher meeting a compass for the first time has no way to make, and the safe
   * answer is already chosen for them. Choosing `true` is also what arms `poor_position_accuracy`.
   */
  const showNorthReference = useExtraBlockSetting(
    northReference === 'true' || initial.current.northReference === 'true');
  const visibilityMode = isEditing ? editedVisibilityMode : restored.data.visibilityMode;
  const liveDisplayFormat = isEditing ? editedLiveDisplayFormat : restored.data.liveDisplayFormat;
  const conditionType = isEditing ? editedConditionType : restored.data.conditionType;
  const answers = isEditing ? editedAnswers : restored.data.answers;
  const toleranceSymmetry = isEditing ? editedToleranceSymmetry : restored.data.toleranceSymmetry;
  const showTolerance = isEditing ? editedShowTolerance : restored.data.showTolerance;
  const betweenAnswers = isEditing ? editedBetweenAnswers : restored.data.betweenAnswers;
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const bypassEnabled = isEditing ? editedBypassEnabled : restored.data.bypassEnabled;

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /* One version per row of the driver's answer space. Nothing about this block is
     named here: `addCompassVersion` already knows its answer key, which is what keeps this
     to one line in twelve places. See `useDrivenVersionProjection`. */
  useDrivenVersionProjection({ block, data: initData(block.data), emit, addVersion: addCompassVersion });

  /** Every formula field in this block, wherever it sits — see `AngleModeProvider`. The merge
   *  lives in the hook so no block can delete its siblings' modes by writing only its own, and
   *  the spread is `block.data` rather than the editor's view, which is `initData`'s output and
   *  omits whatever a block threads separately — numerical's feedback, famously. */
  const angleModeProps = useBlockAngleModes(
    block.data,
    patch => emit({ ...block.data, ...patch }),
  );

  /** Every field write, routed into the version it belongs to — see mcq-block.tsx's note. */
  const [pendingLink, setPendingLink] = useState<string | null>(null);

  /** Link and unlink — Numerical's, with this block's key set. Every comment there applies. */
  function linkToLeader(followsBlockId: string | undefined, remap?: 'remap' | 'empty') {
    const current = initData(block.data);
    if (followsBlockId && !remap && (current.versions?.length ?? 0) > 1) {
      setPendingLink(followsBlockId);
      return;
    }
    if (!followsBlockId) {
      emit(versionStructure(block.data, {
        ...current, followsBlockId: undefined, scenarioBranches: undefined,
      } as unknown as Record<string, unknown>));
      return;
    }
    const leaderBank = ((blocks ?? []).find(b => b.id === followsBlockId)?.data as
      { versions?: ScenarioLeaderVersion[] } | undefined)?.versions;
    /* Collapse the bank to VERSION 1 first — the block's own top-level copy is whatever
       version 1 said when the bank was made, which is not the same thing. */
    const v1 = current.versions?.[0];
    const base = (current.versions?.length ?? 0) > 1 && v1 ? applyCompassVersion(current, v1.id) : current;
    const baseFeedback = ((current.versions?.length ?? 0) > 1
      ? (v1?.feedback as { states: FeedbackState[] } | undefined) : undefined) ?? feedback;
    const fromBlock = () => ({
      feedback: baseFeedback,
      ...Object.fromEntries(COMPASS_VERSION_KEYS.map(k => [k, (base as unknown as Record<string, unknown>)[k]])),
    } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>);
    const { branches } = remap === 'remap'
      ? remapVersionsToScenarios<FieldVersion<{ states: FeedbackState[] }>, ScenarioFieldBranch<{ states: FeedbackState[] }>>(
        leaderBank, current.versions,
        v => ({
          feedback: v.feedback,
          ...Object.fromEntries(COMPASS_VERSION_KEYS.filter(k => k in v).map(k => [k, (v as unknown as Record<string, unknown>)[k]])),
        } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>),
        fromBlock,
      )
      : syncScenarioBranches<ScenarioFieldBranch<{ states: FeedbackState[] }>>(
        leaderBank, current.scenarioBranches, fromBlock,
      );
    const healed = branches.map((b): ScenarioFieldBranch<{ states: FeedbackState[] }> =>
      (b.feedback?.states?.length ? b : { ...b, feedback: baseFeedback }));
    seedEditorFrom(base);
    resyncFeedbackRef.current?.(baseFeedback);
    emit(versionStructure(block.data, {
      ...base, feedback: baseFeedback, followsBlockId, scenarioBranches: healed, versions: undefined,
    } as unknown as Record<string, unknown>));
  }

  /** The projection, kept live — a variant added to the leader becomes a row here (§4.1). */
  useScenarioRowProjection<ScenarioFieldBranch<{ states: FeedbackState[] }>>({
    followsBlockId: storedForLink.followsBlockId,
    blocks,
    rows: storedForLink.scenarioBranches,
    template: () => ({
      feedback,
      ...Object.fromEntries(COMPASS_VERSION_KEYS.map(k => [k, (fields as unknown as Record<string, unknown>)[k]])),
    } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>),
    onSync: (next) => {
      const current = initData(block.data);
      emit(versionStructure(block.data, {
        ...current, scenarioBranches: next,
      } as unknown as Record<string, unknown>));
    },
  });

  const remapModal = pendingLink ? (
    <ScenarioRemapModal
      versionCount={initData(block.data).versions?.length ?? 0}
      variantCount={((blocks ?? []).find(b => b.id === pendingLink)?.data as
        { versions?: unknown[] } | undefined)?.versions?.length ?? 0}
      leaderName={(blocks ?? []).find(b => b.id === pendingLink)?.name ?? ''}
      onCancel={() => setPendingLink(null)}
      onRemap={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'remap'); }}
      onLinkEmpty={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'empty'); }}
    />
  ) : null;

  const linkMenu = (
    <ScenarioLinkMenu
      block={block}
      blocks={blocks}
      value={storedForLink.followsBlockId}
      onChange={linkToLeader}
      emit={emit}
      applyVersion={applyCompassVersion}
    />
  );

  /** The row on screen — graded-only, like every answer key, read off the LEADER's pointer. */
  const activeScenario = gradingMode === 'graded' && storedForLink.scenarioBranches?.length
    ? (storedForLink.scenarioBranches.find(b => b.scenarioId === shownScenarioId)
      ?? storedForLink.scenarioBranches[0])
    : undefined;
  const activeScenarioVersion = activeScenario
    ? scenarioInForce(leaderVersions, activeScenario.scenarioId) as { name?: string } | null : null;
  const scenarioPager = activeScenario ? (
    <VersionSelector
      versions={(leaderVersions ?? []) as { id: string }[]}
      activeId={activeScenario.scenarioId}
      onSelect={setLeaderShown}
      noun='scenario'
    />
  ) : null;
  /**
   * The ghosted stem for the TEST surface, read off the shared pointer rather than the editor's.
   *
   * Deliberately NOT `activeScenarioVersion`, which is gated on graded mode because it names
   * the row whose ANSWER KEY is being authored. The prose is a different question: a
   * participation block still asks about the scenario, and a teacher testing one still has to
   * be able to read it.
   */
  /* Never in Preview — see `useScenarioFollower`'s own note on why this is not the call
     site's decision to make. */
  const ghostedScenario = isPreview ? null : (
    <ScenarioStimulus
      leader={(blocks ?? []).find(b => b.id === storedForLink.followsBlockId)}
      version={scenarioInForce(leaderVersions, shownScenarioId) as Record<string, unknown> | null}
      ghosted
    />
  );
  const dismissUnreviewed = () => {
    const current = initData(block.data);
    emit(versionStructure(block.data, {
      ...current,
      scenarioBranches: markScenarioReviewed(current.scenarioBranches ?? [], activeScenario!.scenarioId),
    } as unknown as Record<string, unknown>));
  };

  const emitFields = (next: Partial<CompassBlockEditorData>) => {
    const base = initData(block.data);
    /* A SCENARIO ROW takes the write before a version can, and the two cannot both apply
       (§2.2) — the same bargain `writeCompassVersion` makes, one selector over. See Numerical's
       own note for why the feedback goes into the row as well. */
    if (base.scenarioBranches?.length) {
      const merged = { ...base, ...next } as CompassBlockEditorData;
      const written = writeScenarioRow(
        base as unknown as Record<string, unknown>, shownScenarioId,
        merged as unknown as Record<string, unknown>, COMPASS_VERSION_KEYS,
      ) as Record<string, unknown>;
      if ('feedback' in next && shownScenarioId) {
        written.scenarioBranches = (written.scenarioBranches as ScenarioFieldBranch<{ states: FeedbackState[] }>[])
          .map(r => (r.scenarioId === shownScenarioId
            ? { ...r, feedback: next.feedback as { states: FeedbackState[] }, unreviewed: undefined } : r));
      }
      emit(written);
      return;
    }
    emit(writeCompassVersion(base, editorVersion, { ...base, ...next } as CompassBlockEditorData) as unknown as Record<string, unknown>);
  };

  const { settleLive, settleStored } = useIssueSettling(
    { answers: answerPositionsKey({ conditionType, answers, betweenAnswers }) },
    { answers: answerPositionsKey(initData(block.data)) },
  );

  const fields: CompassBlockEditorData = {
    question: questionHtml, northReference, visibilityMode, liveDisplayFormat,
    conditionType, answers, toleranceSymmetry, showTolerance, betweenAnswers,
    gradingMode, bypassEnabled,
    feedback: { states: [] }, // placeholder — real feedback pushed via currentDataRef below
  };
  const currentDataRef = useRef(fields);
  currentDataRef.current = fields;

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle = positiveSlotTitleFor(gradingMode, t);

  const {
    feedback, setFeedback, resyncFeedback, updateFeedbackState, deleteFeedbackState, getTitle,
  } = useInputBlockFeedback({
    initialRaw: initial.current.feedback,
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      // Sensor blocks say "measurement", not "question": a student who skips one of these hasn't
      // failed to answer, they've failed to TAKE A READING, and the feedback should name the thing
      // they actually have to do. Shared across GPS/Compass/Angle so the three can't drift apart.
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noMeasurementContent')}</p>`,
      incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
      extra: {
        [NO_SENSOR_DATA_TRIGGER]: t('blocks.sensors.noSensorDataFeedback'),
        poor_heading_accuracy: t('blocks.compass.poorHeadingAccuracyFeedback'),
        poor_position_accuracy: t('blocks.compass.poorPositionAccuracyFeedback'),
      },
    },
    extraDefaults: compassExtraDefaults(t),
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      emitFields({ ...currentDataRef.current, feedback: nextFeedback });
    },
  });

  resyncFeedbackRef.current = resyncFeedback;

  function push(overrides: Partial<CompassBlockEditorData> = {}) {
    emitFields({ ...currentDataRef.current, feedback, ...overrides });
  }

  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const { activeEditorRef } = sharedToolbar;
  const questionEditorRef = useRef<Editor | null>(null);

  // Every plain <input> in this block focuses through here — see onPlainFieldFocus's own doc
  // comment for why leaving the shared toolbar pointed at the Question editor is a real bug.
  const disconnectSharedToolbar = sharedToolbar.disconnect;

  function handleGradingMode(m: GradingMode) {
    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, m, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });

    setGradingMode(m);
    push({ gradingMode: m, feedback: { states: nextFeedbackStates } });
  }

  function handleBypassToggle() {
    const next = !bypassEnabled;
    setBypassEnabled(next);
    push({ bypassEnabled: next });
  }

  function setNorthReferenceAndPush(next: CompassNorthReference) {
    setNorthReference(next);
    push({ northReference: next });
  }
  function setVisibilityModeAndPush(next: CompassVisibilityMode) {
    setVisibilityMode(next);
    push({ visibilityMode: next });
    // A reading staged under the old mode's rules can't survive the switch: the "Take Reading was
    // pressed" flag means nothing in a mode with no button, and leaving it set would show a reading
    // in Live Reading the teacher never took. Resetting puts the test panel back to its pre-reading
    // state, which is what a mode change genuinely is.
    setTestTaken(false);
    setHasSubmitted(false);
  }
  function setLiveDisplayFormatAndPush(next: CompassLiveDisplayFormat) {
    setLiveDisplayFormat(next);
    push({ liveDisplayFormat: next });
  }

  function setConditionTypeAndPush(next: CompassConditionType) {
    setConditionType(next);
    push({ conditionType: next });
  }
  /**
   * Flip ONE answer between a single tolerance and a pair, carrying its numbers across.
   *
   * Switching used to wipe the other representation — go asymmetric and the `±2` you typed was
   * gone, leaving two empty boxes that grade as an exact match. Now that the toggle is per
   * answer this is the one place it happens.
   */
  function toggleAnswerSymmetry(id: string) {
    const next = answers.map(a => {
      if (a.id !== id) return a;
      return answerSymmetry(a, restored.data) === 'symmetric'
        ? { ...a, toleranceSymmetry: 'asymmetric' as const, toleranceBelow: a.toleranceValue, toleranceAbove: a.toleranceValue }
        : { ...a, toleranceSymmetry: 'symmetric' as const, toleranceValue: a.toleranceAbove || a.toleranceBelow };
    });
    setAnswers(next);
    push({ answers: next });
  }
  function updateAnswer(id: string, patch: Partial<ExactAnswer>) {
    const next = answers.map(a => a.id === id ? { ...a, ...patch } : a);
    setAnswers(next);
    push({ answers: next });
  }
  function addAnswer() {
    const next = [...answers, makeExactAnswer()];
    setAnswers(next);
    push({ answers: next });
  }
  function removeAnswer(id: string) {
    if (answers.length <= 1) return;
    const next = answers.filter(a => a.id !== id);
    setAnswers(next);
    push({ answers: next });
  }
  function updateBetweenAnswer(id: string, patch: Partial<BetweenAnswer>) {
    const next = betweenAnswers.map(a => a.id === id ? { ...a, ...patch } : a);
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }
  function addBetweenAnswer() {
    const next = [...betweenAnswers, makeBetweenAnswer()];
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }
  function removeBetweenAnswer(id: string) {
    if (betweenAnswers.length <= 1) return;
    const next = betweenAnswers.filter(a => a.id !== id);
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }
  // One-shot DeviceOrientationEvent read — push-based, unlike Geolocation's callback-based
  // getCurrentPosition, so this wraps a single event in a Promise with listener cleanup + a
  // manual timeout, the closest equivalent to GPS's one-shot semantics.
  function readOneHeadingEvent(timeoutMs: number): Promise<{ headingDegrees: number; headingAccuracy: number } | null> {
    return new Promise(resolve => {
      let settled = false;
      const done = (value: { headingDegrees: number; headingAccuracy: number } | null) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('deviceorientationabsolute', onEvent as EventListener);
        window.removeEventListener('deviceorientation', onEvent as EventListener);
        clearTimeout(timer);
        resolve(value);
      };
      const onEvent = (e: DeviceOrientationEvent & { webkitCompassHeading?: number; webkitCompassAccuracy?: number }) => {
        if (typeof e.webkitCompassHeading === 'number') {
          // iOS Safari: already magnetic-north-referenced, 0-360, negative accuracy = invalid.
          done({ headingDegrees: e.webkitCompassHeading, headingAccuracy: e.webkitCompassAccuracy ?? -1 });
        } else if (e.absolute && e.alpha != null) {
          // Android Chrome: no native accuracy figure — degrade gracefully via the -1 sentinel,
          // same "unknown, never gates" convention compass.ts's evaluateCompass expects.
          done({ headingDegrees: (360 - e.alpha) % 360, headingAccuracy: -1 });
        }
      };
      window.addEventListener('deviceorientationabsolute', onEvent as EventListener);
      window.addEventListener('deviceorientation', onEvent as EventListener);
      const timer = setTimeout(() => done(null), timeoutMs);
    });
  }

  async function handleCaptureHeading() {
    setIsCapturing(true);
    setCaptureError(null);
    try {
      const OrientationCtor = window.DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<'granted' | 'denied'> };
      if (typeof OrientationCtor?.requestPermission === 'function') {
        const result = await OrientationCtor.requestPermission();
        if (result !== 'granted') { setCaptureError(t('blocks.compass.captureErrorPermissionDenied')); setIsCapturing(false); return; }
      }

      let position: GeolocationPosition | null = null;
      if (northReference === 'true') {
        position = await new Promise<GeolocationPosition>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 }));
      }

      const reading = await readOneHeadingEvent(10000);
      if (!reading) { setCaptureError(t('blocks.compass.captureErrorUnsupported')); setIsCapturing(false); return; }

      const resolvedHeading = northReference === 'true' && position
        ? computeTrueHeading(reading.headingDegrees, position.coords.latitude, position.coords.longitude)
        : reading.headingDegrees;

      setPendingCapture({ headingDegrees: resolvedHeading, headingAccuracy: reading.headingAccuracy });
    } catch (err) {
      const geoErr = err as GeolocationPositionError;
      const key = geoErr?.code === 1 ? 'captureErrorPermissionDenied'
        : geoErr?.code === 2 ? 'captureErrorPositionUnavailable'
        : geoErr?.code === 3 ? 'captureErrorTimeout'
        : 'captureErrorGeneric';
      setCaptureError(t(`blocks.compass.${key}`));
    } finally {
      setIsCapturing(false);
    }
  }

  // Writes into whichever answer row is currently LAST in the list — deliberate, not staged
  // per-row: the field workflow is add-row -> capture -> add-row -> capture, building up a
  // multi-target list one physical location at a time.
  function handleUsePendingCapture() {
    if (!pendingCapture) return;
    const lastId = answers[answers.length - 1]!.id;
    updateAnswer(lastId, { value: String(pendingCapture.headingDegrees) });
    setPendingCapture(null);
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = feedback.states.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...defaults.filter(s => s.trigger === 'poor_heading_accuracy'),
      ...defaults.filter(s => s.trigger === 'poor_position_accuracy'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /**
   * A new targeted state starts BLANK.
   *
   * It used to invent one: `lt:10`, then `lt:20`, then `lt:30`, counting up to dodge a collision
   * with a trigger already in the list. Every one of those is a real, gradeable condition the
   * teacher never authored — it fires on real student answers, shows up in Flow, and counts toward
   * coverage, all while reading as a suggestion. Numerical has seeded `exact:` (empty) all along;
   * this is the other two catching up.
   *
   * The dedupe loop went with it. It existed only to make the invented numbers distinct, and blank
   * triggers do not collide in any way that matters — states are keyed by `id`, and two unfinished
   * conditions are two unfinished conditions.
   */
  /** The card the Add button last produced, so it opens ready to type into — the owner's
   *  preference for this family, and the reason `startExpanded` exists. Keyed by mode, because
   *  the panel reorders its sections on a switch and the cards remount; a bare id would reopen
   *  the last one every time. State, not a ref: it is read during render. */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedState() {
    const trigger = 'exact:';
    const targeteds = feedback.states.filter(s => s.type === 'targeted');
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: compassTargetedTitle(trigger, t),
      trigger, content: '',
      evaluation: currentDataRef.current.gradingMode === 'graded' ? 'incorrect' : 'neutral',
    };
    const next = { states: rebuildStates([...targeteds, newState]) };
    setFeedback(next);
    emitFields({ ...currentDataRef.current, feedback: next });
  }

  function handleFeedbackTargetedDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const targeteds = feedback.states.filter(s => s.type === 'targeted');
    const from = targeteds.findIndex(s => s.id === String(active.id));
    const to = targeteds.findIndex(s => s.id === String(over.id));
    if (from === -1 || to === -1) return;
    const next = { states: rebuildStates(arrayMove(targeteds, from, to)) };
    setFeedback(next);
    emitFields({ ...currentDataRef.current, feedback: next });
  }

  function handleDone() {
    push();
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Grading mode metadata ─────────────────────────────────────────────────

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  // Plain-text form of the same data, for space-constrained spots (the collapsed block's own
  // single-line answer bar) that can't fit a full chip + arc diagram.
  /**
   * The accepted answer, in words.
   *
   * `scenario` is the whole two-style rule in one parameter. A DISPLAY surface — the canvas card,
   * the editor at rest — passes null and sees the equation as unrolled chips, because the answer
   * genuinely is "{n}*30", not any one roll of it. The moment a teacher picks Minimum/Maximum/Random
   * the question changes to "what does it accept THIS time", and the honest answer is a band of
   * degrees. Both readings are true; which is showing is the scenario, exactly as numerical's bar
   * decides it.
   *
   * This took a bug report to find because the formula branch below returned early and never
   * consulted a scenario at all, so the bar sat on chips through every roll while the feedback
   * panel beside it resolved — one fact, two derivations, for the seventh time this week.
   */
  // A single representative heading to show in the preview/collapsed mockup's "live reading"
  // placeholder, standing in for what a student would actually see once the block is answered
  // correctly — rather than a generic dash that tells a teacher nothing about what they just
  // configured. Only the FIRST OR'd answer is shown (a live reading is one number, not a list).
  // For 'exact', this must be the raw authored value itself, NOT a midpoint derived from
  // correctRangePairs's tolerance-widened lo/hi — an asymmetric answer of "0, -30/+10" has an
  // accepted band of [-30, 10] whose midpoint is -10 (350 deg), which is a real but different
  // bug: the display would show the wrong heading even though the *answer itself* (0) was typed
  // correctly. 'between' has no single authored value, so its range's own wraparound-aware
  // midpoint is the only sensible representative point there. Null when nothing is configured
  // yet — the mockup falls back to the dash placeholder in that case, same as before.
  function representativeCorrectValue(d: CompassBlockEditorData): number | null {
    if (isCompassSetupIncomplete(d)) return null;
    if (d.conditionType === 'exact') {
      const a = d.answers.find(a => a.value.trim() !== '');
      return a ? num(a.value) : null;
    }
    const a = d.betweenAnswers.find(a => a.low.trim() !== '' && a.high.trim() !== '');
    if (!a) return null;
    const lo = num(a.low), hi = num(a.high);
    const span = ((hi - lo) % 360 + 360) % 360;
    return normalizeDegreesForDisplay(lo + span / 2);
  }

  // ── Live test handlers ──────────────────────────────────────────────────────
  // "Take Reading" is the real student control; it just receives a synthetic reading instead of a
  // sensor one. In continuous mode a reading evaluates immediately, matching every other block's
  // continuous behaviour; otherwise it stages the reading and Submit runs the evaluation.
  function handleTakeReading() {
    setTestTaken(true);
    // Frozen at the moment of capture, so the committed heading stops moving — the whole meaning of
    // the full-strength readout it switches on.
    setCapturedReading(liveReadingFor(currentDataRef.current));
    // Capturing supersedes whatever the revisit put back, or the button would look broken.
    setRestoredReading(null);
    // `continuous` is a TEST-PANEL affordance, so it must not apply in Preview: Preview has no
    // TestBar to switch it off, and a student pressing capture is not submitting — they'd see the
    // answer before the page-level Submit. In Preview only previewSubmitSignal reveals feedback.
    if (continuous && !isPreview) setHasSubmitted(true);
  }
  /**
   * Reset: throw away the committed heading and let the instrument stream again.
   *
   * Clears the ANSWER, not just the display — see SensorCaptureButton. `restoredReading` goes with
   * it, or a revisit's heading would reappear the instant the capture it replaced was discarded.
   * `hasSubmitted` is deliberately left alone: a reset is not an un-submission.
   */
  function handleClearReading() {
    setTestTaken(false);
    setCapturedReading(null);
    setRestoredReading(null);
  }

  /** Arming Device: on iOS the orientation stream needs an explicit permission grant AND that grant has
   * to originate in a user gesture, which is exactly what clicking this option is — so the request
   * lives here rather than in the effect that opens the stream. */

  function handleSourceModeChange(next: SensorSourceMode) {
    if (next === 'device') { void armDeviceReading(); return; }
    setSourceMode('simulate');
  }

  async function armDeviceReading() {
    setDeviceError(null);
    const OrientationCtor = window.DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<'granted' | 'denied'> };
    if (typeof OrientationCtor?.requestPermission === 'function') {
      try {
        if (await OrientationCtor.requestPermission() !== 'granted') {
          setDeviceError(t('blocks.compass.captureErrorPermissionDenied'));
          return;
        }
      } catch {
        setDeviceError(t('blocks.compass.captureErrorPermissionDenied'));
        return;
      }
    }
    setSourceMode('device');
  }

  /** Orthogonal to `testSelection`: switching to the device and back leaves the armed fault armed. */
  const [sourceMode, setSourceMode] = useState<SensorSourceMode>('simulate');
  // Non-null only in Preview; the editor keeps its own per-block simulator untouched.
  const shared = useSensorSimulator();
  /**
   * The simulator is reporting no signal at all — the magnetometer produced nothing.
   *
   * Distinct from a null reading, which is also what a student who has not captured yet has. That
   * one is an ordinary No Answer; this one is the hardware, and the student may have had no way
   * round it.
   */
  const sensorDead = !!shared && !shared.useDevice && shared.signal === 'none';
  /** A DEVICE-mode failure, kept apart from `captureError`, which belongs to the teacher's
   * answer-setup capture and renders beside that panel's own button. Same split, same reason, as
   * `deviceUnavailable`. */
  const [deviceError, setDeviceError] = useState<string | null>(null);
  /**
   * Whether the REAL sensor is being read, which in Preview the shared device decides.
   *
   * This was `sourceMode === 'device'` alone, and that made the consolidated panel's
   * Device switch do nothing: it flipped the shared flag while every block went on reading
   * its own `sourceMode`, which defaults to Simulate. The switch looked wired and was not.
   *
   * The block's own `sourceMode` still governs in the EDITOR, where there is no provider
   * above it and its per-block strip is the thing being operated.
   */
  const deviceArmed = shared ? shared.useDevice : sourceMode === 'device';
  /**
   * What the magnetometer is doing, for the hint line in device mode.
   *
   * `deviceUnavailable` is the honest answer on a desktop: `'DeviceOrientationEvent' in window` is
   * true there, so the only reliable signal is that no usable heading ever arrives.
   */
  const deviceStatusHint = deviceError
    ?? (deviceUnavailable ? t('blocks.compass.captureErrorUnsupported')
      : deviceReading ? '' : t('blocks.compass.simulateDeviceWaiting'));
  /** Only real failures reach the student surface — "waiting for a reading" is a status. */
  const studentDeviceNotice = !deviceArmed ? null
    : deviceError ?? (deviceUnavailable ? t('blocks.compass.captureErrorUnsupported') : null);

  useEffect(() => {
    if (!deviceArmed) { setDeviceReading(null); setDeviceUnavailable(false); return; }
    if (!ORIENTATION_SUPPORTED) { setDeviceUnavailable(true); return; }
    setDeviceUnavailable(false);
    // Whether a USABLE heading has arrived — not merely whether any event has. A desktop browser
    // fires deviceorientation events perfectly happily with `alpha: null` (measured: 2 events in 4s,
    // absolute:true, alpha:null), so counting events would call a machine with no magnetometer
    // "working". Only the two branches below, which actually produce a heading, count.
    let gotReading = false;
    const onEvent = (e: DeviceOrientationEvent & { webkitCompassHeading?: number }) => {
      if (typeof e.webkitCompassHeading === 'number') {
        gotReading = true;
        setDeviceUnavailable(false);
        setDeviceReading({ headingDegrees: e.webkitCompassHeading, headingAccuracy: (e as { webkitCompassAccuracy?: number }).webkitCompassAccuracy ?? -1 });
      } else if (e.absolute && e.alpha != null) {
        gotReading = true;
        setDeviceUnavailable(false);
        setDeviceReading({ headingDegrees: (360 - e.alpha) % 360, headingAccuracy: -1 });
      }
    };
    window.addEventListener('deviceorientationabsolute', onEvent as EventListener);
    window.addEventListener('deviceorientation', onEvent as EventListener);
    // `'DeviceOrientationEvent' in window` is TRUE on a desktop browser with no magnetometer, so the
    // support check above cannot detect missing hardware — the only reliable signal is that no usable
    // reading ever arrives. Without this the strip sat on "Waiting for a reading from this device"
    // forever, which reads as broken code rather than as a device that cannot do this. The one-shot
    // answer-setup capture already had a timeout for the same reason; the live watch had none.
    const timer = setTimeout(() => {
      if (!gotReading) setDeviceUnavailable(true);
    }, DEVICE_HEADING_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('deviceorientationabsolute', onEvent as EventListener);
      window.removeEventListener('deviceorientation', onEvent as EventListener);
    };
  }, [deviceArmed]);

  function handleTestSelectionChange(next: CompassSimSelection) {
    // Choosing "Normal Reading" while the box holds a usable heading arms THAT, rather than dropping
    // to no-reading while a perfectly good value sits on screen.
    if (next.kind === 'none' && simManualValid) {
      setTestSelection({ kind: 'manual', headingDegrees: simManualDegrees });
      if (!continuous) setHasSubmitted(false);
      return;
    }
    setTestSelection(next);
    if (next.kind === 'scenario') setSimNonce(n => n + 1);
    // A new scenario is a new answer — re-evaluate at once when continuous, otherwise fall back
    // to "staged but not yet submitted" so the legend can't show a stale fired chip.
    if (!continuous) setHasSubmitted(false);
  }
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    // Only Capture needs the flag; evaluatedFor() derives the other two from `continuous` directly,
    // and setting it here would leave their feedback stuck on after continuous is switched back off.
    if (next && !isPreview && visibilityMode === 'capture' && testTaken) setHasSubmitted(true);
  }

  /** Manual heading for the simulate strip. A plain degree number, unlike GPS's coordinate pair — so
   * the parser is just "is this a finite number", with normalization deferred to compassSimReading so
   * 370 is accepted here and displayed as 10 there. Applied as typed: with a single numeric box there
   * is nothing to parse out of a longer string, so there is no moment where the teacher has to tell us
   * they've finished, and no apply button. */
  const simManualDegrees = parseFloat(simManualInput.replace(',', '.'));
  const simManualValid = simManualInput.trim() !== '' && Number.isFinite(simManualDegrees);
  const simManualInvalid = simManualInput.trim() !== '' && !simManualValid;
  function handleSimManualInput(v: string) {
    setSimManualInput(v);
    const deg = parseFloat(v.replace(',', '.'));
    if (v.trim() !== '' && Number.isFinite(deg)) handleTestSelectionChange({ kind: 'manual', headingDegrees: deg });
  }

  /** The reading the armed selection produces, gated by whether this visibility mode allows one to
   * exist yet. One helper for all three mount points (Preview, canvas card, phone Test tab) so they
   * cannot disagree about when a reading exists. */
  /** What the armed selection WOULD report, with no visibility-mode gate. This is what the Simulate
   * hint describes: the hint is a PREDICTION ("Take Reading will report 90°"), so withholding it
   * until a capture had happened both contradicted its own wording and made the strip grow a line
   * the moment the button was pressed, shoving the widget below it down the page. */
  function simReadingFor(d: CompassBlockEditorData): CompassSimReading | null {
    if (deviceArmed) {
      // Position accuracy is only consulted in True North mode; a device heading carries no position
      // of its own here, so it reports the healthy sentinel and never trips that gate spuriously.
      return deviceReading ? { ...deviceReading, positionAccuracy: COMPASS_GOOD_POSITION_ACCURACY } : null;
    }
    return compassSimReading(testSelection, d, simNonce, simManualValid ? simManualDegrees : null);
  }
  /**
   * The reading the STUDENT surface may show, which Capture withholds until its button is pressed.
   *
   * In PREVIEW this comes from the task's one shared device — see `sensor-simulator-context`. The
   * position it carries is not incidental here: `computeTrueHeading` needs a latitude and longitude
   * to work out magnetic declination, so a True North block consumes a position whether or not the
   * task contains a GPS block at all. That is why the shared device holds both.
   */
  function readingFor(d: CompassBlockEditorData): CompassSimReading | null {
    // A revisit shows what was captured, EXCEPT in Live — see gps-block's own note. Live tracks
    // the device, so putting an old heading back would be a lie about where they are pointing.
    if (d.visibilityMode !== 'live' && restoredReading) return restoredReading;
    if (!sensorHasReading(d.visibilityMode, testTaken)) return null;
    /*
     * PREVIEW reads the task's one shared device; the EDITOR reads this block's own strip.
     *
     * This used to be `sharedReadingFor(d) ?? simReadingFor(d)` unconditionally, on the stated
     * assumption that no provider sits above the editor. One does — `SensorSimulatorProvider`
     * wraps the whole task page, not just the preview surface — so the shared device answered
     * first everywhere, and since its heading starts at zero, every per-block box was dead: the
     * hint said "will report 212" off the typed value while the readout beside it stayed on 0 and
     * the legend graded against 0.
     *
     * Which strip is ON SCREEN is the thing that decides, and in the editor that is this block's.
     */
    return capturedReading ?? liveReadingFor(d);
  }

  /**
   * The instrument as it stands, with no capture gate in front of it.
   *
   * See `readingFor`'s own note for why the editor and Preview read different strips.
   */
  function liveReadingFor(d: CompassBlockEditorData): CompassSimReading | null {
    return (isPreview ? sharedReadingFor(d) : null) ?? simReadingFor(d);
  }

  /**
   * The sample Capture froze, in the mode that has a Capture to press.
   *
   * Compass used to keep none: `testTaken` only decided WHETHER to show a heading, and the value
   * shown was still the live one, so a captured reading went on drifting under the student's eyes
   * and the word "captured" described nothing. Invisible in the editor, where the heading only
   * moves when the teacher drags the dial — and constant on a real phone, which is where it
   * matters. Live Reading keeps no snapshot by definition, and Capture on Submit takes its sample
   * at submission.
   */
  const [capturedReading, setCapturedReading] = useState<CompassSimReading | null>(null);

  /**
   * The reading as it stood at Submit, frozen.
   *
   * Compass keeps no submitted snapshot of its own — its result recomputes off the live reading
   * whenever it changes — so without this there is nothing to record.
   */
  const [submittedReading, setSubmittedReading] = useState<CompassSimReading | null>(null);
  useEffect(() => {
    if (isPreview && previewSubmitSignal) setSubmittedReading(readingFor(currentDataRef.current));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);
  useReportAnswer(hasSubmitted ? submittedReading : undefined);

  /** This block's view of the shared device, or `null` outside Preview and on the device tab. */
  function sharedReadingFor(d: CompassBlockEditorData): CompassSimReading | null {
    if (!shared || shared.useDevice) return null;
    if (shared.signal === 'none') return null;
    const accuracy = accuracyForSignal(shared.signal);
    const { lat, lng } = shared.position;
    return {
      // The correction is applied HERE rather than left to the caller, for the reason
      // `CompassAnswer` states: whatever reaches `evaluateCompass` must already be expressed in the
      // block's own north reference. A magnetic block reads the raw heading.
      headingDegrees: d.northReference === 'true'
        ? computeTrueHeading(shared.heading.degrees, lat, lng)
        : shared.heading.degrees,
      headingAccuracy: accuracy.heading,
      positionAccuracy: accuracy.position,
    };
  }
  /**
   * Whether the evaluation should be showing. `hasSubmitted` is the event-driven flag — Submit was
   * pressed, or continuous mode auto-submitted when a reading was taken. Capture is the only mode that
   * has a gesture to hang that on, so in continuous mode the other two evaluate immediately: Live
   * Reading is always reading, and Capture on Submit reads automatically. Without this both showed no
   * feedback whatever the teacher did, since neither has a control that could set the flag, and the
   * test bar only offers a Submit button while continuous is switched off.
   *
   * "Always reading" is not the same as HAS read, which is the `reading` argument. A live block that
   * has produced nothing yet has nothing to evaluate, and firing the catch-all on that emptiness put
   * "Your response has been recorded" on screen before the teacher had done a thing — true in the
   * sense that optional treats a blank as a response, and a plain lie as a sentence. A dead sensor
   * still evaluates with no reading: that IS the outcome, and the no-data card is what says so.
   */
  function evaluatedFor(d: CompassBlockEditorData, reading: unknown): boolean {
    if (hasSubmitted) return true;
    if (!continuous || isPreview || d.visibilityMode === 'capture') return false;
    /*
     * LIVE READING: no authored answer, nothing fires.
     *
     * A live instrument is always reading, so a reading exists the instant the block does — the
     * simulator hands every new block a default one — and the card fired its feedback before the
     * teacher had touched anything. The honest gate is not "did somebody ask for this reading" but
     * "is there anything to test it against": a block with no answer authored has nothing for the
     * test surface to say, in any mode.
     *
     * Capture is unaffected, because it already needs a press, and Capture on Submit returns above
     * on `hasSubmitted`. So this lands on Live Reading, which is the only mode that evaluates
     * unprompted — and now the only default both sensors share.
     *
     * OPTIONAL is exempt, because there the premise does not hold: that mode authors no answer
     * by design, so "no answer yet" is permanent rather than a stage the block passes through,
     * and the rule would silence it for good. An optional sensor has nothing to set up and
     * nothing to wait for, so it reads continuously and says so.
     */
    if (d.gradingMode !== 'optional' && isCompassSetupIncomplete(d)) return false;
    return reading != null || sensorDead;
  }


  const simManualProps = { value: simManualInput, onChange: handleSimManualInput, invalid: simManualInvalid };

  /** The student's typed bypass answer. Test state, not block data: it is what a STUDENT enters, so
   * it belongs with testTaken/hasSubmitted and is cleared by Reset, never persisted onto the block. */
  const bypassDegrees = parseFloat(bypassInput.replace(',', '.'));
  // Range-checked, NOT wrapped. A sensor reading genuinely wraps — the instrument reports a
  // continuous rotation, and tolerance bands have to wrap through North for 340->30 to be a 50°
  // wedge. A TYPED answer does not: 985 is not a heading a student meant, it is a slip, and running
  // it through `% 360` turned it into 265 and scored a typo as correct. Wrong input, right answer,
  // no signal to the student is the worst outcome available here, so out-of-range is refused.
  const bypassInRange = Number.isFinite(bypassDegrees) && bypassDegrees >= 0 && bypassDegrees <= 360;
  const bypassValid = bypassInput.trim() !== '' && bypassInRange;
  const bypassInvalid = bypassInput.trim() !== '' && !bypassValid;
  /** Typing an answer IS the answering gesture for a student who cannot use the sensor, so it must
   * reveal feedback in continuous test mode exactly as pressing Capture does. Preview is excluded for
   * the same reason the capture handler excludes it: there, only the page-level Submit reveals
   * feedback, and a student typing their answer is not submitting it. */
  function handleBypassChange(v: string) {
    setBypassInput(v);
    if (continuous && !isPreview && v.trim() !== '') setHasSubmitted(true);
  }
  /** The answer a typed bypass represents, or null when nothing usable has been typed. Normalized
   * into the same closed [0, 360) domain every other heading in this block lives in. */
  // 360 is the one value still folded, to 0: it is a conventional way to write North rather than a
  // slip, and 360 and 0 are the same heading. Every other input is taken exactly as typed.
  const bypassAnswer = bypassValid ? (bypassDegrees === 360 ? 0 : bypassDegrees) : null;
  const bypassProps = { value: bypassInput, onChange: handleBypassChange, invalid: bypassInvalid, answer: bypassAnswer };
  function handleTestSubmit() {
    setHasSubmitted(true);
  }
  /** Another draw inside the armed scenario — see sensorSimRandom. */
  /** Another draw inside the armed scenario AND a fresh variable roll — the test panel has one
   * Reroll button and it means "give me a different randomised setup", which covers both. */
  function handleSimReroll() {
    setSimNonce(n => n + 1);
    setRolledValues(rollVariableValues(variables));
  }
  function handleTestReset() {
    setTestTaken(false);
    setHasSubmitted(false);
    // The bypass is the student's answer, so Reset clears it alongside the capture — leaving it
    // behind would carry an answer across a reset the teacher just asked for.
    setBypassInput('');
    setRestoredReading(null);
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);

  // Preview's single page-level Submit — reacts to the signal CHANGING, not a per-block click,
  // reusing the same test state the live test widget already drives.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) setHasSubmitted(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);


  // Formats a heading per the teacher's own liveDisplayFormat choice — the preview/collapsed
  // mockup should show exactly what the student would see, not a format-agnostic generic number.
  //
  // NODES rather than a string, so the cardinal point can be lettered on screen and spoken as a
  // word: see `cardinalSpoken`. Everything a caller does with this renders it directly.
  function formatLiveDisplay(value: number, format: CompassLiveDisplayFormat): React.ReactNode {
    const point = cardinalPointFromDegrees(value);
    const deg = `${fmtLocale(value, i18n.language)}°`;
    const cardinal = (
      <>
        <span aria-hidden='true'>{t(`blocks.compass.${CARDINAL_I18N_KEYS[point]}`)}</span>
        <span className='sr-only'>{cardinalSpoken(point, t)}</span>
      </>
    );
    if (format === 'cardinalOnly') return cardinal;
    if (format !== 'cardinalDegree') return deg;
    // The comma is spoken, not shown: "North, 90 degrees" rather than one run of syllables.
    return <>{cardinal}<SpokenPause /> {deg}</>;
  }

  // ── Preview ───────────────────────────────────────────────────────────────

  if (isPreview) {
    // Genuinely interactive now — same widget and same evaluateCompass path the live test panel
    // uses, triggered by the page-level bottom Submit. Scenario-simulated rather than sensor-read:
    // a teacher previews on the device they're authoring on, which usually has no magnetometer.
    // The version THIS student drew, from the task roll — see `versionRollKey`.
    const d = buildNormalizedCompassData(initData(block.data), t, taskPreviewRoll?.[versionRollKey(block.id)]);
    const previewReading = readingFor(d);
    const previewTest = computeCompassTestData(d, t, i18n.language, previewReading, evaluatedFor(d, previewReading), bypassAnswer, resolveAnswer, rolledValues, sensorDead);
    /* See gps-block: the same derivation asked about the DRAFT, so Current says what would fire
       now rather than going blank until a reading is taken. */
    const draftTest = computeCompassTestData(d, t, i18n.language, previewReading, true, bypassAnswer, resolveAnswer, rolledValues, sensorDead);
    return (
      // No question render here — CompassAnswerDisplay owns it, so Preview cannot drift from the
      // canvas card or the phone Test tab the way it previously had.
      <div className='flex flex-col gap-3'>
        {/* The widget renders in Capture on Submit too, rather than being replaced by the passive
            note: short-circuiting here left a passive block with no simulate strip, which made the
            mode untestable in Preview — the one surface a teacher uses to check it. */}
        <CompassTestWidget
          scenario={ghostedScenario}
          d={d}
          // Preview reads the task's one device, so this block's own strip stays hidden. In
          // the editor there is no provider above us and this is false, which is what keeps
          // per-block testing exactly as it was.
          sharedDevice={!!shared}
          selection={testSelection}
          onSelectionChange={handleTestSelectionChange}
          mode={sourceMode}
          onModeChange={handleSourceModeChange}
          deviceStatusHint={deviceStatusHint}
          deviceNotice={studentDeviceNotice}
          manual={simManualProps}
          reading={previewReading}
          onTakeReading={handleTakeReading}
          onClearReading={handleClearReading}
          liveHeading={liveReadingFor(d)?.headingDegrees ?? null}
          hasReading={previewReading != null}
          t={t}
          locale={i18n.language}
          formatLive={formatLiveDisplay}
          hintReading={simReadingFor(d)}
          bypass={bypassProps}
        >
          {/* See keyword-block: only the block knows which conditions are in force. */}
          <ReportTestChips chips={draftTest.testChips} states={draftTest.testResult} />
          {renderCompassFiredFeedback(previewTest.testResult)}
        </CompassTestWidget>
      </div>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────

  if (!isEditing) {
    // Through the version in force, so the card tests what the pager is pointed at.
    const d = buildNormalizedCompassData(initData(block.data), t, shownVersionId);
    const previewValue = representativeCorrectValue(d);
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const dispChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';
    const collapsedIssues = settleStored(memoizedByKey(compassIssuesCache, block.data, [t, variables, shownVersionId], () => compassIssues(d, issueCtx)));
    // The simulated reading for the armed scenario, and the evaluation it produces. Both read the
    // persisted `d` rather than editor state — this branch renders the canvas card, which is a
    // separate mount from the open editor.
    const collapsedReading = readingFor(d);
    const collapsedTest = computeCompassTestData(d, t, i18n.language, collapsedReading, evaluatedFor(d, collapsedReading), bypassAnswer, resolveAnswer, rolledValues, sensorDead);
    const compassAriaLabel = d.question
      ? t('blocks.compass.editAriaBaseWithQuestion', { question: d.question.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) })
      : t('blocks.compass.editAriaBase');

    return (
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<Compass className='h-5 w-5 text-ws-primary dark:text-ws-text-link' />}
        titleLabel={t('blocks.compass.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={dispChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={compassAriaLabel}
        answerBarSlot={() => compassCollapsedBar(d, (
          // The collapsed card is a DISPLAY surface: roll `null`, so a variable answer shows
          // its equation as chips rather than one roll of it. Same derivation as the editor.
          <AnswerBarText
            chips={compassChips(d, { trigger: 'correct' }, null, t, i18n.language, d.gradingMode,
              (d.feedback?.states ?? []).filter(x => x.type === 'targeted').map(x => x.trigger))}
            noValidAnswerLabel={answerExprTexts(d).length > 0 ? t('blocks.answers.noValidAnswer') : undefined}
          />
        ), t, collapsedIssues)}
        testEvaluation={collapsedTest.testResult ? (collapsedTest.testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: evaluatedFor(d, collapsedReading),
          chips: collapsedTest.testChips,
          onReset: handleTestReset,
          // Reroll redraws the randomised value INSIDE the armed scenario — "another just-inside-
          // tolerance reading", not another scenario. It lives here rather than on the scenario control
          // because a <select> doesn't fire onChange when you re-pick what's already selected, and
          // because this is the same button that rerolls variables in every other block.
          onReroll: handleSimReroll,
          /* No branches here, so this row carries only the version pager and the way into
             editing it — the same component every banked block uses, which renders nothing at
             all for a block with no bank. */
          topNotices: (
            <VersionBranchNotice
              versions={testPager?.versions ?? shownVersions}
              activeVersionId={testPager ? testPager.activeId : shownVersionId}
              onSelectVersion={testPager?.onSelect ?? setTestVersion}
              noun={testPager?.noun ?? 'version'}
              editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={null}
              onEdit={() => {
                editShownVersion();
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          notices: (
            <IssuesPanel issues={collapsedIssues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {/* DISPLAY STATE ONLY. It reserves the space the question will fill and reads as a normal
            authoring placeholder here, but a student must never see placeholder text — so it is gated on
            !isSelected, because a selected card IS the live test surface. The blank-question warning
            itself lives on the issue bar; this is just the reserved slot. */}
        {!isSelected && !d.question && (
          <EmptyQuestionNote />
        )}
        {isSelected ? (
          // Selected card doubles as the live test surface (the canvas-card convention every
          // audited block follows) — the real widget, driven by a simulated sensor reading.
          // Block-test roll only. Preview is deliberately NOT wrapped: it inherits the
          // task-level provider so every block on the page resolves against one shared roll.
          <BlockPreviewProvider value={rolledValues}>
          <CompassTestWidget
          scenario={ghostedScenario}
            d={d}
            selection={testSelection}
            onSelectionChange={handleTestSelectionChange}
            mode={sourceMode}
            onModeChange={handleSourceModeChange}
            deviceStatusHint={deviceStatusHint}
            deviceNotice={studentDeviceNotice}
            manual={simManualProps}
            reading={collapsedReading}
            onTakeReading={handleTakeReading}
            onClearReading={handleClearReading}
            liveHeading={liveReadingFor(d)?.headingDegrees ?? null}
            hasReading={collapsedReading != null}
            t={t}
            locale={i18n.language}
            formatLive={formatLiveDisplay}
            hintReading={simReadingFor(d)}
            bypass={bypassProps}
          >
            {renderCompassFiredFeedback(collapsedTest.testResult)}
          </CompassTestWidget>
          </BlockPreviewProvider>
        ) : (
        /* The unselected card is the SAME student widget, rendered disabled — not a hand-built
           lookalike. It used to be one, and that copy drifted (it kept drawing a Take Reading button
           for Live Reading blocks) before this shared it. In Live Reading a representative correct
           heading stands in for a fix; Capture shows its button unpressed; Capture on Submit shows
           nothing at all, which is exactly what a student gets. The actual correct-answer readout
           lives in the answer bar (compassCollapsedBar), not here. */
        <CompassAnswerDisplay
          scenario={ghostedScenario}
          disabled
          d={d}
          // Live Reading ONLY, matching GPS's collapsed card. A Capture block shows its button and
          // nothing else here, because a student who has not pressed it sees nothing else either —
          // a heading above an unpressed button claimed a reading already existed. The correct
          // answer is usually a RANGE anyway, which one big number cannot express, so the answer
          // bar carries it (compassCollapsedBar) in every mode.
          reading={d.visibilityMode === 'live' && previewValue != null
            ? { headingDegrees: previewValue, headingAccuracy: COMPASS_GOOD_HEADING_ACCURACY, positionAccuracy: COMPASS_GOOD_POSITION_ACCURACY }
            : null}
          hasReading={d.visibilityMode === 'live' && previewValue != null}
          formatLive={formatLiveDisplay}
          t={t}
        />
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Validation / derived state ──────────────────────────────────────────────

  // Live editor-state snapshot — what the teacher is currently typing, not the last-pushed
  // block.data, so the issue bar and the field highlights update on the same keystroke.
  const liveD: CompassBlockEditorData = {
    question: questionHtmlRef.current,
    northReference, visibilityMode, liveDisplayFormat, conditionType,
    answers, toleranceSymmetry, showTolerance, betweenAnswers, gradingMode, bypassEnabled, feedback,
  };

  // ── Variable testing ────────────────────────────────────────────────────────
  // Same Min/Max/Random tray numerical and slider carry, from the one shared component. Drives this
  // block's OWN roll (slider's model, not numerical's separate scenario) because the test surface
  // here already resolves through `rolledValues` — a second roll state would just be a second thing
  // to keep in sync.
  /** The live answer fields WITH their ids, so each resolves in its own angle mode. */
  const answerFieldRowsLive: { value: string; fieldId: string }[] =
    liveD.conditionType === 'exact'
      ? liveD.answers.map(a => ({ value: a.value, fieldId: `${a.id}.value` }))
      : liveD.betweenAnswers.flatMap(a => [
        { value: a.low, fieldId: a.id }, { value: a.high, fieldId: a.id }]);
  const answerFieldsLive: string[] = answerFieldRowsLive.map(r => r.value);
  const hasChipFormulas = answerFieldsLive.some(hasVariableChip);
  const answerExtrema = hasChipFormulas ? computeFormulaExtrema({
      conditionType: liveD.conditionType, answers: liveD.answers, betweenAnswers: liveD.betweenAnswers,
      threshold: '', toleranceMode: 'absolute', toleranceSymmetry: liveD.toleranceSymmetry, angleMode: 'degrees',
  }, variables) : null;
  function applyVariableTestMode(mode: 'min' | 'max' | 'random') {
    // Drives BOTH: the scenario the feedback panel resolves against, and the block roll the test
    // surface grades against, so the two agree while a scenario is active.
    const next = mode === 'random' ? rollVariableValues(variables)
      : answerExtrema ? (mode === 'min' ? answerExtrema.loValues : answerExtrema.hiValues)
      : null;
    if (!next) return;
    setFeedbackTestValues(next);
    setRolledValues(next);
    setVariableTestMode(mode);
  }
  /** Resolves ONLY while a scenario is picked. With none, every formula stays NaN, which is what

  /**
   * Where the numbers in the chips came from — `equation → value`, the shape numerical's scenario
   * bar uses and the branching blocks' test bars used before it.
   *
   * Shown only under a live scenario. At rest the equation is already on screen with its variables
   * unrolled, and repeating it here with nothing resolved would be the same line twice.
   */
  const scenarioCalculation: React.ReactNode = !feedbackTestValues ? null : (
    <div className='flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-xs text-ws-text'>
      <span className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
        {answerFieldsLive.filter(hasVariableChip).map((f, i) =>
          <span key={i}><FormulaWithValues formula={f} rolledValues={feedbackTestValues} /></span>)}
      </span>
      {(() => {
        const resolved = answerFieldRowsLive.filter(r => hasVariableChip(r.value))
          .map(r => evaluateFormulaRaw(r.value, feedbackTestValues, angleModeFor(liveD, r.fieldId)))
          .filter(Number.isFinite);
        return resolved.length === 0 ? null : (
          <span className='font-semibold tabular-nums'>
            {' → '}{resolved.map(n => `${fmtDegrees(n, i18n.language)}°`).join(', ')}
          </span>
        );
      })()}
    </div>
  );

  const variableTestController: React.ReactNode = !hasChipFormulas ? null : (
    <VariableTestController
      mode={variableTestMode}
      onSelect={applyVariableTestMode}
      onClear={() => { setVariableTestMode(null); setFeedbackTestValues(null); }}
      extremaAvailable={!!answerExtrema}
      calculation={scenarioCalculation}
    />
  );

  const editorIssues = settleLive(compassIssues(liveD, issueCtx));

  // ── Phone "Test" tab ────────────────────────────────────────────────────────
  // Only rendered while `isPhone` (InputBlockFrame ignores it otherwise). Same computation the
  // desktop collapsed card runs, but fed `liveD` instead of `initData(block.data)` so the test
  // reflects what's being typed right now.
  const phoneReading = isPhone ? readingFor(liveD) : null;
  const phoneTest = isPhone ? computeCompassTestData(liveD, t, i18n.language, phoneReading, evaluatedFor(liveD, phoneReading), bypassAnswer, resolveAnswer, rolledValues, sensorDead) : null;
  const phoneTestSlot = phoneTest ? (
    // Block-test roll only. Preview is deliberately NOT wrapped: it inherits the
    // task-level provider so every block on the page resolves against one shared roll.
    <BlockPreviewProvider value={rolledValues}>
    <CompassTestWidget
          scenario={ghostedScenario}
      d={liveD}
      selection={testSelection}
      onSelectionChange={handleTestSelectionChange}
      mode={sourceMode}
      onModeChange={handleSourceModeChange}
      deviceStatusHint={deviceStatusHint}
      deviceNotice={studentDeviceNotice}
      manual={simManualProps}
      reading={phoneReading}
      onTakeReading={handleTakeReading}
      onClearReading={handleClearReading}
      liveHeading={liveReadingFor(liveD)?.headingDegrees ?? null}
      hasReading={phoneReading != null}
      t={t}
      locale={i18n.language}
      formatLive={formatLiveDisplay}
      hintReading={simReadingFor(liveD)}
      bypass={bypassProps}
    >
      {renderCompassFiredFeedback(phoneTest.testResult)}
    </CompassTestWidget>
    </BlockPreviewProvider>
  ) : null;
  const phoneTestBar: TestBarProps | undefined = phoneTest ? {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: evaluatedFor(liveD, phoneReading),
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: handleSimReroll,
    /* The same row as the canvas card's. The EDITOR's pointer here: on a phone, Edit and Test
       are two tabs over ONE session. */
    topNotices: (
      <VersionBranchNotice
        versions={testPager?.versions ?? shownVersions}
        activeVersionId={testPager ? testPager.activeId : shownVersionId}
        onSelectVersion={testPager?.onSelect ?? selectVersion}
        noun={testPager?.noun ?? 'version'}
        editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
        stacked
        branch={null}
        onEdit={() => { editShownVersion(); setPhoneTestMode('edit'); }}
      />
    ),
    notices: (
      <IssuesPanel issues={editorIssues} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  } : undefined;

  const acceptedRanges: CompassInterval[] = computeCompassAcceptedRanges(compassAcceptedRangeConfig(liveD));

  const allTargetedTriggers = feedback.states.filter(s => s.type === 'targeted').map(s => s.trigger);
  const overlappingTriggerPairs = findCompassOverlappingFeedbackPairs(allTargetedTriggers);
  const overlappingTriggerSet = new Set(overlappingTriggerPairs.flat());

  // One deterministic assignment, the same one `getIssues` probes with, so the card and the bar
  // cannot reach different conclusions about the same condition.
  const cardProbeValues = probeRoll(variables);

  /**
   * The card's ERROR — a condition that is broken, not merely awkward.
   *
   * Same detector the issue bar counts, so the two cannot disagree. They were not connected at all
   * before: the bar named the fault for a block with several conditions in it and no card carried a
   * mark, leaving the teacher to open each one and compare.
   */
  function compassFeedbackError(trigger: string, stateId: string): string | undefined {
    const fault = triggerFault(trigger, {
      resolve: (v: string) => evaluateFormulaRaw(v, cardProbeValues, angleModeFor(fields, `feedback.${stateId}.trigger`)),
      declared: new Set(variables.map(v => v.name)),
      probeable: new Set(Object.keys(cardProbeValues)),
      allowWrap: true,
    });
    return fault ? triggerFaultMessage(fault, t) : undefined;
  }

  const acceptedClaimed = gradingMode === 'graded'
    || feedback.states.some(x => x.type === 'targeted' && x.trigger === SET_ANSWER_TRIGGER);
  function compassFeedbackWarning(trigger: string): string | undefined {
    /*
     * Both verdicts are about the ACCEPTED BAND, so they need the band to be spoken for.
     *
     * Graded always speaks for it: Correct is checked first and is terminal, so a target inside it
     * never fires. Outside graded only a Set Answer target claims it — and with no claimant there
     * is nothing to overlap, because the catch-all is catching those readings anyway. The issues
     * list has made this check graded-only for a while; these per-card copies never did, so a
     * Participation block warned "Completely overlaps Correct" about a Correct it does not have.
     *
     * Set Answer authored is the case the warning is genuinely FOR outside graded, and it is now
     * the case that reaches it.
     */
    /*
     * BOTH SIDES OF A COLLISION ARE LIT, which is what makes one findable. A value-vs-value pair
     * already did — `overlappingTriggerSet` is built from `.flat()` — and Set Answer was the
     * one-sided case: it states the band rather than overlapping it, which is true and is not the
     * point. A teacher looking at one lit card should not have to hunt for what it collides with.
     */
    if (trigger === SET_ANSWER_TRIGGER) {
      return acceptedClaimed && feedback.states.some(x => x.type === 'targeted' && x.trigger !== SET_ANSWER_TRIGGER
          && (isCompassFeedbackFullySubsumed(x.trigger, acceptedRanges) || doesCompassFeedbackOverlapAccepted(x.trigger, acceptedRanges)))
        ? t('blocks.compass.feedbackOverlapWarning') : undefined;
    }
    if (!acceptedClaimed) return overlappingTriggerSet.has(trigger) ? t('blocks.compass.feedbackOverlapWarning') : undefined;
    if (isCompassFeedbackFullySubsumed(trigger, acceptedRanges)) return t('blocks.compass.feedbackSubsumedWarning');
    if (doesCompassFeedbackOverlapAccepted(trigger, acceptedRanges)) return t('blocks.compass.feedbackOverlapsCorrectWarning');
    if (overlappingTriggerSet.has(trigger)) return t('blocks.compass.feedbackOverlapWarning');
    return undefined;
  }

  // Checked against the staged pending capture, not a committed answer — same "warn before
  // commit" purpose as GPS's own pendingCaptureAccuracyPoor. Cutoff itself is a fixed constant
  // (see compass.ts's own note on why this isn't teacher-configurable), not a validated field.
  const pendingCaptureHeadingAccuracyPoor = !!pendingCapture && pendingCapture.headingAccuracy >= 0
    && pendingCapture.headingAccuracy > COMPASS_HEADING_ACCURACY_CUTOFF;

  // ── Feedback slots ──────────────────────────────────────────────────────────

  const unansweredState = feedback.states.find(s => s.trigger === 'unanswered');
  const noAnswerSlot = unansweredState ? (() => {
    const s = unansweredState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={getTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => updateFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => {
          const cur = s.evaluation === 'correct' ? 'neutral' : s.evaluation as 'neutral' | 'incorrect';
          updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : null;

  /*
   * The sensor states, in the order evaluateCompass gates them: nothing at all, then a heading too
   * noisy to trust, then — only in True-north mode — a position too vague to correct it with.
   *
   * Position accuracy stays hidden under Magnetic, as it always has: unlike Default Incorrect's
   * inactive state, which flips back the moment a teacher edits an answer, northReference is a
   * deliberate static setting, and a card for a gate that structurally cannot fire is clutter.
   */
  const noSensorDataState = feedback.states.find(s => s.trigger === NO_SENSOR_DATA_TRIGGER);
  const poorHeadingAccuracyState = feedback.states.find(s => s.trigger === 'poor_heading_accuracy');
  const poorPositionAccuracyState = feedback.states.find(s => s.trigger === 'poor_position_accuracy');
  const noSensorDataCards = noSensorDataState ? [
    <SensorFeedbackCard key='no-data' state={noSensorDataState} getTitle={getTitle}
      label={t('blocks.sensors.noSensorDataFeedback')}
      onChange={patch => updateFeedbackState(noSensorDataState.id, patch)}
      activeEditorRef={activeEditorRef} setActiveEditor={setActiveEditor} />,
  ] : [];
  const poorSensorCards = [
    poorHeadingAccuracyState && (
      <SensorFeedbackCard key='heading' state={poorHeadingAccuracyState} getTitle={getTitle}
        label={t('blocks.compass.feedbackTriggerPoorHeadingAccuracy', { cutoff: fmtLocale(COMPASS_HEADING_ACCURACY_CUTOFF, i18n.language) })}
        onChange={patch => updateFeedbackState(poorHeadingAccuracyState.id, patch)}
        activeEditorRef={activeEditorRef} setActiveEditor={setActiveEditor} />
    ),
    poorPositionAccuracyState && northReference === 'true' && (
      <SensorFeedbackCard key='position' state={poorPositionAccuracyState} getTitle={getTitle}
        label={t('blocks.compass.feedbackTriggerPoorPositionAccuracy', { cutoff: fmtLocale(COMPASS_POSITION_ACCURACY_CUTOFF, i18n.language) })}
        onChange={patch => updateFeedbackState(poorPositionAccuracyState.id, patch)}
        activeEditorRef={activeEditorRef} setActiveEditor={setActiveEditor} />
    ),
  ].filter(Boolean);

  // Mode-scoped: a NAMED trigger authored in one mode is hidden, not deleted, in the modes it
  // cannot fire in — see `targetedFiresIn`. Value triggers fire in every mode and all stay.
  const targetedStates = visibleTargeted(feedback.states, gradingMode);
  // No mode gate: `evaluateCompass` fires value triggers in every mode.
  const canAddMore = targetedStates.length < 5;

  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleFeedbackTargetedDragEnd}
      accessibility={dndA11y(t, targetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={targetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {targetedStates.map(s => {
            const ORDER = ['neutral', 'incorrect'] as const;
            return (
              <SortableFeedbackCard
                key={s.id}
                sortId={s.id}
                titleLocked
                /* Neutral and locked outside graded: the stored evaluation mirrors a verdict
                   the mode has not got, and there is no incorrect to cycle TO. */
                title={s.title} content={s.content}
                evaluation={displayEvaluation(gradingMode, s.evaluation)}
                evalLocked={gradingMode !== 'graded'}
                startExpanded={justAddedKey(s.id) === justAddedStateId}
                warning={compassFeedbackWarning(s.trigger)}
                error={compassFeedbackError(s.trigger, s.id)}
                triggerZone={<CompassTriggerEditor fieldId={`feedback.${s.id}.trigger`} answerTaken={targetedStates.some(x => x.id !== s.id && x.trigger === SET_ANSWER_TRIGGER)} onPlainFieldFocus={disconnectSharedToolbar} trigger={s.trigger} onChange={trigger => updateFeedbackState(s.id, { trigger, title: compassTargetedTitle(trigger, t) })} t={t} locale={i18n.language} variables={variables} rolledValues={rolledValues} gradingMode={gradingMode} answerConditionChips={<ConditionChipRow chips={compassChips(fields, { trigger: 'correct' }, feedbackTestValues, t, i18n.language, gradingMode, targetedStates.map(x => x.trigger))} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />} />}
                /* The preset states the authored CONDITION, resolved against the roll, exactly
                   as graded's Correct card does — "= 90°", not the word "Answer". Same chips,
                   same derivation, so a teacher reads one sentence whichever way it was made. */
                triggerZoneSummary={s.trigger === SET_ANSWER_TRIGGER ? (
                  <ConditionChipRow
                    chips={compassChips(fields, { trigger: 'correct' }, feedbackTestValues, t, i18n.language, gradingMode, targetedStates.map(x => x.trigger))}
                    unreachableLabel={t('blocks.feedbackPanel.unreachable')}
                  />
                ) : <CompassTriggerChip angleMode={angleModeFor(fields, `feedback.${s.id}.trigger`)} trigger={s.trigger} evaluation={displayEvaluation(gradingMode, s.evaluation)} t={t} locale={i18n.language} scenario={feedbackTestValues ?? {}} />}
                onTitleChange={() => {}}
                onContentChange={html => updateFeedbackState(s.id, { content: html })}
                onEvaluationCycle={() => updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                onDelete={() => deleteFeedbackState(s.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  // Correct genuinely cannot fire without a real configured answer (an unset exact/between
  // field defaults to 0deg via num()) — shown with the same muted "Inactive" treatment every
  // other block uses for an unreachable state, rather than silently resolving to "0°", which
  // would misleadingly read as a real accepted heading. Same pattern as numerical-block.tsx's
  // own inactiveChip/hasConfiguredAnswer.
  const hasConfiguredAnswer = !isCompassSetupIncomplete(fields);
  const correctInactiveChip = (
    <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
  );

  // One chip per OR'd answer, each with its own cardinal-annotated label and arc — see
  // correctRangePairs's own note on why this uses the pre-split pairs, not acceptedRanges. An
  // explicit "or" connector between multiple chips, since that's exactly the evaluation
  // semantics: a submitted heading only ever falls in one of these ranges at a time, and
  // landing in ANY of them is sufficient for Correct to fire — never "must match all of them."
  /** See angle's twin: with a formula answer and no scenario, show the equation rather than a band
   * resolved at zero. */
  // Hoisted: both default states' chip rows need the carve-outs, and this sat between them.
  const targetedTriggers = targetedStates.map(s => s.trigger);
  const feedbackShowsEquation = compassAnswerHasFormula(fields) && feedbackTestValues === null;


  const compassPositiveInactive = gradingMode === 'graded' && !hasConfiguredAnswer;
  /**
   * The catch-all names what it still catches, like Default Incorrect — but only once SET ANSWER
   * carves the accepted band out of it. Without one the catch-all genuinely catches the whole
   * circle including the answer, and the gap derivation (which always excludes the band) would
   * claim otherwise. The tone follows ANY target, not just Set Answer.
   */
  const compassSetAnswerAuthored = targetedStates.some(x => x.trigger === SET_ANSWER_TRIGGER);
  const compassPositiveChips =
    gradingMode !== 'graded' ? (
      <ConditionChipRow
        chips={catchAllChips(gradingMode, targetedStates.length > 0, t,
          // Enumerates whenever ANYTHING is targeted; `acceptedClaimed` only decides whether the
          // band comes out of the leftovers, which it does once a Set Answer target owns it.
          () => compassChips(fields, { trigger: 'incorrect' }, feedbackTestValues, t, i18n.language, gradingMode,
            targetedTriggers, compassSetAnswerAuthored))}
        unreachableLabel={t('blocks.feedbackPanel.unreachable')}
      />
    ) :
    // With a formula answer and no scenario picked, show the EQUATION as chips — the same thing the
    // collapsed card shows, and the same two-style rule: display shows the variable, the testing
    // line shows a roll of it.
    // ONE derivation, both modes. `feedbackTestValues` is already the ChipRoll: null at rest, the
    // scenario once one is picked — so the mode fork that used to sit here (a joined-string
    // `positiveFormulaChip` when unrolled, per-range `correctChips` when rolled) is now internal
    // to `compassChips`, which is what stops the two from drifting apart again.
    (!hasConfiguredAnswer ? correctInactiveChip : (
      <ConditionChipRow
        chips={compassChips(fields, { trigger: 'correct' }, feedbackTestValues, t, i18n.language, gradingMode, targetedTriggers)}
        unreachableLabel={t('blocks.feedbackPanel.unreachable')}
      />
    ));

  const correctSlot = feedback.states
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={compassPositiveInactive}
        triggerZone={compassPositiveChips}
        triggerZoneSummary={compassPositiveChips}
        title={catchAllTitle(gradingMode, targetedStates.length > 0, t) ?? getTitle(state)}
        content={state.content}
        /* The tick goes once a target exists — the catch-all must not affirm an answer the
           teacher ignored more strongly than one they wrote for. Same rule as every other block. */
        evaluation={gradingMode !== 'graded' && targetedStates.length > 0 ? 'neutral' : state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  const defaultIncorrectUnreachable = isCompassDefaultIncorrectUnreachable(acceptedRanges, targetedTriggers);
  // One chip per genuine gap, same "or" treatment as correctChips above — Default Incorrect
  // fires when the heading lands in ANY leftover gap (a single heading can only ever be in one
  // of them at a time), not when it's in all of them simultaneously. The AND lives one level
  // down, inside how each gap itself was computed (excluded from Correct AND from every
  // targeted trigger) — that's not something the displayed chips themselves need to express.
  // With an unresolved equation there is no band to take the complement OF. The gap maths ran on
  // whatever the formula parsed to (zero), and printed a confident, wrong answer: a bogus band, or
  // "Any heading" when the tolerance made it a full circle. The honest statement is the one the
  // positive slot already makes, negated — "not {n}*30" — and it turns into real bands the moment
  // a scenario is rolled, exactly as the Correct chip does. Numerical has said it this way for a
  // while; this is that treatment ported to the two sensors.
  // "The answer is an equation with no usable band right now" — which covers BOTH the state before
  // a scenario is rolled and a scenario that fails to resolve it. Keying only on "no scenario yet"
  // meant that rolling a formula the roll could not work out flipped the card straight back to the
  // false "Unreachable", which is the very thing being fixed.
  const answerBandUnusable = compassAnswerHasFormula(fields)
    && (acceptedRanges.length === 0 || acceptedRanges.some(r => !Number.isFinite(r.lo) || !Number.isFinite(r.hi)));
  const showSymbolicIncorrect = feedbackShowsEquation || answerBandUnusable;
  // ONE derivation, replacing the three-way fork that stood here: a joined-string chip when the
  // answer was an unrolled equation, per-gap chips when rolled, and an Unreachable pill otherwise.
  // The first two are the same question asked in two modes, which `compassChips` now takes as an
  // argument; only genuine unreachability is still a separate case.
  const incorrectRow = (!showSymbolicIncorrect && defaultIncorrectUnreachable)
    ? <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
    : (
      <ConditionChipRow
        chips={compassChips(fields, { trigger: 'incorrect' }, feedbackTestValues, t, i18n.language, gradingMode, targetedTriggers)}
        unreachableLabel={t('blocks.feedbackPanel.unreachable')}
      />
    );

  const incorrectSlot = feedback.states
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={!showSymbolicIncorrect && defaultIncorrectUnreachable}
        triggerZone={incorrectRow}
        triggerZoneSummary={incorrectRow}
        title={getTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // ── Editing shell ───────────────────────────────────────────────────────────

  return (
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      icon={<Compass className='h-6 w-6' />}
      titleLabel={t('blocks.compass.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.compass.closeEditor')}
      onClose={handleDone}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        <>
          {/* The SECTION goes, not just the row: north reference is the only control under
              Measurement, and `OptionsSection` always draws its heading — so gating the radio
              alone would leave a bare "MEASUREMENT" label over nothing. Dropping it takes this
              panel from three sections to two, which still clears the `< 2` floor that keeps a
              panel single-column, so phone landscape still flows in two. */}
          {showNorthReference && (
            <OptionsSection title={t('blocks.optionsSections.measurement')}>
              <RadioGroup fieldId='northReference'
                aria-label={t('blocks.compass.northReferenceGroupLabel')}
                value={northReference}
                onChange={setNorthReferenceAndPush}
                options={[
                  { value: 'magnetic', label: t('blocks.compass.northReferenceMagnetic') },
                  { value: 'true', label: t('blocks.compass.northReferenceTrue') },
                ]}
              />
            </OptionsSection>
          )}

          <OptionsSection title={t('blocks.optionsSections.answerFormat')}>
            {/* Capture Mode belongs here, not under Student Display: it decides whether the
                student takes a reading and when, which is how the answer is GIVEN rather than how
                the block looks. It used to sit beside Live Display Format, which left this block
                deciding the same question in two different sections. Student Display now means
                only presentation. */}
            <RadioGroup fieldId='visibilityMode'
              aria-label={t('blocks.captureMode.label')}
              value={visibilityMode}
              onChange={setVisibilityModeAndPush}
              options={[
                { value: 'live', label: t('blocks.captureMode.live') },
                { value: 'capture', label: t('blocks.captureMode.capture') },
                { value: 'passive', label: t('blocks.captureMode.onSubmit') },
              ]}
            />

            <CheckboxRow fieldId='bypassEnabled'
              checked={bypassEnabled}
              onToggle={handleBypassToggle}
              label={t('blocks.captureMode.allowBypass')}
            />
          </OptionsSection>

          <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>
            {/* Lifted OUT of Live Reading's nestedContent and placed under the whole group, matching
                GPS. Nesting it said the format belonged to Live Reading alone, which was never true
                of the setting and is no longer true of the code either: Capture now renders the
                teacher's chosen format instead of a hardcoded cardinal+degrees. A SegmentedControl
                rather than a third radio list because it's a three-way inline choice, which is what
                that control is for — and it costs one line instead of four. Visible text is the
                example itself, the full description stays as each segment's aria-label. Greyed, not
                hidden, in Capture on Submit, where nothing is displayed to format. */}
            {/* Offered in every capture mode, unlike the format control below it. Nothing is displayed
                to FORMAT in Capture on Submit, but a tolerance is not about display: with no readout a
                student is blind and gets one shot, so the required precision is the only guidance they
                have. Disabled instead when the condition type is not `exact`, where the band would be
                the answer rather than a statement about precision. */}
            <CheckboxRow fieldId='showTolerance'
              checked={showTolerance}
              onToggle={() => { const v = !showTolerance; setShowTolerance(v); push({ showTolerance: v }); }}
              label={t('blocks.compass.showTolerance')}
              disabled={conditionType !== 'exact' || !settingAvailable('showTolerance', gradingMode)}
            />

            <SegmentedControl fieldId='liveDisplayFormat'
              aria-label={t('blocks.compass.liveDisplayFormatGroupLabel')}
              stretch
              disabled={visibilityMode === 'passive'}
              value={liveDisplayFormat}
              onChange={v => setLiveDisplayFormatAndPush(v as CompassLiveDisplayFormat)}
              options={[
                { id: 'degrees', label: t('blocks.compass.liveDisplayFormatDegreesShort'), ariaLabel: t('blocks.compass.liveDisplayFormatDegrees') },
                { id: 'cardinalDegree', label: t('blocks.compass.liveDisplayFormatCardinalDegreeShort'), ariaLabel: t('blocks.compass.liveDisplayFormatCardinalDegree') },
                { id: 'cardinalOnly', label: t('blocks.compass.liveDisplayFormatCardinalOnlyShort'), ariaLabel: t('blocks.compass.liveDisplayFormatCardinalOnly') },
              ]}
            />
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.compass.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      sharedToolbar={sharedToolbar}
      // On the FRAME, not the feedback panel: the surface that most needs the numbers
      // interpreting is the one the answer bar is hidden behind.
      scenarioBarSlot={variableTestController}
      /**
       * The accepted answer, in the EDITOR — which this block did not have.
       *
       * Both blocks built an answer bar and gave it only to the collapsed canvas card, so the one
       * surface where a teacher is actually setting the answer was the one that never showed what
       * it had become. Numerical has had an editor bar all along; this is the same content through
       * the same shared bar, minus the card's mobile chip, and it sits under the scenario bar so a
       * rolled equation and the band it resolves to read as one thing.
       *
       * Issues are NOT repeated here: `warningBarSlot` renders them directly above, which is the
       * split the collapsed card cannot make and the editor can.
       */
      answerBarSlot={fields.gradingMode !== 'graded'
        ? collapsedModeBar(fields.gradingMode, t, 'compass')
        : (() => {
            /*
             * `isCompassSetupIncomplete` is the WRONG test for this, and gating on it is what made an
             * incomplete range show no bar at all. It conflates two states the bar must separate:
             * "nothing authored yet" (no bar) and "authored, but nothing valid" ("No Valid Answer").
             * A `between` row with one bound filled is the second — the teacher typed something and
             * the evaluator accepts nothing — and it read as the first.
             *
             * `answerExprTexts` asks the honest question: is any answer field authored at all?
             */
            const chips = compassChips(liveD, { trigger: 'correct' }, feedbackTestValues, t, i18n.language, liveD.gradingMode, targetedTriggers);
            const authored = answerExprTexts(liveD).length > 0;
            if (!answerBarHasContent(chips, authored)) return null;
            return (
              <CollapsedBlockBar tone='success' align='start' scrollX>
                <AnswerBarText chips={chips}
                  noValidAnswerLabel={authored ? t('blocks.answers.noValidAnswer') : undefined} />
              </CollapsedBlockBar>
            );
          })()}
      feedbackPanelProps={{
        noAnswerSlot,
        noSensorDataSlot: <>{noSensorDataCards}</>,
        poorSensorSection: poorSensorCards.length > 0
          ? {
            // Named only when there is more than one, so the heading says what they share rather
            // than repeating a lone card's own label back at it.
            heading: poorSensorCards.length > 1 ? t('blocks.sensors.poorSectionHeading') : undefined,
            content: <>{poorSensorCards}</>,
          }
          : undefined,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore,
          isEmpty: targetedStates.length === 0,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
            {/* The three-way offer, when linking meets an existing bank — §7.1. */}
            {remapModal}
            {/* The link control and what it points at, for BOTH kinds of link — see
                `VariesWithSection`, which decides on its own whether a DRIVEN block
                needs it. This was five lines of identical JSX in twelve blocks. */}
            <VariesWithSection
              following={!!activeScenario}
              linkMenu={linkMenu}
              stimulus={<ScenarioStimulus leader={(blocks ?? []).find(b => b.id === storedForLink.followsBlockId)}
                  version={activeScenarioVersion as Record<string, unknown> | null} />}
            />
            {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
                the answer setup, because a driven axis varies the question too. Renders its
                children bare when this block is not driven. See `DrivenRowFrame`. */}
            <DrivenRowFrame block={block} data={initData(block.data)} emit={emit}>
            {/* A bank is a niche feature: in the heading it is available without being advertised. */}
            <EditorSectionHeading
              label={t('blocks.areas.question')}
              divider={!!activeScenario}
              action={(
                <div className='flex items-center gap-2'>
                {/* The bank is GONE while this block follows a scenario, not merely inert (§2.2). */}
                {!storedForLink.followsBlockId && (
                <VersionSelector
                  versions={(initData(block.data).versions ?? []) as { id: string }[]}
                  activeId={editorVersionId}
                  onSelect={selectVersion}
                  onAdd={() => {
                    const next = addCompassVersion(initData(block.data), editorVersion, () => crypto.randomUUID());
                    emit(versionStructure(block.data, next));
                    const added = next.versions?.[next.versions.length - 1];
                    if (added) selectVersion(added.id, next);
                  }}
                  onRemove={(id) => {
                    const next = removeCompassVersion(initData(block.data), id);
                    emit(versionStructure(block.data, next));
                    const landing = next.versions?.[0];
                    selectVersion(landing?.id, next);
                    setTestVersion(landing?.id);
                  }}
                />
                )}
                {/* Silent once there is a Varies-With section, which is where the
                    control moves to — see `QuestionHeadingLink`. Written as
                    `!activeScenario` it knew about scenarios only, so a DRIVEN block
                    showed the link twice and the heading overflowed sideways. */}
                <QuestionHeadingLink
                  following={!!activeScenario}
                  linkMenu={linkMenu}
                />
                </div>
              )}
            />
            <BlockQuestionEditor
              value={questionHtml}
              onChange={html => {
                // The render-time SNAPSHOT has to move with the ref, not just the ref.
                // `currentDataRef.current` is rebuilt during render, and this handler runs before any
                // re-render, so every push path would otherwise spread the PREVIOUS question and leave
                // the persisted value one keystroke behind what is on screen. MCQ/Dropdown/Keyword avoid
                // this by reading questionHtmlRef live at push time; these blocks bake the question into
                // `fields` instead, so the two have to be kept in lockstep here.
                questionHtmlRef.current = html;
                setQuestionHtml(html);
                currentDataRef.current = { ...currentDataRef.current, question: html };
                push();
              }}
              toolbar={sharedToolbar}
              editorRef={questionEditorRef}
              shouldFocusRef={shouldFocusEditor}
              embedded={embedded}
            />

            {/*
              * ANSWER SETUP, hidden in OPTIONAL only.
              *
              * Optional is the one mode with no use for an answer key. Survey keeps it: Set Answer
             * targets the authored band, so the band is live there even though nothing is graded. So none of this has anything to act on: the
              * band, its tolerance, and the capture button that fills a row all describe what counts
              * as right. Hidden rather than greyed, because the whole SECTION's job is gone — the
              * same call the feedback panel already makes for its own correctness cards in optional.
              * A single control that merely has no effect is disabled in place instead.
              *
              * Nothing is deleted. Switch back to Has Correct and the answer is exactly as it was.
              */}
            {gradingMode !== 'optional' && (<>
              {/* Armed-delete announcement — matches rotation-block.tsx's exact pattern. */}
              <div aria-live='assertive' aria-atomic='true' className='sr-only'>
                {(armedAnswerId || armedBetweenId) ? t('blocks.actions.deleteArmed') : ''}
              </div>

              <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

              {/* The scenario's own box, around the ANSWER and nothing else — the sensor's
                  settings describe the instrument rather than the answer, so they stay outside
                  it exactly as they stay outside a version. */}
              <ScenarioSetupFrame
                scenario={activeScenario}
                name={activeScenarioVersion?.name}
                pager={scenarioPager}
                unreviewedCount={unreviewedCount(storedForLink.scenarioBranches)}
                onDismissUnreviewed={dismissUnreviewed}
              >
              <AnswerSetupFields
                variables={variables}
                rolledValues={rolledValues}
                onPlainFieldFocus={disconnectSharedToolbar}
                t={t}
                locale={i18n.language}
                conditionType={conditionType}
                onConditionTypeChange={setConditionTypeAndPush}
                answers={answers}
                onUpdateAnswer={updateAnswer}
                onAddAnswer={addAnswer}
                onRemoveAnswer={removeAnswer}
                onToggleAnswerSymmetry={toggleAnswerSymmetry}
                betweenAnswers={betweenAnswers}
                onUpdateBetweenAnswer={updateBetweenAnswer}
                onAddBetweenAnswer={addBetweenAnswer}
                onRemoveBetweenAnswer={removeBetweenAnswer}
                armedAnswerId={armedAnswerId}
                setArmedAnswerId={setArmedAnswerId}
                armedBetweenId={armedBetweenId}
                setArmedBetweenId={setArmedBetweenId}
              />
              </ScenarioSetupFrame>

              {/* Capture sits directly under Answer Setup's own "Add another answer" button — its
                  whole purpose is filling an answer row, so it belongs next to the answer-adding
                  UI, not separated by the accuracy-gates section below. Only rendered for the
                  Exact condition — capture writes into a single answer row, which 'between' has no
                  equivalent of. */}
              {conditionType === 'exact' && (
                <div className='flex flex-col gap-1'>
                  <Button onClick={handleCaptureHeading} disabled={isCapturing || !ORIENTATION_SUPPORTED}>
                    <Compass aria-hidden='true' className='h-4 w-4' />
                    {isCapturing ? t('blocks.compass.capturing')
                      : northReference === 'true' ? t('blocks.compass.captureButtonTrue') : t('blocks.compass.captureButtonMagnetic')}
                  </Button>
                  {!ORIENTATION_SUPPORTED ? (
                    <p role='alert' className='flex items-center gap-1.5 text-xs text-ws-warning-fg'>
                      <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />{t('blocks.compass.requiresOrientationServices')}
                    </p>
                  ) : captureError && (
                    // Warning, with the triangle, matching GPS's own capture messaging and the
                    // requiresOrientationServices line right above. A capture that could not complete
                    // is a fact about the device or its permissions, never a mistake in the block, so
                    // error red overstated it — and it was the one message here with no icon at all.
                    <p role='alert' className='flex items-center gap-1.5 text-xs text-ws-warning-fg'>
                      <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
                      {captureError}
                    </p>
                  )}
                  {pendingCapture && (
                    <div className='flex flex-col gap-2 rounded-lg border border-ws-border bg-ws-hover px-3 py-2.5'>
                      <p className='text-center text-lg font-semibold text-ws-text'>{t('blocks.compass.pendingCaptureHeading', { heading: fmtLocale(pendingCapture.headingDegrees, i18n.language), cardinal: t(`blocks.compass.${CARDINAL_I18N_KEYS[cardinalPointFromDegrees(pendingCapture.headingDegrees)]}`) })}</p>
                      <p className='text-center text-xs text-ws-muted'>{t('blocks.compass.pendingCaptureHeadingAccuracy', { accuracy: fmtLocale(pendingCapture.headingAccuracy, i18n.language) })}</p>
                      {pendingCaptureHeadingAccuracyPoor && (
                        <p role='alert' className='flex items-center gap-1.5 text-xs text-ws-warning-fg'>
                          <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
                          {t('blocks.compass.pendingCaptureAccuracyWarning', { cutoff: fmtLocale(COMPASS_HEADING_ACCURACY_CUTOFF, i18n.language) })}
                        </p>
                      )}
                      <p className='text-center text-xs italic text-ws-muted'>{t('blocks.compass.pendingCaptureAppliedNote')}</p>
                      <div className='flex justify-end'>
                        <Button onClick={handleUsePendingCapture} className='shrink-0'>
                          {t('blocks.compass.pendingCaptureUse')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>)}
            </DrivenRowFrame>
          </div>
      }
    />
  );
}
